
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Card,
  CardContent,
  Grid,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Drawer,
  Container,
  useTheme,
  useMediaQuery,
  Button,
  Divider,
  ListItemIcon,
  CssBaseline,
  createTheme,
  ThemeProvider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Menu,
  Slide,
  Tooltip,
  Skeleton,
  TextField,

} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import {
  Menu as MenuIcon,
  TrendingUp,
  ShoppingCart,
  AttachMoney,
  ShowChart,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Dashboard as DashboardIcon,
  Inventory,
  Receipt,
  People,
  Analytics,
  Download,
  Close,
  Sort,
  SwapVert,
  TrendingDown,
  LocalShipping,
  LocationOn,
  SupervisorAccount,
  AdminPanelSettings,
  Store,
  FileDownload,
  Schedule,
  AccessTime,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LineChart, Line } from 'recharts';
import ClickAwayListener from '@mui/material/ClickAwayListener'; // <-- Add this line
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import Header from '../Header';
import { local_signage, local_url } from '../Const';

const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
      light: '#a8b5f5',
      dark: '#3d52a0',
    },
    secondary: {
      main: '#f093fb',
      light: '#f5b7fd',
      dark: '#c86fc9',
    },
    background: {
      default: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      paper: '#ffffff',
    },
    success: {
      main: '#4ade80',
      light: '#86efac',
      dark: '#16a34a',
    },
    warning: {
      main: '#fbbf24',
      light: '#fcd34d',
      dark: '#d97706',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#1d4ed8',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.65rem',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          background: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '20px',
          '&:last-child': {
            paddingBottom: '20px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          padding: '8px 16px',
          transition: 'all 0.2s ease',
        },
        contained: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.6)',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          height: 8,
          background: 'rgba(255, 255, 255, 0.2)',
        },
        bar: {
          borderRadius: 10,
          background: 'linear-gradient(90deg, #667eea, #764ba2)',
        },
      },
    },
  },
});

export const DashBoard = () => {
  const navigate = useNavigate();

  // Handle 403 Forbidden - Logout user
  const handle403Error = useCallback(() => {
    // Clear all localStorage
    localStorage.clear();

    // Show toast notification
    toast.error('Session expired or unauthorized access. Please login again.', {
      position: 'top-center',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
    });

    // Navigate to login after a short delay
    setTimeout(() => {
      navigate('/');
    }, 1000);
  }, [navigate]);

  // Centralized fetch wrapper to handle 403 errors
  const fetchWithAuth = useCallback(async (url, options = {}) => {
    try {
      const response = await fetch(url, options);

      // Check for 403 Forbidden
      if (response.status === 403) {
        handle403Error();
        throw new Error('403 Forbidden - Session expired');
      }

      return response;
    } catch (error) {
      // If it's a 403 error, don't log it as we've already handled it
      if (error.message !== '403 Forbidden - Session expired') {
        // Error already handled
      }
      throw error;
    }
  }, [handle403Error]);

  // Skeleton loader for dashboard cards
  const CardSkeleton = React.memo(() => (
    <Card
      sx={{
        height: '100%',
        minHeight: 160,
        maxHeight: 180,
        animation: 'pulse 1.5s ease-in-out infinite',
        '@keyframes pulse': {
          '0%': { opacity: 1 },
          '50%': { opacity: 0.6 },
          '100%': { opacity: 1 }
        }
      }}
    >
      <CardContent sx={{ p: 2, height: '100%' }}>
        <Box display="flex" flexDirection="column" height="100%">
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="circular" width={24} height={24} />
          </Box>
          <Box flex={1} display="flex" flexDirection="column" justifyContent="center">
            <Skeleton variant="text" width="80%" height={32} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="50%" height={16} />
          </Box>
          <Box mt="auto">
            <Skeleton variant="text" width="40%" height={14} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  ));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [dateRangeOption, setDateRangeOption] = useState('Today');
  const [dateRangeError, setDateRangeError] = useState('');
  const [onlineOrdersSearchQuery, setOnlineOrdersSearchQuery] = useState('');

  // Helper function to get authorization headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('AuthorizationToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  // Date range validation function
  const validateDateRange = useCallback((fromDate, toDate) => {
    if (!fromDate || !toDate) {
      return { isValid: false, error: 'Please select both from and to dates' };
    }

    if (fromDate.isAfter(toDate)) {
      return { isValid: false, error: 'From date cannot be after to date' };
    }

    const diffInMonths = toDate.diff(fromDate, 'month', true);
    if (diffInMonths > 3) {
      return { isValid: false, error: 'Date range cannot exceed 3 months' };
    }

    return { isValid: true, error: '' };
  }, []);
  const [category, setCategory] = useState('all');
  const [region, setRegion] = useState('all');
  const [branches, setBranches] = useState([]);
  const [selectedBranchID, setSelectedBranchID] = useState(() => {
    const userRole = localStorage.getItem('userRole');

    // For SGMGR and AGMGR, use branchId from localStorage
    if (userRole === 'SGMGR' || userRole === 'AGMGR') {
      const branchId = localStorage.getItem('branchId');
      if (branchId) {
        return branchId;
      }

      // Fallback to userData if branchId not found
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          return userData.user?.branchId || userData.user?.defaultLoginBranchId || '';
        } catch (error) {
          // Error parsing userData
        }
      }
    }
    return ''; // Default fallback
  });

  const [selectedAreaID, setSelectedAreaID] = useState(() => {
    const userRole = localStorage.getItem('userRole');

    // For RGMGR, use ALL
    if (userRole === 'RGMGR') {
      return 'ALL';
    }

    // For SGMGR and AGMGR, use areaId from localStorage
    if (userRole === 'SGMGR' || userRole === 'AGMGR') {
      const areaId = localStorage.getItem('areaId');
      if (areaId) {
        return areaId;
      }

      // Fallback to userData if areaId not found
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          return userData.user?.areaId || '00001';
        } catch (error) {
          // Error parsing userData
        }
      }
    }

    return '00001'; // Default fallback
  }); // Default AreaID, will be dynamic for Area Manager
  const [areas, setAreas] = useState([]); // For Central Manager area list
  const [isAreasLoading, setIsAreasLoading] = useState(false);
  const [isBranchesLoading, setIsBranchesLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [filtersSubmitted, setFiltersSubmitted] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);
  // Role mapping function
  const getUserRoleFromStorage = () => {
    const userRole = localStorage.getItem('userRole');
    const roleMapping = {
      'RGMGR': 'central-manager',
      'RMGR1': 'central-manager',  // Regional Manager -> Central Manager
      'AGMGR': 'area-manager',     // Area Manager -> Area Manager
      'SGMGR': 'shop-manager'      // Shop Manager -> Shop Manager
    };
    return roleMapping[userRole] || 'shop-manager';
  };

  const [currentRole, setCurrentRole] = useState(getUserRoleFromStorage());
  const [statsOrder, setStatsOrder] = useState([0, 1, 2, 3, 4, 5]);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [apiCache, setApiCache] = useState({});
  const [lastApiParams, setLastApiParams] = useState({});
  const [apiLoadingStates, setApiLoadingStates] = useState({});

  // Format dates for API calls (YYYYMMDD)
  const formatDateForAPI = (date) => {
    return date ? date.format('YYYYMMDD') : '';
  };

  // Helper function to get dashboard title with area and branch info
  const getDashboardTitle = useCallback(() => {
    const userRole = localStorage.getItem('userRole');
    let areaName = '';
    let branchName = '';

    // Get area name
    if (selectedAreaID === 'ALL') {
      areaName = 'All Areas';
    } else {
      const area = areas.find(a => a.sfAreaId === selectedAreaID);
      if (area) {
        areaName = area.sfAreaName;
      } else {
        // Fallback to localStorage if area not in dropdown
        const userDataString = localStorage.getItem('userData');
        if (userDataString) {
          try {
            const userData = JSON.parse(userDataString);
            areaName = userData.user?.areaId || selectedAreaID;
          } catch (error) {
            areaName = selectedAreaID;
          }
        } else {
          areaName = selectedAreaID;
        }
      }
    }

    // Get branch name
    if (selectedBranchID === 'ALL') {
      branchName = 'All Branches';
    } else {
      const branch = branches.find(b => b.branchID === selectedBranchID);
      if (branch) {
        branchName = branch.branchName;
      } else {
        // Fallback to localStorage if branch not in dropdown
        const userDataString = localStorage.getItem('userData');
        if (userDataString) {
          try {
            const userData = JSON.parse(userDataString);
            branchName = userData.user?.branchName || selectedBranchID;
          } catch (error) {
            branchName = selectedBranchID;
          }
        } else {
          branchName = selectedBranchID;
        }
      }
    }

    // Build title based on role
    if (userRole === 'RGMGR') {
      // For RGMGR, show selected area and branch from dropdowns
      return `E-commerce Dashboard for ${areaName} (${branchName})`;
    } else if (userRole === 'AGMGR') {
      // For AGMGR (Area Manager), show selected area and branch from dropdowns
      return `Area Manager Dashboard for ${areaName} (${branchName})`;
    } else if (userRole === 'SGMGR') {
      // For SGMGR (Shop Manager), show area and branch from localStorage
      return `Shop Manager Dashboard for ${areaName} (${branchName})`;
    }

    return 'Dashboard';
  }, [selectedAreaID, selectedBranchID, areas, branches]);

  // Helper function to format branch ID for API calls
  const formatBranchForAPI = (branchId) => {
    if (branchId === 'ALL') {
      return 'ALL'; // Pass 'ALL' to API when ALL is selected by any role
    }
    if (currentRole === 'shop-manager') {
      return ''; // Shop Manager always uses L108
    }
    // Area Manager and Central Manager can use actual selected branch ID
    return branchId;
  };

  // Create cache key from parameters
  const createCacheKey = useCallback((params) => {
    return JSON.stringify(params);
  }, []);

  // Check if API should be called based on parameter changes
  const shouldCallAPI = useCallback((apiName, newParams) => {
    const cacheKey = createCacheKey(newParams);
    const lastParams = lastApiParams[apiName];
    const shouldCall = !lastParams || lastParams !== cacheKey;
    return shouldCall;
  }, [lastApiParams, createCacheKey]);

  // Update API parameters cache
  const updateApiCache = useCallback((apiName, params, data) => {
    const cacheKey = createCacheKey(params);
    setLastApiParams(prev => ({ ...prev, [apiName]: cacheKey }));
    setApiCache(prev => ({ ...prev, [apiName]: data }));
  }, [createCacheKey]);

  // Loading state management
  const setApiLoading = useCallback((apiName, isLoading) => {
    setApiLoadingStates(prev => {
      const newState = { ...prev, [apiName]: isLoading };
      return newState;
    });
  }, []);

  const isApiLoading = useCallback((apiName) => {
    return apiLoadingStates[apiName] || false;
  }, [apiLoadingStates]);

  // Check if main dashboard APIs are loading
  const areMainApisLoading = useCallback(() => {
    const mainApis = ['salesSummary', 'totalOrdersData', 'walletData', 'horecaSummary', 'happyHourData', 'deliveryStatus'];
    return mainApis.some(api => isApiLoading(api));
  }, [isApiLoading]);

  // Individual card loading checks
  const cardLoadingChecks = {
    'Placed Orders': () => {
      const isLoading = isApiLoading('placedOrdersSummary');
      return isLoading;
    },
    'Scheduled Deliveries': () => isApiLoading('retailOrderSummary'),
    'Wallet': () => isApiLoading('walletData'),
    'Horeca': () => isApiLoading('horecaSummary'),
    'Happy Hour': () => isApiLoading('happyHourData'),
    'Retail': () => isApiLoading('retailSummary'),
    'Stock': () => false, // Stock is static/mocked data
    'Division Sales': () => isApiLoading('salesSummary'),
    'Delivery Status': () => isApiLoading('deliveryStatus')
  };

  // Handle manual refresh
  const handleRefresh = useCallback(async (options = {}) => {
    const {
      force = false, // Force refresh even if data exists
      essential = true, // Only call essential APIs (cards data)
      tables = false, // Include table data APIs
      graphs = false, // Include graph/chart APIs
      all = false // Call all APIs (override other options)
    } = options;

    // Prevent multiple simultaneous refresh calls
    if (isRefreshing) {
      return;
    }

    setIsRefreshing(true);

    // Clear caches to force fresh data fetch
    setApiCache({});
    setLastApiParams({});

    try {
      const apiCalls = [];

      // Essential APIs for main cards (always called unless explicitly disabled)
      if (essential || all) {
        apiCalls.push(
          fetchSalesSummaryData(),
          fetchRetailOrderSummary(),
          fetchRetailSalesSummaryData(),
          fetchWalletData(null, fromDate, toDate),
          fetchHappyHourData(fromDate, toDate),
          fetchHorecaSummary(fromDate, toDate),
          fetchDeliveryStatus(fromDate, toDate),
          fetchPlacedOrdersSummary(fromDate, toDate)
        );
      }

      // Table data APIs (only when tables are visible or requested)
      if (tables || all || showTable) {
        apiCalls.push(
          fetchRetailDetailedData(),
          fetchDetailedSalesData(),
          fetchHappyHourTableData(fromDate, toDate),
          fetchTotalOrdersTableData(null, fromDate, toDate),
          fetchPlacedOrdersTableData(null, fromDate, toDate),
          fetchHorecaTableData(fromDate, toDate)
          // fetchWalletData moved to essential since it's needed for main Wallet card
        );
      }

      // Graph and summary APIs (additional graph-specific calls)
      if (graphs || all) {
        // Graph-specific APIs would go here if needed
        // fetchHappyHourData, fetchHorecaSummary, fetchDeliveryStatus moved to essential

        // Add graph data call if available and we have a default filter
        if (typeof fetchGraphData === 'function') {
          apiCalls.push(fetchGraphData('POULTRY', fromDate, toDate)); // Use default division
        }
      }

      await Promise.all(apiCalls);
      setLastUpdated(dayjs());
    } catch (error) {
      console.error('Error refreshing dashboard data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [fromDate, toDate, selectedBranchID, selectedAreaID, showTable]); // Dependencies ensure fresh data on changes

  // Separate function for refreshing graph data when needed
  const handleGraphRefresh = useCallback(async (filter) => {
    if (typeof fetchGraphData === 'function' && filter && fromDate && toDate) {
      await fetchGraphData(filter, fromDate, toDate);
    }
  }, [fromDate, toDate]);

  // Handle date range option changes
  const handleDateRangeChange = (option) => {
    setDateRangeOption(option);
    // Changing the date range requires an explicit submit to fetch data
    setFiltersSubmitted(false);

    let newFromDate, newToDate;

    switch (option) {
      case 'Today':
        newFromDate = dayjs();
        newToDate = dayjs();
        break;
      case 'Yesterday':
        newFromDate = dayjs().subtract(1, 'day');
        newToDate = dayjs().subtract(1, 'day');
        break;
      case 'This Week':
        newFromDate = dayjs().subtract(7, 'day');
        newToDate = dayjs();
        break;
      case 'This Month':
        newFromDate = dayjs().subtract(30, 'day');
        newToDate = dayjs();
        break;
      case 'Custom':
        // Keep existing dates for custom selection
        return;
      default:
        return;
    }

    // Validate the new date range
    const validation = validateDateRange(newFromDate, newToDate);
    if (validation.isValid) {
      setFromDate(newFromDate);
      setToDate(newToDate);
      setDateRangeError(''); // Clear any existing error
    } else {
      setDateRangeError(validation.error);
    }
  };

  // Fetch areas list for Central Manager
  const fetchAreas = useCallback(async (setDefaultArea = false) => {
    setIsAreasLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetchWithAuth(`${local_url}/api/SFArea/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setAreas(data);

      // Only set first area as default on initial load, not when user manually selects 'ALL'
      if (data.length > 0 && setDefaultArea) {
        if (selectedAreaID === 'ALL') {
          // Set first area as default for Central Manager on initial load only
          setSelectedAreaID(data[0].sfAreaId);
          // Note: fetchBranchList will be called by the useEffect after setSelectedAreaID updates
        } else {
          // Check if current area exists in the list
          const currentAreaExists = data.find(area => area.sfAreaId === selectedAreaID);
          if (!currentAreaExists) {
            setSelectedAreaID(data[0].sfAreaId);
          }
        }
      }
    } catch (error) {
      setAreas([]);
    } finally {
      setIsAreasLoading(false);
    }
  }, [selectedAreaID]); // Keep selectedAreaID dependency for closure

  // Fetch branch list for Area Manager and Central Manager
  const fetchBranchList = useCallback(async (areaId = null) => {
    const targetAreaId = areaId || selectedAreaID || '00001';

    // Don't fetch branches if area is 'ALL' - Central Manager needs to select a specific area first
    if (targetAreaId === 'ALL') {
      setBranches([]);
      return;
    }

    // Check cache first
    const cacheKey = `branches_${targetAreaId}`;
    if (apiCache[cacheKey]) {
      setBranches(apiCache[cacheKey]);
      return;
    }

    setIsBranchesLoading(true);
    try {
      const token = localStorage.getItem('AuthorizationToken');
      const response = await fetchWithAuth(`${local_url}/api/Company/GetBranchList/${targetAreaId}/SEGMENT/Laziz`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setBranches(data);

      // Cache the branch data
      setApiCache(prev => ({ ...prev, [cacheKey]: data }));

      // For Area Manager and Central Manager, set first branch as default if branches are available
      // For Shop Manager, keep L108 as default
      if ((currentRole === 'area-manager' || currentRole === 'central-manager') && data.length > 0) {
        // Set first branch as default, or keep current selection if it exists in the list
        const currentBranchExists = data.find(branch => branch.branchID === selectedBranchID);
        if (!currentBranchExists && selectedBranchID !== 'ALL') {
          setSelectedBranchID(data[0].branchID);
        }

        // Only trigger refresh on initial load, not on subsequent area changes
        // The useEffect with selectedAreaID dependency will handle area changes
        if (isInitialLoad) {
          // Do not auto-refresh data on initial load. Require user to click Submit.
          // We still clear the initial load flag so UI behaves normally after initial branches are populated.
          setIsInitialLoad(false);
        }
      }
    } catch (error) {
      setBranches([]);
    } finally {
      setIsBranchesLoading(false);
    }
  }, [currentRole, selectedBranchID, selectedAreaID]);

  // Initial load only - runs once on component mount
  useEffect(() => {
    if (currentRole === 'central-manager') {
      fetchAreas(true).then(() => { // Pass true to set default area on initial load
        // After areas are loaded, fetch branches for the selected area
        setTimeout(() => {
          fetchBranchList();
        }, 200);
      });
    } else if (currentRole === 'area-manager') {
      fetchBranchList();
    } else {

      // Get branchId and areaId from localStorage
      let branchId = localStorage.getItem('branchId');
      let areaId = localStorage.getItem('areaId');

      // Fallback to userData if not found in localStorage
      if (!branchId || !areaId) {
        const userDataString = localStorage.getItem('userData');
        if (userDataString) {
          try {
            const userData = JSON.parse(userDataString);
            if (!branchId) {
              branchId = userData.user?.branchId || userData.user?.defaultLoginBranchId || '';
            }
            if (!areaId) {
              areaId = userData.user?.areaId || '00001';
            }
          } catch (error) {
            console.error('Error parsing userData:', error);
          }
        }
      }

      // Set default fallbacks if still not found
      branchId = branchId || '';
      areaId = areaId || '00001';

      setSelectedBranchID(branchId);
      setSelectedAreaID(areaId);

      // Initial API load after setting branch ID and area ID
      setTimeout(() => {
        if (fromDate && toDate) {
          handleRefresh({ all: true }).then(() => { // Load all data on initial load
            setIsInitialLoad(false);
          });
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRole]); // Only depend on currentRole to prevent re-runs when fetchAreas/fetchBranchList change

  // For Central Manager: fetch branches when selectedAreaID changes from 'ALL' to a specific area
  useEffect(() => {
    if (currentRole === 'central-manager' && selectedAreaID && selectedAreaID !== 'ALL' && !isInitialLoad) {
      fetchBranchList(selectedAreaID);
    }
  }, [currentRole, selectedAreaID, isInitialLoad, fetchBranchList]);

  // Auto refresh interval only - doesn't depend on date/branch changes
  // Only refresh if user is active and data is visible
  useEffect(() => {
    let interval;
    let isUserActive = true;

    // Track user activity
    const resetUserActivity = () => {
      isUserActive = true;
    };

    document.addEventListener('mousemove', resetUserActivity);
    document.addEventListener('keypress', resetUserActivity);
    document.addEventListener('scroll', resetUserActivity);

    // Set inactive after 10 minutes of no activity
    const inactivityTimer = setInterval(() => {
      isUserActive = false;
    }, 10 * 60 * 1000);

    interval = setInterval(() => {
      if (isUserActive && !isInitialLoad && filtersSubmitted) {
        handleRefresh({ essential: true }); // Only refresh essential data on auto-refresh
      }
    }, 30 * 60 * 1000); // 30 minutes

    return () => {
      clearInterval(interval);
      clearInterval(inactivityTimer);
      document.removeEventListener('mousemove', resetUserActivity);
      document.removeEventListener('keypress', resetUserActivity);
      document.removeEventListener('scroll', resetUserActivity);
    };
  }, []); // Empty dependencies - interval should not depend on handleRefresh

  // Fallback to disable initial load flag after 5 seconds
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 5000);

    return () => clearTimeout(fallbackTimer);
  }, []);

  // Debounced API refresh - only trigger when essential parameters change
  useEffect(() => {
    // Skip initial load to prevent duplicate calls
    if (isInitialLoad || !filtersSubmitted) {
      return;
    }

    // Only refresh if we have valid data and add debouncing
    if (selectedBranchID && fromDate && toDate) {
      // Validate date range before making API calls
      const validation = validateDateRange(fromDate, toDate);
      if (!validation.isValid) {
        return;
      }

      const timer = setTimeout(() => {
        handleRefresh({ essential: true, graphs: true }); // Only essential + graphs on auto-refresh
      }, 500); // Increased debounce time to reduce rapid calls
      return () => clearTimeout(timer);
    }
  }, [fromDate, toDate, selectedBranchID, selectedAreaID, isInitialLoad, validateDateRange]);

  const [showBottomLoader, setShowBottomLoader] = useState(false);
  const [selectedPincode, setSelectedPincode] = useState(null);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [salesSummaryData, setSalesSummaryData] = useState([]);
  const [salesSummaryTablePage, setSalesSummaryTablePage] = useState(0);
  const [salesSummaryTableRowsPerPage] = useState(200);
  const [detailedSalesData, setDetailedSalesData] = useState([]);
  const [detailedSalesTablePage, setDetailedSalesTablePage] = useState(0);
  const [detailedSalesTableRowsPerPage, setDetailedSalesTableRowsPerPage] = useState(200);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const tableRef = useRef(null);
  const [graphFilter, setGraphFilter] = useState('POULTRY');
  const [graphData, setGraphData] = useState([]);
  const [graphLoading, setGraphLoading] = useState(false);
  const [horecaSummary, setHorecaSummary] = useState({ SaleQty: 0, TotalAmount: 0 });
  const [happyHourData, setHappyHourData] = useState([]);
  const [happyHourSummary, setHappyHourSummary] = useState({ totalQty: 0, totalAmount: 0, divisions: [] });
  const [happyHourDetailedData, setHappyHourDetailedData] = useState([]);
  const [isHappyHourTableLoading, setIsHappyHourTableLoading] = useState(false);
  const [happyHourTablePage, setHappyHourTablePage] = useState(0);
  const [happyHourTableRowsPerPage, setHappyHourTableRowsPerPage] = useState(200);
  const [horecaTableData, setHorecaTableData] = useState([]);
  const [isHorecaTableLoading, setIsHorecaTableLoading] = useState(false);
  const [retailOrderSummary, setRetailOrderSummary] = useState({
    totalCount: 0,
    totalAmount: 0,
    completedCount: 0,
    canceledCount: 0,
  });

  const [retailSummary, setRetailSummary] = useState({
    totalQty: 0,
    totalAmount: 0,
    posQty: 0,
    posAmount: 0,
    webQty: 0,
    webAmount: 0,
    channelBreakdown: []
  });

  const [retailDetailedData, setRetailDetailedData] = useState([]);
  const [isRetailTableLoading, setIsRetailTableLoading] = useState(false);
  const [retailTablePage, setRetailTablePage] = useState(1);
  const [retailTableRowsPerPage] = useState(200);

  const [horecaTablePage, setHorecaTablePage] = useState(1);
  const [horecaTableRowsPerPage] = useState(200);

  const [walletTableData, setWalletTableData] = useState([]);
  const [isWalletTableLoading, setIsWalletTableLoading] = useState(false);
  const [walletTablePage, setWalletTablePage] = useState(0);
  const [walletTableRowsPerPage] = useState(200);
  const [walletSummary, setWalletSummary] = useState({
    prepaidCount: 0,
    prepaidAmount: 0,
    subscriptionCount: 0,
    subscriptionAmount: 0,
  });

  const [totalOrdersTableData, setTotalOrdersTableData] = useState([]);
  const [isTotalOrdersTableLoading, setIsTotalOrdersTableLoading] = useState(false);
  const [totalOrdersTablePage, setTotalOrdersTablePage] = useState(0);
  const [totalOrdersTableRowsPerPage, setTotalOrdersTableRowsPerPage] = useState(200);
  const [expandedOrderRows, setExpandedOrderRows] = useState({});

  // Placed Orders state
  const [placedOrdersSummary, setPlacedOrdersSummary] = useState([]);
  const [placedOrdersTotal, setPlacedOrdersTotal] = useState({ count: 0, amount: 0 });
  const [placedOrdersTableData, setPlacedOrdersTableData] = useState([]);
  const [isPlacedOrdersTableLoading, setIsPlacedOrdersTableLoading] = useState(false);
  const [placedOrdersTablePage, setPlacedOrdersTablePage] = useState(0);
  const [placedOrdersTableRowsPerPage, setPlacedOrdersTableRowsPerPage] = useState(200);
  const [expandedPlacedOrderRows, setExpandedPlacedOrderRows] = useState({});

  // Search states for all tables
  const [searchQuery, setSearchQuery] = useState('');

  // Role configuration
  const roles = {
    'shop-manager': {
      name: 'Shop Manager',
      icon: <Store />,
      color: '#4caf50',
      description: 'Individual store operations'
    },
    'area-manager': {
      name: 'Area Manager',
      icon: <SupervisorAccount />,
      color: '#ff9800',
      description: 'Regional area oversight'
    },
    'central-manager': {
      name: 'Central Manager',
      icon: <AdminPanelSettings />,
      color: '#2196f3',
      description: 'Overall operations management'
    }
  };

  // Top 4 Pin codes for Central Manager
  const topPinCodes = [
    { pincode: '440001', city: 'Nagpur', orders: 1245, value: 875000, growth: '+12.5%' },
    { pincode: '491441', city: 'Rajnandgaon', orders: 892, value: 623000, growth: '+8.7%' },
    { pincode: '491001', city: 'Durg', orders: 756, value: 534000, growth: '+15.2%' },
    { pincode: '492001', city: 'Raipur', orders: 1134, value: 798000, growth: '+10.3%' }
  ];

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // Role-based data configuration
  const getRoleBasedStats = () => [
    {
      title: 'Placed Orders',
      value: (
        <Box display="flex" flexDirection="column" gap={0.25}>
          <Typography
            variant="h5"
            fontWeight="700"
            sx={{
              color: '#1976d2',
              fontSize: '2rem',
              textAlign: 'center',
              lineHeight: 1.1,
            }}
          >
            {placedOrdersTotal.count.toLocaleString()}
          </Typography>
          <Typography
            variant="caption"
            fontWeight="500"
            sx={{
              color: '#555454ff',
              fontSize: '0.95rem',
              textAlign: 'center',
              mb: 0.35,
              fontWeight: '300'
            }}
          >
            Placed Orders
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '0.7rem',
              textAlign: 'center',
              mb: 0.25,
              fontWeight: 500,
            }}
          >
            Order Date
          </Typography>
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              color: '#388e3c',
              fontSize: '1.2rem',
              textAlign: 'center',
            }}
          >
            ₹{placedOrdersTotal.amount.toLocaleString()}
          </Typography>
        </Box>
      ),
      subtitle: (
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
          <Box display="flex" alignItems="center" gap={0.25}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#388e3c' }} />
            <Typography variant="caption" fontWeight="500" color="#388e3c" sx={{ fontSize: '0.6rem' }}>
              {placedOrdersSummary.find(item => item.OrderStatus === 'DL')?.OrderCount || 0} Done
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.25}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#d32f2f' }} />
            <Typography variant="caption" fontWeight="500" color="#d32f2f" sx={{ fontSize: '0.6rem' }}>
              {placedOrdersSummary.find(item => item.OrderStatus === 'CN')?.OrderCount || 0} Cancel
            </Typography>
          </Box>
        </Box>
      ),
      icon: ShoppingCart,
      color: 'primary',
      numericValue: placedOrdersTotal.count,
      breakdown: placedOrdersSummary.map(item => {
        const itemColor = item.OrderStatus === 'DL' ? '#4caf50' :
          item.OrderStatus === 'PL' ? '#2196f3' :
            item.OrderStatus === 'PN' ? '#ff9800' :
              item.OrderStatus === 'CN' ? '#f44336' :
                item.OrderStatus === 'OD' ? '#00bcd4' :
                  item.OrderStatus === 'CR' ? '#ff5722' :
                    item.OrderStatus === 'HR' ? '#9c27b0' :
                      item.OrderStatus === 'DR' ? '#795548' : '#757575';
        return {
          label: item.OrderStatus === 'DL' ? 'Delivered' :
            item.OrderStatus === 'PL' ? 'Planned' :
              item.OrderStatus === 'PN' ? 'Pending' :
                item.OrderStatus === 'CN' ? 'Cancelled' :
                  item.OrderStatus === 'OD' ? 'Out for Delivery' :
                    item.OrderStatus === 'CR' ? 'Cancellation Request' :
                      item.OrderStatus === 'HR' ? 'Hold Request' :
                        item.OrderStatus === 'DR' ? 'Diversion Request' : item.OrderStatus,
          value: `${item.OrderCount} Orders / ₹${item.TotalAmount ? item.TotalAmount.toLocaleString() : '0'}`,
          orderStatus: item.OrderStatus,
          color: itemColor,
          icon: <ShoppingCart sx={{ color: itemColor, fontSize: 18 }} />
        };
      })
    },
    {
      title: 'Scheduled Deliveries',
      value: (
        <Box display="flex" flexDirection="column" gap={0.25}>
          <Typography
            variant="h5"
            fontWeight="700"
            sx={{
              color: '#1976d2',
              fontSize: '1rem',
              textAlign: 'center',
              lineHeight: 1.1,
            }}
          >
            {retailOrderSummary.totalCount.toLocaleString()}
          </Typography>
          <Typography
            variant="caption"
            fontWeight="500"
            sx={{
              color: '#666',
              fontSize: '0.65rem',
              textAlign: 'center',
              mb: 0.25,
            }}
          >
            Scheduled Deliveries
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '0.7rem',
              textAlign: 'center',
              mb: 0.25,
              fontWeight: 500,
            }}
          >
            Schedule Date
          </Typography>
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              color: '#388e3c',
              fontSize: '0.8rem',
              textAlign: 'center',
            }}
          >
            ₹{retailOrderSummary.totalAmount.toLocaleString()}
          </Typography>
        </Box>
      ),
      subtitle: (
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
          <Box display="flex" alignItems="center" gap={0.25}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#388e3c' }} />
            <Typography variant="caption" fontWeight="500" color="#388e3c" sx={{ fontSize: '0.6rem' }}>
              {retailOrderSummary.completedCount} Done
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.25}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#d32f2f' }} />
            <Typography variant="caption" fontWeight="500" color="#d32f2f" sx={{ fontSize: '0.6rem' }}>
              {retailOrderSummary.canceledCount} Cancel
            </Typography>
          </Box>
        </Box>
      ),
      icon: ShoppingCart,
      color: 'primary',
      numericValue: retailOrderSummary.totalCount,
    },
    {
      title: 'Wallet',
      value: (
        <Box display="flex" flexDirection="column" gap={0.25}>
          <Typography
            variant="h5"
            fontWeight="700"
            sx={{
              color: '#1976d2',
              fontSize: '1rem',
              textAlign: 'center',
              lineHeight: 1.1,
            }}
          >
            {(walletSummary.prepaidCount + walletSummary.subscriptionCount).toLocaleString()}
          </Typography>
          <Typography
            variant="caption"
            fontWeight="500"
            sx={{
              color: '#666',
              fontSize: '0.65rem',
              textAlign: 'center',
              mb: 0.25,
            }}
          >
            Total Accounts
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '0.7rem',
              textAlign: 'center',
              mb: 0.25,
              fontWeight: 500,
            }}
          >
            Bill Date
          </Typography>
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              color: '#388e3c',
              fontSize: '0.8rem',
              textAlign: 'center',
            }}
          >
            ₹{(walletSummary.prepaidAmount + walletSummary.subscriptionAmount).toLocaleString()}
          </Typography>
        </Box>
      ),
      subtitle: (
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
          <Box display="flex" alignItems="center" gap={0.25}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#1976d2' }} />
            <Typography variant="caption" fontWeight="500" color="#1976d2" sx={{ fontSize: '0.6rem' }}>
              {walletSummary.prepaidCount} Prepaid
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.25}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#388e3c' }} />
            <Typography variant="caption" fontWeight="500" color="#388e3c" sx={{ fontSize: '0.6rem' }}>
              {walletSummary.subscriptionCount} Sub
            </Typography>
          </Box>
        </Box>
      ),
      icon: AttachMoney,
      color: 'success',
      numericValue: walletSummary.prepaidCount + walletSummary.subscriptionCount,
    },
    {
      title: 'Horeca',
      value: (
        <Box display="flex" flexDirection="column" gap={0.25}>
          <Typography
            variant="h5"
            fontWeight="700"
            sx={{
              color: '#ff6b35',
              fontSize: '1rem',
              textAlign: 'center',
              lineHeight: 1.1,
            }}
          >
            {horecaSummary.SaleQty?.toLocaleString() || '0'}
          </Typography>
          <Typography
            variant="caption"
            fontWeight="500"
            sx={{
              color: '#666',
              fontSize: '0.65rem',
              textAlign: 'center',
              mb: 0.25,
            }}
          >
            KG Sold
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '0.7rem',
              textAlign: 'center',
              mb: 0.25,
              fontWeight: 500,
            }}
          >
            Bill Date
          </Typography>
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              color: '#388e3c',
              fontSize: '0.8rem',
              textAlign: 'center',
            }}
          >
            ₹{horecaSummary.TotalAmount?.toLocaleString() || '0'}
          </Typography>
        </Box>
      ),
      subtitle: (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 0.5 }}>
          <Box display="flex" alignItems="center" gap={0.25}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#ff6b35' }} />
            <Typography variant="caption" fontWeight="500" color="#ff6b35" sx={{ fontSize: '0.6rem' }}>
              {horecaSummary.Division || 'POULTRY'}
            </Typography>
          </Box>
        </Box>
      ),
      icon: ShowChart,
      color: 'warning',
      numericValue: horecaSummary.TotalAmount || 0,
    },
    {
      title: 'Happy Hour',
      value: (
        <Box display="flex" flexDirection="column" gap={0.25}>
          <Typography
            variant="h5"
            fontWeight="700"
            sx={{
              color: '#9c27b0',
              fontSize: '1rem',
              textAlign: 'center',
              lineHeight: 1.1,
            }}
          >
            {happyHourSummary.totalQty?.toLocaleString() || '0'}
          </Typography>
          <Typography
            variant="caption"
            fontWeight="500"
            sx={{
              color: '#666',
              fontSize: '0.65rem',
              textAlign: 'center',
              mb: 0.25,
            }}
          >
            KG Happy Hour
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '0.7rem',
              textAlign: 'center',
              mb: 0.25,
              fontWeight: 500,
            }}
          >
            Bill Date
          </Typography>
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              color: '#388e3c',
              fontSize: '0.8rem',
              textAlign: 'center',
            }}
          >
            ₹{happyHourSummary.totalAmount?.toLocaleString() || '0'}
          </Typography>
        </Box>
      ),
      subtitle: (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 0.5 }}>
          <Box display="flex" alignItems="center" gap={0.25}>
            <AccessTime sx={{ fontSize: 10, color: '#9c27b0' }} />
            <Typography variant="caption" fontWeight="500" color="#9c27b0" sx={{ fontSize: '0.6rem' }}>
              {happyHourSummary.divisions?.length || 0} Divs
            </Typography>
          </Box>
        </Box>
      ),
      icon: Schedule,
      color: 'secondary',
      numericValue: happyHourSummary.totalAmount || 0,
    },
    {
      title: 'Retail',
      value: (
        <Box display="flex" flexDirection="column" gap={0.25}>
          <Typography
            variant="h5"
            fontWeight="700"
            sx={{
              color: '#2e7d32',
              fontSize: '1rem',
              textAlign: 'center',
              lineHeight: 1.1,
            }}
          >
            {retailSummary.totalQty?.toLocaleString() || '0'}
          </Typography>
          <Typography
            variant="caption"
            fontWeight="500"
            sx={{
              color: '#666',
              fontSize: '0.65rem',
              textAlign: 'center',
              mb: 0.25,
            }}
          >
            KG Retail
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#666',
              fontSize: '0.7rem',
              textAlign: 'center',
              mb: 0.25,
              fontWeight: 500,
            }}
          >
            Bill Date
          </Typography>
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              color: '#388e3c',
              fontSize: '0.8rem',
              textAlign: 'center',
            }}
          >
            ₹{retailSummary.totalAmount?.toLocaleString() || '0'}
          </Typography>
        </Box>
      ),
      subtitle: (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 0.5 }}>
          <Box display="flex" alignItems="center" gap={0.25}>
            <Store sx={{ fontSize: 10, color: '#2e7d32' }} />
            <Typography variant="caption" fontWeight="500" color="#2e7d32" sx={{ fontSize: '0.6rem' }}>
              POS & WEB
            </Typography>
          </Box>
        </Box>
      ),
      icon: Store,
      color: 'success',
      numericValue: retailSummary.totalAmount || 31000,
    },
    {
      title: 'Division Sales',
      value: (
        <Box
          display="flex"
          flexDirection="column"
          gap={1.5}
          sx={{
            width: '100%',
            p: 2,
            maxHeight: '280px',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
          }}
        >
          {salesSummaryData && salesSummaryData.length > 0 ? (
            <Grid container spacing={2}>
              {salesSummaryData.map((division, index) => {
                const colors = ['#1976d2', '#388e3c', '#ff6b35', '#9c27b0', '#00bcd4', '#ff5722'];
                const color = colors[index % colors.length];
                return (
                  <Grid item xs={6} sm={4} md={2} key={division.name}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${color}15, ${color}05)`,
                        border: `2px solid ${color}20`,
                        textAlign: 'center',
                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: `linear-gradient(90deg, transparent, ${color}20, transparent)`,
                          transition: 'left 0.6s ease',
                        },
                        '&:hover': {
                          backgroundColor: `${color}25`,
                          border: `2px solid ${color}60`,
                          boxShadow: `0 8px 32px ${color}40, inset 0 1px 0 rgba(255,255,255,0.2)`,
                          transform: 'translateY(-4px) scale(1.05)',
                          '&:before': {
                            left: '100%',
                          }
                        },
                        '&:active': {
                          transform: 'translateY(-2px) scale(1.02)',
                        }
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="700"
                        sx={{
                          color: color,
                          fontSize: '0.9rem',
                          display: 'block',
                          mb: 1,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.1)',
                            textShadow: `0 0 10px ${color}60`,
                          }
                        }}
                      >
                        {division.name}
                      </Typography>
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="h6"
                          fontWeight="800"
                          sx={{
                            color: color,
                            fontSize: '1.1rem',
                            lineHeight: 1.2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              textShadow: `0 0 15px ${color}70`,
                              transform: 'scale(1.05)',
                            }
                          }}
                        >
                          {division.count.toLocaleString()}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: `${color}80`,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                          }}
                        >
                          Units
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        fontWeight="700"
                        sx={{
                          color: color,
                          fontSize: '0.95rem',
                          background: `linear-gradient(135deg, ${color}, ${color}CC)`,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            filter: `drop-shadow(0 0 8px ${color}60)`,
                            transform: 'translateY(-1px)',
                          }
                        }}
                      >
                        ₹{division.totalAmount.toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                py: 4,
                background: 'linear-gradient(135deg, #f5f5f5, #e8e8e8)',
                borderRadius: 2,
                border: '2px dashed #ddd'
              }}
            >
              <Typography variant="h6" color="text.secondary" fontWeight="500">
                Loading divisions...
              </Typography>
            </Box>
          )}
        </Box>
      ),
      subtitle: (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 0.5 }}>
          <Box display="flex" alignItems="center" gap={0.25}>
            <Analytics sx={{ fontSize: 10, color: '#1976d2' }} />
            <Typography variant="caption" fontWeight="500" color="#1976d2" sx={{ fontSize: '0.6rem' }}>
              {salesSummaryData?.length || 0} Divisions
            </Typography>
          </Box>
        </Box>
      ),
      icon: Analytics,
      color: 'info',
      numericValue: getProductSalesTotals().totalSale || 0,
    },
    // Add other cards below as needed, using API
  ];
  // Fetch sales summary data with caching
  const fetchSalesSummaryData = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isApiLoading('salesSummary')) {
      return;
    }

    const fromDateFormatted = formatDateForAPI(fromDate);
    const toDateFormatted = formatDateForAPI(toDate);
    const params = { fromDate: fromDateFormatted, toDate: toDateFormatted, branchId: selectedBranchID, areaId: selectedAreaID, api: 'salesummary' };

    // Check if API should be called
    if (!shouldCallAPI('salesSummary', params)) {
      const cachedData = apiCache['salesSummary'];
      if (cachedData) {
        setSalesSummaryData(cachedData);
        return;
      }
    }

    setApiLoading('salesSummary', true);
    try {
      const response = await fetchWithAuth(`${local_signage}/api/salesummary?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=Summary`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();

      // Transform API response and aggregate by division
      const divisionMap = {};
      data.forEach(item => {
        const division = item.Division;
        if (!divisionMap[division]) {
          divisionMap[division] = {
            name: division,
            count: 0,
            totalAmount: 0
          };
        }
        divisionMap[division].count += item.SaleQty;
        divisionMap[division].totalAmount += item.TotalAmount;
      });

      const transformedData = Object.values(divisionMap);
      const totalAmount = transformedData.reduce((sum, item) => sum + item.totalAmount, 0);

      // Add performance percentage
      transformedData.forEach(item => {
        item.performance = totalAmount > 0 ? Math.round((item.totalAmount / totalAmount) * 100) : 0;
        item.count = Math.round(item.count * 100) / 100;
      });

      setSalesSummaryData(transformedData);
      updateApiCache('salesSummary', params, transformedData);
    } catch (error) {
      setSalesSummaryData([]);
    } finally {
      setApiLoading('salesSummary', false);
    }
  }, [fromDate, toDate, selectedBranchID, selectedAreaID, shouldCallAPI, apiCache, updateApiCache, isApiLoading, setApiLoading]);


  const fetchRetailOrderSummary = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (isApiLoading('retailOrderSummary')) {
      return;
    }

    const fromDateFormatted = formatDateForAPI(fromDate);
    const toDateFormatted = formatDateForAPI(toDate);
    const params = { fromDate: fromDateFormatted, toDate: toDateFormatted, branchId: selectedBranchID, areaId: selectedAreaID, api: 'triporder' };

    // Check if API should be called
    if (!shouldCallAPI('retailOrderSummary', params)) {
      const cachedData = apiCache['retailOrderSummary'];
      if (cachedData) {
        setRetailOrderSummary(cachedData);
        return;
      }
    }

    setApiLoading('retailOrderSummary', true);
    try {
      const response = await fetchWithAuth(`${local_signage}/api/triporder?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=ALL&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=summary`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();

      // Aggregate data by OrderStatus
      const aggregatedData = Object.values(data.reduce((acc, item) => {
        if (!acc[item.OrderStatus]) {
          acc[item.OrderStatus] = { OrderStatus: item.OrderStatus, OrderCount: 0, TotalAmount: 0 };
        }
        acc[item.OrderStatus].OrderCount += (item.OrderCount || 0);
        acc[item.OrderStatus].TotalAmount += (item.TotalAmount || 0);
        return acc;
      }, {}));

      // Calculate totals from aggregated summary data
      const totalCount = aggregatedData.reduce((sum, item) => sum + item.OrderCount, 0);
      const totalAmount = aggregatedData.reduce((sum, item) => sum + item.TotalAmount, 0);
      const completed = aggregatedData.find(item => item.OrderStatus === 'PL')?.OrderCount || 0;
      const canceled = aggregatedData.find(item => item.OrderStatus === 'CN')?.OrderCount || 0;

      const summaryData = {
        totalCount,
        totalAmount,
        completedCount: completed,
        canceledCount: canceled,
        rawData: aggregatedData // Store aggregated data for breakdown
      };

      setRetailOrderSummary(summaryData);
      updateApiCache('retailOrderSummary', params, summaryData);
    } catch (error) {
      const fallbackData = {
        totalCount: 0,
        totalAmount: 0,
        completedCount: 0,
        canceledCount: 0,
        rawData: []
      };
      setRetailOrderSummary(fallbackData);
    } finally {
      setApiLoading('retailOrderSummary', false);
    }
  }, [fromDate, toDate, selectedBranchID, selectedAreaID, shouldCallAPI, apiCache, updateApiCache, isApiLoading, setApiLoading]);

  // Fetch detailed sales data with caching
  const fetchDetailedSalesData = useCallback(async (division = null) => {
    const fromDateFormatted = formatDateForAPI(fromDate);
    const toDateFormatted = formatDateForAPI(toDate);
    const params = { fromDate: fromDateFormatted, toDate: toDateFormatted, branchId: selectedBranchID, areaId: selectedAreaID, api: 'detailedSales', division };

    // Check if API should be called
    if (!shouldCallAPI('detailedSalesData', params)) {
      const cachedData = apiCache['detailedSalesData'];
      if (cachedData) {
        const filteredData = division ? cachedData.filter(item => item.Division === division) : cachedData;
        setDetailedSalesData(filteredData);
        setSelectedDivision(division);
        return;
      }
    }

    try {
      const response = await fetchWithAuth(`${local_signage}/api/salesummary?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=ALL`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();

      // Filter by division if specified
      const filteredData = division ? data.filter(item => item.Division === division) : data;
      setDetailedSalesData(filteredData);
      setSelectedDivision(division);
      updateApiCache('detailedSalesData', params, data); // Cache the full data for future filtering
    } catch (error) {
      setDetailedSalesData([]);
    }
  }, [fromDate, toDate, selectedBranchID, selectedAreaID, shouldCallAPI, apiCache, updateApiCache]);

  // Fetch retail sales summary data
  const fetchRetailSalesSummaryData = useCallback(async () => {
    const fromDateFormatted = formatDateForAPI(fromDate);
    const toDateFormatted = formatDateForAPI(toDate);
    const params = { fromDate: fromDateFormatted, toDate: toDateFormatted, branchId: selectedBranchID, areaId: selectedAreaID, api: 'retailsummary' };

    // Temporarily disable cache to test fresh data
    // if (!shouldCallAPI('retailSummary', params)) {
    //   const cachedData = apiCache['retailSummary'];
    //   if (cachedData) {
    //     console.log('Using cached retail data:', cachedData); // Debug log
    //     console.log('Cached channelBreakdown:', cachedData.channelBreakdown); // Debug log
    //     setRetailSummary(cachedData);
    //     return;
    //   }
    // }

    setApiLoading('retailSummary', true);
    try {
      const response = await fetchWithAuth(`${local_signage}/api/salesummary?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=ALL`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();

      // Calculate totals
      const totalQty = data.reduce((sum, item) => sum + (item.SaleQty || 0), 0);
      const totalAmount = data.reduce((sum, item) => sum + (item.TotalAmount || 0), 0);

      // Group by ChannelID
      const channelData = data.reduce((acc, item, index) => {
        // Try different possible field names for ChannelID
        const channelId = item.ChannelID || item.channelId || item.Channel || item.channel || 'Unknown';

        const existing = acc.find(ch => ch.channelId === channelId);
        if (existing) {
          existing.qty += item.SaleQty || 0;
          existing.amount += item.TotalAmount || 0;
        } else {
          acc.push({
            channelId: channelId,
            qty: item.SaleQty || 0,
            amount: item.TotalAmount || 0
          });
        }
        return acc;
      }, []);

      // Calculate individual channel values
      const posData = channelData.find(ch => ch.channelId === 'POS') || { qty: 0, amount: 0 };
      const webData = channelData.find(ch => ch.channelId === 'WEB') || { qty: 0, amount: 0 };

      const retailData = {
        totalQty,
        totalAmount,
        posQty: posData.qty,
        posAmount: posData.amount,
        webQty: webData.qty,
        webAmount: webData.amount,
        channelBreakdown: channelData
      };

      setRetailSummary(retailData);
      updateApiCache('retailSummary', params, retailData);
    } catch (error) {
      setRetailSummary({
        totalQty: 0,
        totalAmount: 0,
        posQty: 0,
        posAmount: 0,
        webQty: 0,
        webAmount: 0,
        channelBreakdown: []
      });
    } finally {
      setApiLoading('retailSummary', false);
    }
  }, [fromDate, toDate, selectedBranchID, selectedAreaID, shouldCallAPI, apiCache, updateApiCache, isApiLoading, setApiLoading]);

  // Fetch retail detailed data with ChannelID filtering
  const fetchRetailDetailedData = useCallback(async (channelFilter = null) => {
    const fromDateFormatted = formatDateForAPI(fromDate);
    const toDateFormatted = formatDateForAPI(toDate);

    try {
      // Build URL - use direct filtering approach for instant results
      const response = await fetchWithAuth(
        `${local_signage}/api/salesummary?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=ALL`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );
      const data = await response.json();

      // Apply client-side filtering for instant results
      const filteredData = channelFilter
        ? data.filter(item => item.ChannelID === channelFilter)
        : data;

      // Set data immediately without loading delays
      setRetailDetailedData(filteredData);
      return filteredData;
    } catch (error) {
      setRetailDetailedData([]);
      return [];
    }
  }, [fromDate, toDate, selectedBranchID, selectedAreaID]);

  // Centralized data fetching - DISABLED to prevent duplicate API calls
  // handleRefresh() already calls all these functions
  // useEffect(() => {
  //   if (fromDate && toDate) {
  //     fetchSalesSummaryData();
  //     fetchRetailOrderSummary();
  //     fetchRetailSalesSummaryData();
  //   }
  // }, [fromDate, toDate, selectedBranchID, fetchSalesSummaryData, fetchRetailOrderSummary, fetchRetailSalesSummaryData]);


  // Sample data
  const salesData = [
    { month: 'Jan', sales: 120000, year: '2024' },
    { month: 'Feb', sales: 135000, year: '2024' },
    { month: 'Mar', sales: 148000, year: '2024' },
    { month: 'Apr', sales: 162000, year: '2024' },
    { month: 'May', sales: 178000, year: '2024' },
    { month: 'Jun', sales: 195000, year: '2024' },
    { month: 'Jul', sales: 210000, year: '2024' },
    { month: 'Aug', sales: 185000, year: '2024' },
    { month: 'Sep', sales: 225000, year: '2024' },
    { month: 'Oct', sales: 245000, year: '2024' },
    { month: 'Nov', sales: 268000, year: '2024' },
    { month: 'Dec', sales: 290000, year: '2024' }
  ];

  const worstProducts = [
    { name: 'Poultry', performance: 24, count: 156 },
    { name: 'Oil', performance: 32, count: 243 },
    { name: 'Masale', performance: 18, count: 89 },
    { name: 'Eggs', performance: 45, count: 312 },
    { name: 'Ready to Cook', performance: 38, count: 178 }
  ];

  // Delivery Status state - will be populated by API
  const [deliveryStatus, setDeliveryStatus] = useState([
    { name: 'Cancelled', count: 0, color: '#f44336' },
    { name: 'Delivered', count: 0, color: '#4caf50' },
    { name: 'Planned', count: 0, color: '#2196f3' },
    { name: 'Pending', count: 0, color: '#ff9800' },
    { name: 'Hold Request', count: 0, color: '#9c27b0' },
    { name: 'Cancellation Request', count: 0, color: '#ff5722' },
    { name: 'Deviation Request', count: 0, color: '#795548' },
    { name: 'Online Orders', count: 0, color: '#333', isBold: true }
  ]);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, active: true },
    { text: 'Products', icon: <Inventory /> },
    { text: 'Orders', icon: <Receipt /> },
    { text: 'Customers', icon: <People /> },
    { text: 'Analytics', icon: <Analytics /> }
  ];



  const fetchGraphData = useCallback(async (division = 'POULTRY', fromDate, toDate) => {
    // Prevent multiple simultaneous calls
    if (isApiLoading('graphData')) {
      return;
    }

    const fromDateFormatted = fromDate ? fromDate.format('YYYYMMDD') : '';
    const toDateFormatted = toDate ? toDate.format('YYYYMMDD') : '';
    const params = { fromDate: fromDateFormatted, toDate: toDateFormatted, branchId: selectedBranchID, areaId: selectedAreaID, api: 'graphData', division };

    // Check if API should be called
    if (!shouldCallAPI('graphData', params)) {
      const cachedData = apiCache['graphData'];
      if (cachedData) {
        setGraphData(cachedData);
        return;
      }
    }

    setApiLoading('graphData', true);
    setGraphLoading(true);
    try {
      const response = await fetchWithAuth(
        `${local_signage}/api/salesummary?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=ALL`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );
      const data = await response.json();
      // Filter by division
      const filtered = data.filter(item => item.Division === division);

      // Group by ItemName and sum TotalAmount, SaleQty, PrevTotalAmount, PrevSaleQty
      const grouped = {};
      filtered.forEach(item => {
        if (!grouped[item.ItemName]) {
          grouped[item.ItemName] = {
            ItemName: item.ItemName,
            TotalAmount: 0,
            SaleQty: 0,
            PrevTotalAmount: 0,
            PrevSaleQty: 0,
          };
        }
        grouped[item.ItemName].TotalAmount += item.TotalAmount || 0;
        grouped[item.ItemName].SaleQty += item.SaleQty || 0;
        grouped[item.ItemName].PrevTotalAmount += item.PrevTotalAmount || 0;
        grouped[item.ItemName].PrevSaleQty += item.PrevSaleQty || 0;
      });

      // Convert grouped object to array
      const graphArray = Object.values(grouped);
      setGraphData(graphArray);
      updateApiCache('graphData', params, graphArray);
    } catch (error) {
      setGraphData([]);
    } finally {
      setGraphLoading(false);
      setApiLoading('graphData', false);
    }
  }, [selectedBranchID, selectedAreaID, shouldCallAPI, apiCache, updateApiCache, setApiLoading]);

  const fetchWalletData = useCallback(async (typeFilter = null, fromDate, toDate) => {
    // Prevent multiple simultaneous calls
    if (isApiLoading('walletData')) {
      return;
    }

    const fromDateFormatted = fromDate ? fromDate.format('YYYYMMDD') : '';
    const toDateFormatted = toDate ? toDate.format('YYYYMMDD') : '';
    const params = { fromDate: fromDateFormatted, toDate: toDateFormatted, branchId: selectedBranchID, areaId: selectedAreaID, api: 'wallet', typeFilter };

    // Check if API should be called
    if (!shouldCallAPI('walletData', params)) {
      const cachedData = apiCache['walletData'];
      if (cachedData) {
        const { summary, data } = cachedData;
        setWalletSummary(summary);

        // Filter for table if needed
        const filteredData = typeFilter ? data.filter(item => item.OrderType === typeFilter) : data;
        setWalletTableData(filteredData);
        return;
      }
    }

    setApiLoading('walletData', true);
    setIsWalletTableLoading(true);
    try {
      const response = await fetchWithAuth(`${local_signage}/api/wallet?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=ALL&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=ALL`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      let data = await response.json();

      // Calculate summary
      const prepaid = data.filter(item => item.OrderType === 'PREPD');
      const subscription = data.filter(item => item.OrderType === 'SBSCR');
      const summary = {
        prepaidCount: prepaid.length,
        prepaidAmount: prepaid.reduce((sum, item) => sum + (item.TotalAmount || 0), 0),
        subscriptionCount: subscription.length,
        subscriptionAmount: subscription.reduce((sum, item) => sum + (item.TotalAmount || 0), 0),
      };
      setWalletSummary(summary);

      // Filter for table if needed
      const filteredData = typeFilter ? data.filter(item => item.OrderType === typeFilter) : data;
      setWalletTableData(filteredData);

      // Cache both summary and full data
      updateApiCache('walletData', params, { summary, data });
    } catch (error) {
      setWalletTableData([]);
      setWalletSummary({
        prepaidCount: 0,
        prepaidAmount: 0,
        subscriptionCount: 0,
        subscriptionAmount: 0,
      });
    } finally {
      setIsWalletTableLoading(false);
      setApiLoading('walletData', false);
    }
  }, [selectedBranchID, selectedAreaID, shouldCallAPI, apiCache, updateApiCache, setApiLoading, isApiLoading]);

  // Fetch Happy Hour detailed table data
  const fetchHappyHourTableData = useCallback(async (fromDate, toDate) => {
    const fromDateFormatted = fromDate ? fromDate.format('YYYYMMDD') : '';
    const toDateFormatted = toDate ? toDate.format('YYYYMMDD') : '';
    const params = { fromDate: fromDateFormatted, toDate: toDateFormatted, branchId: selectedBranchID, areaId: selectedAreaID, api: 'happyhour-table' };

    // Check if API should be called
    if (!shouldCallAPI('happyHourTableData', params)) {
      const cachedData = apiCache['happyHourTableData'];
      if (cachedData) {
        setHappyHourDetailedData(cachedData);
        return;
      }
    }

    setApiLoading('happyHourTableData', true);
    setIsHappyHourTableLoading(true);
    try {
      const response = await fetchWithAuth(`${local_signage}/api/happyhour?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=ALL&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=ALL`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setHappyHourDetailedData(data);
      updateApiCache('happyHourTableData', params, data);
    } catch (error) {
      setHappyHourDetailedData([]);
    } finally {
      setIsHappyHourTableLoading(false);
      setApiLoading('happyHourTableData', false);
    }
  }, [selectedBranchID, selectedAreaID, shouldCallAPI, apiCache, updateApiCache, setApiLoading]);


  // DISABLED - handleRefresh() already calls fetchWalletData
  // useEffect(() => {
  //   if (fromDate && toDate) {
  //     fetchWalletData(null, fromDate, toDate);
  //   }
  // }, [fromDate, toDate, selectedBranchID, fetchWalletData]);




  // Fetch graph data only when graph filter changes (not area changes)
  // Remove selectedAreaID dependency to prevent unnecessary calls
  useEffect(() => {
    if (fromDate && toDate && graphFilter) {
      const timer = setTimeout(() => {
        fetchGraphData(graphFilter, fromDate, toDate);
      }, 300); // Debounce graph data calls
      return () => clearTimeout(timer);
    }
  }, [graphFilter]); // Only trigger on graphFilter change

  // DISABLED - handleRefresh() already calls these functions  
  // useEffect(() => {
  //   if (fromDate && toDate) {
  //     fetchHappyHourData(fromDate, toDate);
  //     fetchHorecaSummary(fromDate, toDate);
  //   }
  // }, [fromDate, toDate]);



  const fetchTotalOrdersTableData = async (statusFilter = null, fromDate, toDate) => {
    setApiLoading('totalOrdersData', true);
    setIsTotalOrdersTableLoading(true);
    try {
      const fromDateFormatted = fromDate ? fromDate.format('YYYYMMDD') : '';
      const toDateFormatted = toDate ? toDate.format('YYYYMMDD') : '';
      const response = await fetchWithAuth(`${local_signage}/api/triporder?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=ALL&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=ScheduleOrderDetails`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      let data = await response.json();
      // If statusFilter is set, filter by OrderStatusId
      if (statusFilter) {
        data = data.filter(item => item.OrderStatusId === statusFilter);
      }
      setTotalOrdersTableData(data);
    } catch (error) {
      setTotalOrdersTableData([]);
    }
    setApiLoading('totalOrdersData', false);
    setIsTotalOrdersTableLoading(false);
  };

  // Fetch Placed Orders Summary
  const fetchPlacedOrdersSummary = useCallback(async (fromDate, toDate) => {
    // Prevent multiple simultaneous calls
    if (isApiLoading('placedOrdersSummary')) {
      return;
    }

    const fromDateFormatted = fromDate ? fromDate.format('YYYYMMDD') : '';
    const toDateFormatted = toDate ? toDate.format('YYYYMMDD') : '';
    const params = { fromDate: fromDateFormatted, toDate: toDateFormatted, branchId: selectedBranchID, areaId: selectedAreaID, api: 'placedorders-summary' };

    // Check if API should be called
    if (!shouldCallAPI('placedOrdersSummary', params)) {
      const cachedData = apiCache['placedOrdersSummary'];
      if (cachedData) {
        setPlacedOrdersSummary(cachedData.rawData || []);
        setPlacedOrdersTotal({ count: cachedData.totalCount || 0, amount: cachedData.totalAmount || 0 });
        setApiLoading('placedOrdersSummary', false);
        return;
      }
    }

    setApiLoading('placedOrdersSummary', true);
    try {
      const response = await fetchWithAuth(`${local_signage}/api/triporder?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=ALL&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=OrderSUMMARY`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();

      // Aggregate data by OrderStatus
      const aggregatedData = Object.values(data.reduce((acc, item) => {
        if (!acc[item.OrderStatus]) {
          acc[item.OrderStatus] = { OrderStatus: item.OrderStatus, OrderCount: 0, TotalAmount: 0 };
        }
        acc[item.OrderStatus].OrderCount += (item.OrderCount || 0);
        acc[item.OrderStatus].TotalAmount += (item.TotalAmount || 0);
        return acc;
      }, {}));

      // Calculate totals
      const totalCount = aggregatedData.reduce((sum, item) => sum + item.OrderCount, 0);
      const totalAmount = aggregatedData.reduce((sum, item) => sum + item.TotalAmount, 0);

      const summaryData = {
        totalCount,
        totalAmount,
        rawData: aggregatedData
      };

      setPlacedOrdersSummary(aggregatedData);
      setPlacedOrdersTotal({ count: totalCount, amount: totalAmount });
      updateApiCache('placedOrdersSummary', params, summaryData);
    } catch (error) {
      setPlacedOrdersSummary([]);
      setPlacedOrdersTotal({ count: 0, amount: 0 });
    }
    setApiLoading('placedOrdersSummary', false);
  }, [selectedBranchID, selectedAreaID, shouldCallAPI, apiCache, updateApiCache, setApiLoading, isApiLoading]);

  // Fetch Placed Orders Table Data
  const fetchPlacedOrdersTableData = useCallback(async (statusFilter = null, fromDate, toDate) => {
    // Prevent multiple simultaneous calls
    if (isApiLoading('placedOrdersTableData')) {
      return;
    }

    const fromDateFormatted = fromDate ? fromDate.format('YYYYMMDD') : '';
    const toDateFormatted = toDate ? toDate.format('YYYYMMDD') : '';
    const params = { fromDate: fromDateFormatted, toDate: toDateFormatted, branchId: selectedBranchID, areaId: selectedAreaID, statusFilter, api: 'placedorders-table' };

    // Check if API should be called
    if (!shouldCallAPI('placedOrdersTableData', params)) {
      const cachedData = apiCache['placedOrdersTableData'];
      if (cachedData) {
        setPlacedOrdersTableData(cachedData);
        return;
      }
    }

    setApiLoading('placedOrdersTableData', true);
    setIsPlacedOrdersTableLoading(true);
    try {
      const response = await fetchWithAuth(`${local_signage}/api/triporder?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=ALL&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=OrderDetails`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      let data = await response.json();
      // If statusFilter is set, filter by OrderStatusId
      if (statusFilter) {
        data = data.filter(item => item.OrderStatusId === statusFilter);
      }
      setPlacedOrdersTableData(data);
      updateApiCache('placedOrdersTableData', params, data);
    } catch (error) {
      setPlacedOrdersTableData([]);
    }
    setApiLoading('placedOrdersTableData', false);
    setIsPlacedOrdersTableLoading(false);
  }, [selectedBranchID, selectedAreaID, shouldCallAPI, apiCache, updateApiCache, setApiLoading, isApiLoading]);

  // Helper function to parse ItemDetails string
  const parseItemDetails = (itemDetailsString) => {
    if (!itemDetailsString) return [];

    try {
      // First try to parse as standard JSON
      const parsed = JSON.parse(itemDetailsString);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (e) {
      // If JSON parse fails, fall back to custom parsing
      // console.log('JSON parse failed, using fallback:', e);
    }

    try {
      // Split by },{ pattern to get individual items
      const items = itemDetailsString.split('},{').map(item => {
        // Remove leading/trailing braces
        item = item.replace(/^{|}$/g, '');

        // Parse key-value pairs
        const parts = item.split(',');
        const itemObj = {};

        parts.forEach(part => {
          const [key, ...valueParts] = part.split(':');
          const value = valueParts.join(':'); // Handle cases where value contains ':'
          if (key && value) {
            // Remove potential quotes from key and value
            const cleanKey = key.trim().replace(/^"|"$/g, '').trim();
            const cleanValue = value.trim().replace(/^"|"$/g, '').trim();
            itemObj[cleanKey] = cleanValue;
          }
        });

        return itemObj;
      });

      return items;
    } catch (error) {
      return [];
    }
  };

  // Search filter function for tables
  const filterTableData = (data, query) => {
    if (!query || query.trim() === '') return data;

    const searchTerm = query.toLowerCase().trim();

    return data.filter(row => {
      // Search in SaleOrderID, POSCustomerID, SaleId, or any ID-like field
      const saleOrderId = (row.SaleOrderID || '').toString().toLowerCase();
      const customerId = (row.POSCustomerID || '').toString().toLowerCase();
      const saleId = (row.SaleId || '').toString().toLowerCase();
      const orderId = (row.OrderID || '').toString().toLowerCase();

      return saleOrderId.includes(searchTerm) ||
        customerId.includes(searchTerm) ||
        saleId.includes(searchTerm) ||
        orderId.includes(searchTerm);
    });
  };

  // Toggle row expansion
  const toggleOrderRow = (orderId) => {
    setExpandedOrderRows(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const GraphFilterButtons = ({ value, onChange }) => (
    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
      {['POULTRY', 'EGG', 'NONPOULTRY', 'RTC'].map(filter => (
        <Button
          key={filter}
          variant={value === filter ? 'contained' : 'outlined'}
          color="primary"
          size="small"
          onClick={() => onChange(filter)}
          sx={{
            fontWeight: 600,
            borderRadius: 2,
            minWidth: 90,
            height: 30,
            bgcolor: value === filter ? 'primary.main' : 'white',
            color: value === filter ? 'white' : 'primary.main',
            boxShadow: value === filter ? 2 : 0,
          }}
        >
          {filter}
        </Button>
      ))}
    </Box>
  );

  const fetchHorecaTableData = async (fromDate, toDate) => {
    try {
      const fromDateFormatted = fromDate ? fromDate.format('YYYYMMDD') : '';
      const toDateFormatted = toDate ? toDate.format('YYYYMMDD') : '';
      const response = await fetchWithAuth(`${local_signage}/api/horeca?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=POS&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=ALL`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setHorecaTableData(data);
      return data;
    } catch (error) {
      setHorecaTableData([]);
      return [];
    }
  };




  const fetchHorecaSummary = async (fromDate, toDate) => {
    setApiLoading('horecaSummary', true);
    try {
      const fromDateFormatted = fromDate ? fromDate.format('YYYYMMDD') : '';
      const toDateFormatted = toDate ? toDate.format('YYYYMMDD') : '';
      const response = await fetchWithAuth(`${local_signage}/api/horeca?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=POS&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=Summary`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();

      // Calculate totals from all divisions
      const totalQty = data.reduce((sum, item) => sum + (item.SaleQty || 0), 0);
      const totalAmount = data.reduce((sum, item) => sum + (item.TotalAmount || 0), 0);
      const divisions = [...new Set(data.map(item => item.Division).filter(Boolean))];

      setHorecaSummary({
        SaleQty: totalQty,
        TotalAmount: totalAmount,
        Division: divisions.length > 0 ? divisions.join(', ') : 'POULTRY'
      });
    } catch (error) {
      setHorecaSummary({ SaleQty: 0, TotalAmount: 0, Division: 'POULTRY' });
    } finally {
      setApiLoading('horecaSummary', false);
    }
  };

  // Fetch Delivery Status data from trip orders API
  const fetchDeliveryStatus = useCallback(async (fromDate, toDate) => {
    const fromDateFormatted = fromDate ? fromDate.format('YYYYMMDD') : '';
    const toDateFormatted = toDate ? toDate.format('YYYYMMDD') : '';
    const params = { fromDate: fromDateFormatted, toDate: toDateFormatted, branchId: selectedBranchID, areaId: selectedAreaID, api: 'delivery-status' };

    // Check if API should be called
    if (!shouldCallAPI('deliveryStatus', params)) {
      const cachedData = apiCache['deliveryStatus'];
      if (cachedData) {
        return;
      }
    }

    setApiLoading('deliveryStatus', true);
    try {
      const response = await fetchWithAuth(`${local_signage}/api/triporder?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=ALL&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=summary`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();

      // Map API status codes to display names
      const statusMapping = {
        'CN': { name: 'Cancelled', color: '#f44336' },
        'CR': { name: 'Cancellation Request', color: '#ff5722' },
        'HR': { name: 'Hold Request', color: '#9c27b0' },
        'PL': { name: 'Planned', color: '#2196f3' },
        'PN': { name: 'Pending', color: '#ff9800' },
        'DR': { name: 'Diversion Request', color: '#795548' },
        'DL': { name: 'Delivered', color: '#4caf50' },
        'OD': { name: 'Out for Delivery', color: '#00bcd4' }
      };

      // Initialize delivery status array
      const statusArray = [];
      let totalOrders = 0;
      let totalAmount = 0;

      // Process API data
      data.forEach(item => {
        const statusInfo = statusMapping[item.OrderStatus];
        if (statusInfo) {
          const existingStatus = statusArray.find(s => s.name === statusInfo.name);
          if (existingStatus) {
            existingStatus.count += (item.OrderCount || 0);
            existingStatus.amount += (item.TotalAmount || 0);
          } else {
            statusArray.push({
              name: statusInfo.name,
              count: item.OrderCount || 0,
              amount: item.TotalAmount || 0,
              color: statusInfo.color
            });
          }
          totalOrders += (item.OrderCount || 0);
          totalAmount += (item.TotalAmount || 0);
        }
      });

      // Add statuses with 0 count for those not returned by API
      Object.values(statusMapping).forEach(statusInfo => {
        if (!statusArray.find(item => item.name === statusInfo.name)) {
          statusArray.push({
            name: statusInfo.name,
            count: 0,
            amount: 0,
            color: statusInfo.color
          });
        }
      });

      // Add total orders at the end
      statusArray.push({
        name: 'Online Orders',
        count: totalOrders,
        amount: totalAmount,
        color: '#333',
        isBold: true
      });

      // Sort to ensure consistent order (optional)
      const sortOrder = ['Delivered', 'Out for Delivery', 'Planned', 'Pending', 'Hold Request', 'Diversion Request', 'Cancellation Request', 'Cancelled'];
      statusArray.sort((a, b) => {
        if (a.isBold && !b.isBold) return 1;
        if (!a.isBold && b.isBold) return -1;
        if (a.isBold && b.isBold) return 0;

        const aIndex = sortOrder.indexOf(a.name);
        const bIndex = sortOrder.indexOf(b.name);
        return aIndex - bIndex;
      });

      setDeliveryStatus(statusArray);

      // Cache the data
      updateApiCache('deliveryStatus', statusArray);

    } catch (error) {
      // Set default empty state on error
      setDeliveryStatus([
        { name: 'Cancelled', count: 0, color: '#f44336' },
        { name: 'Delivered', count: 0, color: '#4caf50' },
        { name: 'Planned', count: 0, color: '#2196f3' },
        { name: 'Pending', count: 0, color: '#ff9800' },
        { name: 'Hold Request', count: 0, color: '#9c27b0' },
        { name: 'Cancellation Request', count: 0, color: '#ff5722' },
        { name: 'Deviation Request', count: 0, color: '#795548' },
        { name: 'Online Orders', count: 0, color: '#333', isBold: true }
      ]);
    } finally {
      setApiLoading('deliveryStatus', false);
    }
  }, [selectedBranchID, selectedAreaID, shouldCallAPI, apiCache, updateApiCache, setApiLoading]);

  // Fetch Happy Hour summary data
  const fetchHappyHourData = useCallback(async (fromDate, toDate) => {
    const fromDateFormatted = fromDate ? fromDate.format('YYYYMMDD') : '';
    const toDateFormatted = toDate ? toDate.format('YYYYMMDD') : '';
    const params = { fromDate: fromDateFormatted, toDate: toDateFormatted, branchId: selectedBranchID, areaId: selectedAreaID, api: 'happyhour-summary' };

    // Check if API should be called
    if (!shouldCallAPI('happyHourData', params)) {
      const cachedData = apiCache['happyHourData'];
      if (cachedData) {
        const { data, summary } = cachedData;
        setHappyHourData(data);
        setHappyHourSummary(summary);
        return;
      }
    }

    setApiLoading('happyHourData', true);
    try {
      const response = await fetchWithAuth(`${local_signage}/api/happyhour?Option=5&AreaId=${selectedAreaID}&BranchId=${formatBranchForAPI(selectedBranchID)}&AdvanceOrderType=ALL&FromDate=${fromDateFormatted}&ToDate=${toDateFormatted}&GroupByOption=Summary`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      const data = await response.json();

      if (data && data.length > 0) {
        setHappyHourData(data);

        // Calculate summary
        const totalQty = data.reduce((sum, item) => sum + (item.SaleQty || 0), 0);
        const totalAmount = data.reduce((sum, item) => sum + (item.TotalAmount || 0), 0);
        const divisions = [...new Set(data.map(item => item.Division).filter(Boolean))];

        const summary = {
          totalQty,
          totalAmount,
          divisions
        };

        setHappyHourSummary(summary);

        // Cache both data and summary
        updateApiCache('happyHourData', params, { data, summary });
      } else {
        setHappyHourData([]);
        setHappyHourSummary({ totalQty: 0, totalAmount: 0, divisions: [] });
      }
    } catch (error) {
      setHappyHourData([]);
      setHappyHourSummary({ totalQty: 0, totalAmount: 0, divisions: [] });
    } finally {
      setApiLoading('happyHourData', false);
    }
  }, [selectedBranchID, selectedAreaID, shouldCallAPI, apiCache, updateApiCache, setApiLoading]);



  // Removed invalid useEffect - functions don't exist




  // Helper function to get readable order status
  const getOrderStatusLabel = (statusCode) => {
    const statusMap = {
      'CN': 'Cancelled',
      'CR': 'Cancellation Request',
      'PL': 'Planned',
      'PN': 'Pending',
      'DL': 'Delivered',
      'OD': 'Out for Delivery',
      'HR': 'Hold Request',
      'DR': 'Diversion Request'
    };
    return statusMap[statusCode] || statusCode;
  };

  // Helper function to format date as DD-MM-YYYY
  const formatDateToDDMMYYYY = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return dateValue;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Excel export functionality using XLSX
  const exportToExcel = (data, filename) => {
    if (!data || data.length === 0) return;

    let exportData;

    // Special handling for Online Orders - convert status codes and add calculated fields
    if (filename === 'Total_Orders_Details' && data[0]?.OrderStatusId) {
      exportData = [];
      data.forEach(row => {
        const { OrderStatusId, ItemDetails, WooCommOrderStatus, CancellationReasonFromPOS, DeliveryDateTime, ...rest } = row;
        const totalAmount = rest.TotalAmount || 0;
        const deliveryCharge = 10;
        const orderAmount = totalAmount - deliveryCharge;

        const items = parseItemDetails(ItemDetails);

        if (items && items.length > 0) {
          items.forEach(item => {
            const units = parseFloat(item.Qty) || 1;
            const weight = parseFloat(item.ItemUnitQty) || 0;
            const finalVolume = units * weight;

            // Calculate Product Amount: use existing value or calculate from Qty × Rate
            const itemRate = parseFloat(item.ItemRate || item.Price) || 0;
            const qty = parseFloat(item.Qty) || 0;
            let productAmount = parseFloat(item.ProductAmount || item.Amount) || 0;
            if (productAmount === 0 && itemRate > 0) {
              productAmount = qty * itemRate;
            }

            exportData.push({
              DelBranchID: rest.DelBranchID,
              SFAreaId: rest.SFAreaId,
              SFAreaName: rest.SFAreaName || '',
              SaleOrderID: rest.SaleOrderID,
              SaleId: rest.SaleId,
              DeliveryScheduleDate: formatDateToDDMMYYYY(rest.DeliveryScheduleDate),
              POSCustomerID: rest.POSCustomerID,
              'Order Amount': orderAmount,
              'Delivery Charge': deliveryCharge,
              'Total Amount': totalAmount,
              TimeSlotName: rest.TimeSlotName,
              OrderStatus: getOrderStatusLabel(OrderStatusId),
              'ItemName': item.ItemName || '',
              'ItemId': item.Partnumber || '',
              'UOM': item.UOMID || '',
              'Qty': item.Qty || '',
              'Unit Qty': item.ItemUnitQty || '',
              'Final Volume': finalVolume,
              'Item Rate': itemRate || '',
              'Product Amount': productAmount || ''
            });
          });
        } else {
          exportData.push({
            DelBranchID: rest.DelBranchID,
            SFAreaId: rest.SFAreaId,
            SFAreaName: rest.SFAreaName || '',
            SaleOrderID: rest.SaleOrderID,
            SaleId: rest.SaleId,
            DeliveryScheduleDate: formatDateToDDMMYYYY(rest.DeliveryScheduleDate),
            POSCustomerID: rest.POSCustomerID,
            'Order Amount': orderAmount,
            'Delivery Charge': deliveryCharge,
            'Total Amount': totalAmount,
            TimeSlotName: rest.TimeSlotName,
            OrderStatus: getOrderStatusLabel(OrderStatusId),
            'ItemName': '',
            'ItemId': '',
            'UOM': '',
            'Qty': '',
            'Unit Qty': '',
            'Final Volume': '',
            'Item Rate': '',
            'Product Amount': ''
          });
        }
      });
    }
    // Special handling for Placed Orders - convert status codes and add SFAreaName
    else if (filename === 'Placed_Orders_Details' && data[0]?.OrderStatusId) {
      exportData = [];
      data.forEach(row => {
        const { OrderStatusId, ItemDetails, WooCommOrderStatus, CancellationReasonFromPOS, DeliveryDateTime, ...rest } = row;
        const totalAmount = rest.TotalAmount || 0;
        const deliveryCharge = 10;
        const orderAmount = totalAmount - deliveryCharge;

        const items = parseItemDetails(ItemDetails);

        if (items && items.length > 0) {
          items.forEach(item => {
            const units = parseFloat(item.Qty) || 1;
            const weight = parseFloat(item.ItemUnitQty) || 0;
            const finalVolume = units * weight;

            // Calculate Product Amount: use existing value or calculate from Qty × Rate
            const itemRate = parseFloat(item.ItemRate || item.Price) || 0;
            const qty = parseFloat(item.Qty) || 0;
            let productAmount = parseFloat(item.ProductAmount || item.Amount) || 0;
            if (productAmount === 0 && itemRate > 0) {
              productAmount = qty * itemRate;
            }

            exportData.push({
              DelBranchID: rest.DelBranchID,
              SFAreaId: rest.SFAreaId,
              SFAreaName: rest.SFAreaName || '',
              SaleOrderID: rest.SaleOrderID,
              SaleId: rest.SaleId,
              DeliveryScheduleDate: formatDateToDDMMYYYY(rest.DeliveryScheduleDate),
              POSCustomerID: rest.POSCustomerID,
              'Order Amount': orderAmount,
              'Delivery Charge': deliveryCharge,
              'Total Amount': totalAmount,
              TimeSlotName: rest.TimeSlotName,
              OrderStatus: getOrderStatusLabel(OrderStatusId),
              'ItemName': item.ItemName || '',
              'ItemId': item.Partnumber || '',
              'UOM': item.UOMID || '',
              'Qty': item.Qty || '',
              'Unit Qty': item.ItemUnitQty || '',
              'Final Volume': finalVolume,
              'Item Rate': itemRate || '',
              'Product Amount': productAmount || ''
            });
          });
        } else {
          // Fallback if no items or parsing fails, still export the order row
          exportData.push({
            DelBranchID: rest.DelBranchID,
            SFAreaId: rest.SFAreaId,
            SFAreaName: rest.SFAreaName || '',
            SaleOrderID: rest.SaleOrderID,
            SaleId: rest.SaleId,
            DeliveryScheduleDate: formatDateToDDMMYYYY(rest.DeliveryScheduleDate),
            POSCustomerID: rest.POSCustomerID,
            'Order Amount': orderAmount,
            'Delivery Charge': deliveryCharge,
            'Total Amount': totalAmount,
            TimeSlotName: rest.TimeSlotName,
            OrderStatus: getOrderStatusLabel(OrderStatusId),
            'ItemName': '',
            'ItemId': '',
            'UOM': '',
            'Qty': '',
            'Unit Qty': '',
            'Final Volume': '',
            'Item Rate': '',
            'Product Amount': ''
          });
        }
      });
    } else {
      // Default export for other tables - format dates
      exportData = data.map(row => {
        const formattedRow = {};
        Object.keys(row).forEach(key => {
          if (key === 'id') return; // Skip id field
          let value = row[key];
          // Format dates to DD-MM-YYYY
          if (value && typeof value === 'string' && (value.includes('T') || value.match(/^\d{4}-\d{2}-\d{2}/))) {
            value = formatDateToDDMMYYYY(value);
          }
          formattedRow[key] = value;
        });
        return formattedRow;
      });
    }

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

    // Generate XLSX file and download
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const handlePincodeClick = (pincode, city) => {
    setSelectedPincode({ pincode, city });
    setSelectedMetric('Pincode Data');
    setShowBottomLoader(true);
    setLoadingProgress(0);
    setShowTable(false);

    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => {
            setShowBottomLoader(false);
            setShowTable(true);

            setTimeout(() => {
              tableRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }, 100);
          }, 300);
          return 100;
        }
        return prev + 2;
      });
    }, 40);
  };

  const handleStatCardClick = (metricTitle, isBreakdown = false) => {
    if (isTableLoading || showBottomLoader) return;

    setIsTableLoading(true);
    setSelectedMetric(metricTitle);
    setSelectedPincode(null);
    setShowBottomLoader(true);
    setLoadingProgress(0);
    setShowTable(false);

    // Call appropriate API based on card clicked
    if (metricTitle === 'Horeca') {
      fetchHorecaTableData(fromDate, toDate);
    } else if (metricTitle === 'Online Orders') {
      fetchTotalOrdersTableData(null, fromDate, toDate);
    } else if (metricTitle === 'Wallet') {
      fetchWalletData(null, fromDate, toDate);
    } else if (metricTitle === 'Happy Hour') {
      fetchHappyHourTableData(fromDate, toDate);
    }

    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => {
            setShowBottomLoader(false);
            setShowTable(true);
            setIsTableLoading(false);
            setTimeout(() => {
              tableRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }, 200);
          }, 300);
          return 100;
        }
        return prev + 3;
      });
    }, 35);
  };

  const handleChartClick = (data) => {
    if (data && data.activeLabel) {
      setSelectedMetric('Monthly Sales');
      setSelectedPincode(null);
      setShowBottomLoader(true);
      setLoadingProgress(0);
      setShowTable(false);

      const progressInterval = setInterval(() => {
        setLoadingProgress((prev) => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            setTimeout(() => {
              setShowBottomLoader(false);
              setShowTable(true);

              setTimeout(() => {
                tableRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }, 100);
            }, 300);
            return 100;
          }
          return prev + 2;
        });
      }, 40);
    }
  };

  const handleDeliveryStatusClick = async (statusName) => {
    // Map status names to OrderStatusId
    const statusMapping = {
      'Delivered': 'DL',
      'Out for Delivery': 'OD',
      'Planned': 'PL',
      'Pending': 'PN',
      'Cancelled': 'CN',
      'Cancellation Request': 'CR',
      'Hold Request': 'HR',
      'Deviation Request': 'DR'
    };

    const statusFilter = statusName === 'Online Orders' || statusName === 'Delayed Orders' ? null : statusMapping[statusName];

    // Set metric name for display
    const metricName = 'Scheduled Deliveries - Details';

    setSelectedMetric(metricName);
    setSelectedPincode(null);
    setShowBottomLoader(true);
    setLoadingProgress(0);
    setShowTable(false);
    setIsTableLoading(true);

    // Fetch the API data with status filter
    await fetchTotalOrdersTableData(statusFilter, fromDate, toDate);

    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => {
            setShowBottomLoader(false);
            setShowTable(true);
            setIsTableLoading(false);

            setTimeout(() => {
              tableRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }, 100);
          }, 300);
          return 100;
        }
        return prev + 2;
      });
    }, 40);
  };

  const handleRoleChange = (role) => {
    setCurrentRole(role);
    setRoleMenuAnchor(null);
    setSelectedMetric(null);
    setSelectedPincode(null);
    setShowTable(false);

    // Handle branch fetching and setup based on role
    if (role === 'area-manager') {
      // Fetch branches for Area Manager
      fetchBranchList();
    } else if (role === 'shop-manager') {
      // For Shop Manager, set L108 as default branch
      setSelectedBranchID('');
      setBranches([]); // Clear branches list
    } else {
      // For Central Manager, set ALL as default branch
      setSelectedBranchID('ALL');
      setBranches([]); // Clear branches list
    }
  };

  // Handle product click in Product Sales Performance
  const handleProductClick = async (product) => {
    if (isTableLoading) {
      return;
    }

    setIsTableLoading(true);
    setSelectedMetric('Division Sales');
    setSelectedPincode(null);
    setShowTable(false);

    try {
      // Fetch detailed data for the selected division
      await fetchDetailedSalesData(product.name);

      // Show table immediately after data is loaded
      setShowTable(true);
      setIsTableLoading(false);

      // Scroll to table
      setTimeout(() => {
        tableRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    } catch (error) {
      setIsTableLoading(false);
    }
  };

  const handleSortClick = (event) => {
    setSortMenuAnchor(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortMenuAnchor(null);
  };

  const handleSortSelect = (sortType) => {
    const currentStats = getRoleBasedStats();
    let newOrder = [...Array(currentStats.length).keys()];

    switch (sortType) {
      case 'highest':
        newOrder.sort((a, b) => currentStats[b].numericValue - currentStats[a].numericValue);
        break;
      case 'lowest':
        newOrder.sort((a, b) => currentStats[a].numericValue - currentStats[b].numericValue);
        break;
      case 'alphabetical':
        newOrder.sort((a, b) => currentStats[a].title.localeCompare(currentStats[b].title));
        break;
      default:
        newOrder = [...Array(currentStats.length).keys()];
    }

    setStatsOrder(newOrder);
    setSortMenuAnchor(null);
  };

  const getSortedStats = () => {
    const currentStats = getRoleBasedStats();
    return statsOrder.slice(0, currentStats.length).map(index => currentStats[index]);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'delivered':
      case 'active':
      case 'good':
      case 'success':
        return 'success';
      case 'processing':
      case 'pending':
      case 'accepted':
      case 'low':
      case 'warning':
        return 'warning';
      case 'shipped':
      case 'out for delivery':
      case 'info':
        return 'info';
      case 'critical':
      case 'inactive':
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  // Add loader state for Division Sales API
  const [productCardLoading, setProductCardLoading] = useState(false);

  // Pagination for Division Sales Table
  const [productTablePage, setProductTablePage] = useState(0);
  const productTableRowsPerPage = 200;

  // Calculate total quantity and total sale for Division Sales
  const getProductSalesTotals = () => {
    if (!salesSummaryData || salesSummaryData.length === 0) return { totalQty: 0, totalSale: 0 };
    let totalQty = 0;
    let totalSale = 0;
    salesSummaryData.forEach(item => {
      totalQty += item.count || 0;
      totalSale += item.totalAmount || 0;
    });
    return { totalQty, totalSale };
  };

  // Loader for Division Sales API
  useEffect(() => {
    setProductCardLoading(true);
    const timer = setTimeout(() => setProductCardLoading(false), 800);
    return () => clearTimeout(timer);
  }, [salesSummaryData]);

  // Memoized optimizations moved outside renderTable function
  const optimizedColumns = useMemo(() => {
    if (!selectedMetric) return [];

    // Predefined column sets for known data types
    const columnSets = {
      'Division Sales': ['BranchId', 'BusinessDate', 'ItemID', 'ItemName', 'Division', 'ChannelID', 'POSCustomerID', 'SaleQty', 'UOMID', 'TotalAmount', 'PricingSchemeID'],
      'Division Sales - Total Quantity': ['division', 'count', 'totalAmount'],
      'Scheduled Deliveries - Details': ['DelBranchID', 'SFAreaId', 'SaleOrderID', 'SaleId', 'DeliveryScheduleDate', 'POSCustomerID', 'TotalAmount', 'TimeSlotName', 'OrderStatusId', 'WooCommOrderStatus'],
      'Wallet - Details': ['SaleOrderID', 'BusinessDay', 'POSCustomerID', 'TotalAmount', 'PaymentMode'],
      'Happy Hour - Details': ['BusinessDay', 'WeekDay', 'ItemID', 'Division', 'ChannelID', 'POSCustomerID', 'SaleQty', 'UOMID', 'TotalAmount', 'PricingSchemeID']
    };

    // Use predefined columns if available
    if (columnSets[selectedMetric]) {
      const relevantData = selectedMetric === 'Division Sales' ? detailedSalesData :
        selectedMetric === 'Division Sales - Total Quantity' ? salesSummaryData : [];

      if (relevantData.length > 0) {
        return columnSets[selectedMetric].filter(col => relevantData[0].hasOwnProperty(col));
      }
      return columnSets[selectedMetric];
    }

    return [];
  }, [selectedMetric, detailedSalesData, salesSummaryData]);

  const isCurrentTableLoading = useMemo(() => {
    switch (selectedMetric) {
      case 'Scheduled Deliveries - Details': return isTotalOrdersTableLoading;
      case 'Wallet - Details': return isWalletTableLoading;
      case 'Happy Hour - Details': return isHappyHourTableLoading;
      case 'Horeca - Details': return isHorecaTableLoading;
      case 'Division Sales':
      case 'Division Sales - Total Quantity': return isTableLoading;
      default: return false;
    }
  }, [selectedMetric, isTotalOrdersTableLoading, isWalletTableLoading, isHappyHourTableLoading,
    isHorecaTableLoading, isTableLoading]);
  const filteredTotalOrdersTableData = React.useMemo(() => {
    if (!onlineOrdersSearchQuery) return totalOrdersTableData;
    const lowercasedQuery = onlineOrdersSearchQuery.toLowerCase();
    return totalOrdersTableData.filter((row) => {
      const saleOrderId = row.SaleOrderID ? String(row.SaleOrderID).toLowerCase() : '';
      const billNumber = row.SaleId ? String(row.SaleId).toLowerCase() : '';
      const customerId = row.POSCustomerID ? String(row.POSCustomerID).toLowerCase() : '';
      return saleOrderId.includes(lowercasedQuery) ||
        billNumber.includes(lowercasedQuery) ||
        customerId.includes(lowercasedQuery);
    });
  }, [totalOrdersTableData, onlineOrdersSearchQuery]);

  const renderTable = () => {
    if (!selectedMetric) return null;

    // Division Sales table
    if (selectedMetric === 'Division Sales') {
      return (
        <Slide direction="up" in={showTable} timeout={800}>
          <Card ref={tableRef} sx={{ mt: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Division Sales - Detailed View
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownload />}
                  onClick={() => exportToExcel(detailedSalesData, 'Division_Sales_Details')}
                  disabled={!detailedSalesData || detailedSalesData.length === 0}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Export
                </Button>
              </Box>
              <TableContainer sx={{
                maxHeight: 420,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid #f5f5f5',
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: '#f9f9f9',
                }
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Branch ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Bill Number</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Business Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Item ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Item Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Division</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Channel</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Customer ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }} align="right">Sale Qty</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>UOM</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }} align="right">Total Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Pricing Scheme</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isTableLoading ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : detailedSalesData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center">
                          <Typography variant="caption" color="text.secondary">
                            No data available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      detailedSalesData.slice(detailedSalesTablePage * detailedSalesTableRowsPerPage, (detailedSalesTablePage + 1) * detailedSalesTableRowsPerPage).map((row, idx) => (
                        <TableRow key={`${row.BranchId}-${row.ItemID}-${row.BusinessDate}-${idx}`}>
                          <TableCell>{row.BranchId}</TableCell>
                          <TableCell sx={{ fontWeight: '500' }}>{row.SaleId}</TableCell>
                          <TableCell>{row.BusinessDate ? new Date(row.BusinessDate).toLocaleDateString() : ''}</TableCell>
                          <TableCell>{row.ItemID}</TableCell>
                          <TableCell sx={{ fontWeight: '500' }}>{row.ItemName}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.Division}
                              size="small"
                              sx={{
                                backgroundColor: row.Division === 'RTC' ? '#e3f2fd' :
                                  row.Division === 'POULTRY' ? '#e8f5e8' :
                                    row.Division === 'EGG' ? '#fff3e0' : '#f3e5f5',
                                color: row.Division === 'RTC' ? '#1976d2' :
                                  row.Division === 'POULTRY' ? '#388e3c' :
                                    row.Division === 'EGG' ? '#f57c00' : '#7b1fa2',
                                fontSize: '0.75rem',
                                height: '24px'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.ChannelID}
                              size="small"
                              sx={{
                                backgroundColor: row.ChannelID === 'POS' ? '#e8f5e8' :
                                  row.ChannelID === 'WEB' ? '#fff3e0' : '#f3e5f5',
                                color: row.ChannelID === 'POS' ? '#388e3c' :
                                  row.ChannelID === 'WEB' ? '#f57c00' : '#7b1fa2',
                                fontSize: '0.75rem',
                                height: '24px'
                              }}
                            />
                          </TableCell>
                          <TableCell>{row.POSCustomerID}</TableCell>
                          <TableCell align="right">
                            <span style={{ color: '#2e7d32', fontWeight: '600' }}>
                              {row.SaleQty}
                            </span>
                          </TableCell>
                          <TableCell>{row.UOMID || 'Units'}</TableCell>
                          <TableCell align="right">
                            <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                              ₹{row.TotalAmount?.toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>{row.PricingSchemeID}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1}>
                <Typography variant="body2" color="text.secondary">
                  Total Records: {detailedSalesData.length}
                </Typography>
                <TablePagination
                  rowsPerPageOptions={[50, 100, 200]}
                  component="div"
                  count={detailedSalesData.length}
                  rowsPerPage={detailedSalesTableRowsPerPage}
                  page={detailedSalesTablePage}
                  onPageChange={(event, newPage) => setDetailedSalesTablePage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setDetailedSalesTableRowsPerPage(parseInt(event.target.value, 10));
                    setDetailedSalesTablePage(0);
                  }}
                  sx={{ ml: 'auto' }}
                />
              </Box>
            </CardContent>
          </Card>
        </Slide>
      );
    }

    // Placed Orders table
    if (selectedMetric === 'Placed Orders - Details') {
      return (
        <Slide direction="up" in={showTable} timeout={800}>
          <Card ref={tableRef} sx={{ mt: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Placed Orders - Detailed View
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <TextField
                    size="small"
                    placeholder="Search by Order ID, Customer ID, Bill Number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ width: 350 }}
                    InputProps={{
                      startAdornment: (
                        <Box component="span" sx={{ mr: 1, display: 'flex' }}>🔍</Box>
                      ),
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownload />}
                    onClick={() => exportToExcel(filterTableData(placedOrdersTableData, searchQuery), 'Placed_Orders_Details')}
                    disabled={!placedOrdersTableData || placedOrdersTableData.length === 0}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    Export
                  </Button>
                </Box>
              </Box>
              <TableContainer sx={{
                maxHeight: 420,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid #f5f5f5',
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: '#f9f9f9',
                }
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa', width: '50px' }}>Details</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Branch ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Area ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Area Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Sale Order ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Sale ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Delivery Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Customer ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Delivery Charge</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Total Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Time Slot</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Order Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isPlacedOrdersTableLoading ? (
                      <TableRow>
                        <TableCell colSpan={13} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : placedOrdersTableData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13} align="center">
                          <Typography variant="caption" color="text.secondary">
                            No data available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filterTableData(placedOrdersTableData, searchQuery).slice(placedOrdersTablePage * placedOrdersTableRowsPerPage, (placedOrdersTablePage + 1) * placedOrdersTableRowsPerPage).map((row, idx) => {
                        const rowKey = `${row.SaleOrderID}-${row.SaleId}-${idx}`;
                        const isExpanded = expandedPlacedOrderRows[rowKey];
                        const itemDetails = parseItemDetails(row.ItemDetails);

                        // Debug log for first row item details
                        if (idx === 0 && placedOrdersTablePage === 0) {
                          console.log('first row item details raw:', row.ItemDetails);
                          console.log('parsed:', itemDetails);
                        }

                        return (
                          <React.Fragment key={rowKey}>
                            <TableRow
                              sx={{
                                ...(row.OrderStatusId === 'CN' && {
                                  border: '2px solid #f44336',
                                  backgroundColor: 'rgba(244, 67, 54, 0.05)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                  }
                                })
                              }}
                            >
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setExpandedPlacedOrderRows(prev => ({
                                      ...prev,
                                      [rowKey]: !prev[rowKey]
                                    }));
                                  }}
                                  sx={{ padding: '4px' }}
                                >
                                  {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </TableCell>
                              <TableCell>{row.DelBranchID}</TableCell>
                              <TableCell>{row.SFAreaId}</TableCell>
                              <TableCell>{row.SFAreaName || ''}</TableCell>
                              <TableCell sx={{ fontWeight: '500' }}>{row.SaleOrderID}</TableCell>
                              <TableCell>{row.SaleId}</TableCell>
                              <TableCell>{row.DeliveryScheduleDate ? new Date(row.DeliveryScheduleDate).toLocaleDateString() : ''}</TableCell>
                              <TableCell>{row.POSCustomerID}</TableCell>
                              <TableCell>
                                <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                                  ₹{(row.TotalAmount || 0).toLocaleString()}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span style={{ color: '#1976d2', fontWeight: 600 }}>
                                  ₹10
                                </span>
                              </TableCell>
                              <TableCell>
                                <span style={{ color: '#388e3c', fontWeight: 600 }}>
                                  ₹{((row.TotalAmount || 0) + 10).toLocaleString()}
                                </span>
                              </TableCell>
                              <TableCell>{row.TimeSlotName}</TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    row.OrderStatusId === 'CN' ? 'Cancelled' :
                                      row.OrderStatusId === 'CR' ? 'Cancellation Request' :
                                        row.OrderStatusId === 'PL' ? 'Planned' :
                                          row.OrderStatusId === 'PN' ? 'Pending' :
                                            row.OrderStatusId === 'DL' ? 'Delivered' :
                                              row.OrderStatusId === 'OD' ? 'Out for Delivery' :
                                                row.OrderStatusId === 'HR' ? 'Hold Request' :
                                                  row.OrderStatusId === 'DR' ? 'Diversion Request' :
                                                    row.OrderStatusId
                                  }
                                  size="small"
                                  sx={{
                                    backgroundColor: row.OrderStatusId === 'CN' ? '#ffebee' :
                                      row.OrderStatusId === 'CR' ? '#fbe9e7' :
                                        row.OrderStatusId === 'PL' ? '#e3f2fd' :
                                          row.OrderStatusId === 'PN' ? '#fff3e0' :
                                            row.OrderStatusId === 'DL' ? '#e8f5e9' :
                                              row.OrderStatusId === 'OD' ? '#e0f7fa' :
                                                row.OrderStatusId === 'HR' ? '#f3e5f5' :
                                                  row.OrderStatusId === 'DR' ? '#efebe9' : '#f3e5f5',
                                    color: row.OrderStatusId === 'CN' ? '#d32f2f' :
                                      row.OrderStatusId === 'CR' ? '#ff5722' :
                                        row.OrderStatusId === 'PL' ? '#1976d2' :
                                          row.OrderStatusId === 'PN' ? '#f57c00' :
                                            row.OrderStatusId === 'DL' ? '#388e3c' :
                                              row.OrderStatusId === 'OD' ? '#00838f' :
                                                row.OrderStatusId === 'HR' ? '#7b1fa2' :
                                                  row.OrderStatusId === 'DR' ? '#5d4037' : '#7b1fa2',
                                    fontSize: '0.75rem',
                                    height: '24px',
                                    fontWeight: '600'
                                  }}
                                />
                              </TableCell>
                            </TableRow>

                            {/* Expanded Row for Item Details */}
                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={12} sx={{ paddingBottom: 0, paddingTop: 0, backgroundColor: '#f5f5f5' }}>
                                  <Box sx={{ margin: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                                      Item Details:
                                    </Typography>
                                    {itemDetails.length > 0 ? (
                                      <Table size="small" sx={{ backgroundColor: 'white', boxShadow: 1 }}>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Item Name</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Part Number</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>UOM</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">Qty</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">Unit Qty</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">Rate</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">Amount</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {itemDetails.map((item, itemIdx) => (
                                            <TableRow key={itemIdx}>
                                              <TableCell>{item.ItemName || '-'}</TableCell>
                                              <TableCell>{item.Partnumber || '-'}</TableCell>
                                              <TableCell>{item.UOMID || '-'}</TableCell>
                                              <TableCell align="right">{item.Qty || '-'}</TableCell>
                                              <TableCell align="right">{item.ItemUnitQty || '-'}</TableCell>
                                              <TableCell align="right">
                                                <span style={{ color: '#2e7d32', fontWeight: 500 }}>
                                                  ₹{(item.ItemRate || item.Price) ? parseFloat(item.ItemRate || item.Price).toFixed(2) : '0.00'}
                                                </span>
                                              </TableCell>
                                              <TableCell align="right">
                                                <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                                                  ₹{(item.ProductAmount || item.Amount) ? parseFloat(item.ProductAmount || item.Amount).toFixed(2) : '0.00'}
                                                </span>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        No item details available.
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1}>
                <Typography variant="body2" color="text.secondary">
                  Total Records: {placedOrdersTableData.length}
                </Typography>
                <TablePagination
                  rowsPerPageOptions={[50, 100, 200]}
                  component="div"
                  count={placedOrdersTableData.length}
                  rowsPerPage={placedOrdersTableRowsPerPage}
                  page={placedOrdersTablePage}
                  onPageChange={(event, newPage) => setPlacedOrdersTablePage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setPlacedOrdersTableRowsPerPage(parseInt(event.target.value, 10));
                    setPlacedOrdersTablePage(0);
                  }}
                  sx={{ ml: 'auto' }}
                />
              </Box>
            </CardContent>
          </Card>
        </Slide>
      );
    }

    // Online Orders table
    if (selectedMetric === 'Scheduled Deliveries - Details') {
      return (
        <Slide direction="up" in={showTable} timeout={800}>
          <Card ref={tableRef} sx={{ mt: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
                <Typography variant="h6">
                  Online Orders - Detailed View
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search Order ID, Bill #, Customer ID..."
                  value={onlineOrdersSearchQuery}
                  onChange={(e) => setOnlineOrdersSearchQuery(e.target.value)}
                  sx={{ minWidth: { xs: '100%', sm: 300 }, ml: 'auto', mr: 2 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownload />}
                  onClick={() => exportToExcel(filteredTotalOrdersTableData, 'Total_Orders_Details')}
                  disabled={!filteredTotalOrdersTableData || filteredTotalOrdersTableData.length === 0}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Export
                </Button>
              </Box>
              <TableContainer sx={{
                maxHeight: 420,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid #f5f5f5',
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: '#f9f9f9',
                }
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa', width: '50px' }}>Details</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Branch ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Area ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Area Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Sale Order ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Bill Number</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Delivery Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Customer ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Delivery Charge</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Total Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Time Slot</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Order Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isTotalOrdersTableLoading ? (
                      <TableRow>
                        <TableCell colSpan={13} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : filteredTotalOrdersTableData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13} align="center">
                          <Typography variant="caption" color="text.secondary">
                            No data available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTotalOrdersTableData.slice(totalOrdersTablePage * totalOrdersTableRowsPerPage, (totalOrdersTablePage + 1) * totalOrdersTableRowsPerPage).map((row, idx) => {
                        const rowKey = `${row.SaleOrderID}-${row.SaleId}-${idx}`;
                        const isExpanded = expandedOrderRows[rowKey];
                        const itemDetails = parseItemDetails(row.ItemDetails);

                        return (
                          <React.Fragment key={rowKey}>
                            <TableRow
                              sx={{
                                ...(row.OrderStatusId === 'CN' && {
                                  border: '2px solid #f44336',
                                  backgroundColor: 'rgba(244, 67, 54, 0.05)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                  }
                                })
                              }}
                            >
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleOrderRow(rowKey)}
                                  sx={{ padding: '4px' }}
                                >
                                  {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </TableCell>
                              <TableCell>{row.DelBranchID}</TableCell>
                              <TableCell>{row.SFAreaId}</TableCell>
                              <TableCell>{row.SFAreaName || ''}</TableCell>
                              <TableCell sx={{ fontWeight: '500' }}>{row.SaleOrderID}</TableCell>
                              <TableCell>{row.SaleId}</TableCell>
                              <TableCell>{row.DeliveryScheduleDate ? new Date(row.DeliveryScheduleDate).toLocaleDateString() : ''}</TableCell>
                              <TableCell>{row.POSCustomerID}</TableCell>
                              <TableCell>
                                <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                                  ₹{(row.TotalAmount || 0).toLocaleString()}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span style={{ color: '#1976d2', fontWeight: 600 }}>
                                  ₹10
                                </span>
                              </TableCell>
                              <TableCell>
                                <span style={{ color: '#388e3c', fontWeight: 600 }}>
                                  ₹{((row.TotalAmount || 0) + 10).toLocaleString()}
                                </span>
                              </TableCell>
                              <TableCell>{row.TimeSlotName}</TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    row.OrderStatusId === 'CN' ? 'Cancelled' :
                                      row.OrderStatusId === 'CR' ? 'Cancellation Request' :
                                        row.OrderStatusId === 'PL' ? 'Planned' :
                                          row.OrderStatusId === 'PN' ? 'Pending' :
                                            row.OrderStatusId === 'DL' ? 'Delivered' :
                                              row.OrderStatusId === 'OD' ? 'Out for Delivery' :
                                                row.OrderStatusId === 'HR' ? 'Hold Request' :
                                                  row.OrderStatusId === 'DR' ? 'Diversion Request' :
                                                    row.OrderStatusId
                                  }
                                  size="small"
                                  sx={{
                                    backgroundColor: row.OrderStatusId === 'CN' ? '#ffebee' :
                                      row.OrderStatusId === 'CR' ? '#fbe9e7' :
                                        row.OrderStatusId === 'PL' ? '#e3f2fd' :
                                          row.OrderStatusId === 'PN' ? '#fff3e0' :
                                            row.OrderStatusId === 'DL' ? '#e8f5e9' :
                                              row.OrderStatusId === 'OD' ? '#e0f7fa' :
                                                row.OrderStatusId === 'HR' ? '#f3e5f5' :
                                                  row.OrderStatusId === 'DR' ? '#efebe9' : '#f3e5f5',
                                    color: row.OrderStatusId === 'CN' ? '#d32f2f' :
                                      row.OrderStatusId === 'CR' ? '#ff5722' :
                                        row.OrderStatusId === 'PL' ? '#1976d2' :
                                          row.OrderStatusId === 'PN' ? '#f57c00' :
                                            row.OrderStatusId === 'DL' ? '#388e3c' :
                                              row.OrderStatusId === 'OD' ? '#00838f' :
                                                row.OrderStatusId === 'HR' ? '#7b1fa2' :
                                                  row.OrderStatusId === 'DR' ? '#5d4037' : '#7b1fa2',
                                    fontSize: '0.75rem',
                                    height: '24px',
                                    fontWeight: '600'
                                  }}
                                />
                              </TableCell>
                            </TableRow>

                            {/* Expanded Row for Item Details */}
                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={12} sx={{ paddingBottom: 0, paddingTop: 0, backgroundColor: '#f5f5f5' }}>
                                  <Box sx={{ margin: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                                      Item Details:
                                    </Typography>
                                    {itemDetails.length > 0 ? (
                                      <Table size="small" sx={{ backgroundColor: 'white', boxShadow: 1 }}>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Item Name</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Part Number</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>UOM</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">Qty</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">Unit Qty</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">Rate</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }} align="right">Amount</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {itemDetails.map((item, itemIdx) => (
                                            <TableRow key={itemIdx}>
                                              <TableCell>{item.ItemName || '-'}</TableCell>
                                              <TableCell>{item.Partnumber || '-'}</TableCell>
                                              <TableCell>{item.UOMID || '-'}</TableCell>
                                              <TableCell align="right">{item.Qty || '-'}</TableCell>
                                              <TableCell align="right">{item.ItemUnitQty || '-'}</TableCell>
                                              <TableCell align="right">
                                                <span style={{ color: '#2e7d32', fontWeight: 500 }}>
                                                  ₹{(item.ItemRate || item.Price) ? parseFloat(item.ItemRate || item.Price).toFixed(2) : '0.00'}
                                                </span>
                                              </TableCell>
                                              <TableCell align="right">
                                                <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                                                  ₹{(item.ProductAmount || item.Amount) ? parseFloat(item.ProductAmount || item.Amount).toFixed(2) : '0.00'}
                                                </span>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        No item details available.
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1}>
                <Typography variant="body2" color="text.secondary">
                  Total Records: {filteredTotalOrdersTableData.length}
                </Typography>
                <TablePagination
                  rowsPerPageOptions={[50, 100, 200]}
                  component="div"
                  count={filteredTotalOrdersTableData.length}
                  rowsPerPage={totalOrdersTableRowsPerPage}
                  page={totalOrdersTablePage}
                  onPageChange={(event, newPage) => setTotalOrdersTablePage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setTotalOrdersTableRowsPerPage(parseInt(event.target.value, 10));
                    setTotalOrdersTablePage(0);
                  }}
                  sx={{ ml: 'auto' }}
                />
              </Box>
            </CardContent>
          </Card>
        </Slide>
      );
    }

    // Horeca Sale table
    if (selectedMetric === 'Horeca - Details') {
      // Calculate paginated data for Horeca table
      const startIndex = (horecaTablePage - 1) * horecaTableRowsPerPage;
      const endIndex = startIndex + horecaTableRowsPerPage;
      const horecaPaginatedData = horecaTableData.slice(startIndex, endIndex);

      return (
        <Slide direction="up" in={showTable} timeout={800}>
          <Card ref={tableRef} sx={{ mt: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Horeca - Detailed Items {horecaTableData.length > horecaTableRowsPerPage ? `(Page ${horecaTablePage})` : ''}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownload />}
                  onClick={() => exportToExcel(horecaTableData, 'Horeca_Details')}
                  disabled={!horecaTableData || horecaTableData.length === 0}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Export
                </Button>
              </Box>
              <TableContainer sx={{
                maxHeight: 420,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid #f5f5f5',
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: '#f9f9f9',
                }
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Branch ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Bill Number</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Business Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Item Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Division</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Channel ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Item ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }} align="right">Sale Qty</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>UOM</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }} align="right">Total Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Pricing Scheme</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {horecaPaginatedData.length > 0 ? (
                      horecaPaginatedData.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.BranchId}</TableCell>
                          <TableCell sx={{ fontWeight: '500' }}>{row.SaleId}</TableCell>
                          <TableCell>{row.BusinessDate ? new Date(row.BusinessDate).toLocaleDateString() : ''}</TableCell>
                          <TableCell>{row.ItemName}</TableCell>
                          <TableCell>{row.Division}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.ChannelID}
                              size="small"
                              color={row.ChannelID === 'POS' ? 'primary' : row.ChannelID === 'APP' ? 'secondary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{row.ItemID}</TableCell>
                          <TableCell align="right">
                            <span style={{ color: '#388e3c', fontWeight: 600 }}>
                              {row.SaleQty?.toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>{row.UOMID}</TableCell>
                          <TableCell align="right">
                            <span style={{ color: '#d32f2f', fontWeight: 600 }}>
                              ₹{row.TotalAmount?.toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>{row.PricingSchemeID}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={11} align="center">
                          <Typography variant="caption" color="text.secondary">
                            No Horeca data available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Horeca Pagination Controls */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} px={2}>
                <Typography variant="body2" color="text.secondary">
                  Total Records: {horecaTableData.length}
                  {horecaTableData.length > horecaTableRowsPerPage && (
                    <> | Showing {Math.min((horecaTablePage - 1) * horecaTableRowsPerPage + 1, horecaTableData.length)}-{Math.min(horecaTablePage * horecaTableRowsPerPage, horecaTableData.length)}</>
                  )}
                </Typography>

                {horecaTableData.length > horecaTableRowsPerPage && (

                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      size="small"
                      disabled={horecaTablePage === 1}
                      onClick={() => setHorecaTablePage(horecaTablePage - 1)}
                      variant="outlined"
                    >
                      Previous
                    </Button>

                    <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
                      Page {horecaTablePage} of {Math.ceil(horecaTableData.length / horecaTableRowsPerPage)}
                    </Typography>

                    <Button
                      size="small"
                      disabled={horecaTablePage >= Math.ceil(horecaTableData.length / horecaTableRowsPerPage)}
                      onClick={() => setHorecaTablePage(horecaTablePage + 1)}
                      variant="outlined"
                    >
                      Next
                    </Button>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Slide>
      );
    }
    if (selectedMetric === 'Wallet - Details') {
      return (
        <Slide direction="up" in={showTable} timeout={800}>
          <Card ref={tableRef} sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Wallet - Detailed View
              </Typography>
              <TableContainer sx={{
                maxHeight: 420,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid #f5f5f5',
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: '#f9f9f9',
                }
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Sale Order ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Customer Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Order Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Total Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Receiving Branch</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Mobile</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Channel</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Created Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isWalletTableLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : walletTableData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="caption" color="text.secondary">
                            No data available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      walletTableData.slice(walletTablePage * walletTableRowsPerPage, (walletTablePage + 1) * walletTableRowsPerPage).map((row, idx) => (
                        <TableRow key={`${row.SaleOrderID}-${row.CreatedDate}-${idx}`}>
                          <TableCell>
                            <span style={{
                              fontFamily: 'monospace',
                              backgroundColor: '#f5f5f5',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontSize: '0.8rem'
                            }}>
                              {row.SaleOrderID}
                            </span>
                          </TableCell>
                          <TableCell sx={{ fontWeight: '500' }}>{row.CustomerName}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.OrderType === 'PREPD' ? 'Prepaid' : row.OrderType === 'SBSCR' ? 'Subscription' : row.OrderType}
                              color={row.OrderType === 'PREPD' ? 'primary' : row.OrderType === 'SBSCR' ? 'success' : 'default'}
                              size="small"
                              sx={{ fontSize: '0.75rem', height: '24px' }}
                            />
                          </TableCell>
                          <TableCell>
                            <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                              ₹{row.TotalAmount?.toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>{row.ReceivingBarnch}</TableCell>
                          <TableCell>{row.Mobile}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.ChannelId}
                              size="small"
                              color={row.ChannelId === 'POS' ? 'primary' : 'secondary'}
                              sx={{ fontSize: '0.75rem', height: '20px' }}
                            />
                          </TableCell>
                          <TableCell>{row.CreatedDate ? new Date(row.CreatedDate).toLocaleDateString() : ''}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={walletTableData.length}
                page={walletTablePage}
                onPageChange={(event, newPage) => setWalletTablePage(newPage)}
                rowsPerPage={walletTableRowsPerPage}
                onRowsPerPageChange={() => { }}
                rowsPerPageOptions={[200]}
                labelRowsPerPage="Rows per page:"
              />
            </CardContent>
          </Card>
        </Slide>
      );
    }

    // Happy Hour table
    if (selectedMetric === 'Happy Hour - Details') {
      return (
        <Slide direction="up" in={showTable} timeout={800}>
          <Card ref={tableRef} sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Happy Hour - Detailed View
              </Typography>
              <TableContainer sx={{
                maxHeight: 420,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid #f5f5f5',
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: '#f9f9f9',
                }
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Bill No</strong></TableCell>
                      <TableCell><strong>Business Day</strong></TableCell>
                      <TableCell><strong>Week Day</strong></TableCell>
                      <TableCell><strong>Item ID</strong></TableCell>
                      <TableCell><strong>Division</strong></TableCell>
                      <TableCell><strong>Channel ID</strong></TableCell>
                      <TableCell><strong>Customer ID</strong></TableCell>
                      <TableCell align="right"><strong>Sale Qty</strong></TableCell>
                      <TableCell><strong>UOM</strong></TableCell>
                      <TableCell align="right"><strong>Total Amount</strong></TableCell>
                      <TableCell><strong>Pricing Scheme</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isHappyHourTableLoading ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ mt: 1 }}>Loading Happy Hour data...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : happyHourDetailedData.length > 0 ? (
                      happyHourDetailedData.slice(happyHourTablePage * happyHourTableRowsPerPage, (happyHourTablePage + 1) * happyHourTableRowsPerPage).map((item, index) => (
                        <TableRow key={`${item.ItemID}-${item.BusinessDay}-${index}`}>
                          <TableCell>{item.SaleId}</TableCell>
                          <TableCell>{new Date(item.BusinessDay).toLocaleDateString()}</TableCell>
                          <TableCell>{item.WeekDay}</TableCell>
                          <TableCell>
                            <span style={{
                              fontFamily: 'monospace',
                              backgroundColor: '#f5f5f5',
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontSize: '0.8rem'
                            }}>
                              {item.ItemID}
                            </span>
                          </TableCell>
                          <TableCell>{item.Division}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.ChannelID}
                              size="small"
                              color={item.ChannelID === 'POS' ? 'primary' : 'secondary'}
                              sx={{ fontSize: '0.75rem', height: '24px' }}
                            />
                          </TableCell>
                          <TableCell>{item.POSCustomerID}</TableCell>
                          <TableCell align="right">
                            <span style={{ color: '#2e7d32', fontWeight: '600' }}>
                              {item.SaleQty}
                            </span>
                          </TableCell>
                          <TableCell>{item.UOMID}</TableCell>
                          <TableCell align="right">
                            <span style={{ color: '#2e7d32', fontWeight: '600' }}>
                              ₹{item.TotalAmount?.toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell>{item.PricingSchemeID}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">No Happy Hour data available</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[50, 100, 200]}
                component="div"
                count={happyHourDetailedData.length}
                rowsPerPage={happyHourTableRowsPerPage}
                page={happyHourTablePage}
                onPageChange={(event, newPage) => setHappyHourTablePage(newPage)}
                onRowsPerPageChange={(event) => {
                  setHappyHourTableRowsPerPage(parseInt(event.target.value, 10));
                  setHappyHourTablePage(0);
                }}
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => {
                    if (happyHourDetailedData.length > 0) {
                      exportToExcel(happyHourDetailedData, 'Happy_Hour_Details_export');
                    }
                  }}
                  disabled={happyHourDetailedData.length === 0}
                >
                  Export to Excel
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Slide>
      );
    }

    let data = [];
    let fullData = []; // For export - contains all data without pagination
    let title = selectedMetric;

    // Handle API data based on selected metric
    if (selectedMetric === 'Division Sales') {
      fullData = detailedSalesData.map((item, index) => ({
        id: index + 1,
        ...item, // Keep all original properties from API
        BusinessDate: new Date(item.BusinessDate).toLocaleDateString()
      }));
      data = fullData; // No pagination for Division Sales in this section
      title = selectedDivision ? `${selectedDivision} Division - Sales Details` : 'Division Sales - All Divisions';
    } else if (selectedMetric === 'Division Sales - Total Quantity') {
      // Show aggregated quantity data by division
      fullData = salesSummaryData.map((item, index) => ({
        id: index + 1,
        division: item.name,
        totalQty: item.count,
        totalAmount: item.totalAmount,
        percentage: item.performance,
        avgPerUnit: item.totalAmount && item.count ? (item.totalAmount / item.count).toFixed(2) : 0
      }));
      data = fullData; // No pagination for this view
      title = 'Division Sales - Total Quantity Breakdown';
    } else if (selectedMetric.startsWith('Retail - ')) {
      // Calculate pagination
      const startIndex = (retailTablePage - 1) * retailTableRowsPerPage;
      const endIndex = startIndex + retailTableRowsPerPage;

      // Full data for export (no pagination)
      fullData = retailDetailedData.map((item, index) => ({
        id: index + 1,
        billNumber: item.SaleId,
        orderId: item.SaleOrderID,
        businessDate: new Date(item.BusinessDate).toLocaleDateString(),
        itemName: item.ItemName,
        division: item.Division,
        channelId: item.ChannelID,
        customerID: item.POSCustomerID,
        saleQty: item.SaleQty,
        uom: item.UOMID,
        totalAmount: item.TotalAmount,
        pricingScheme: item.PricingSchemeID,
        itemId: item.ItemID,
        branchId: item.BranchId
      }));

      // Paginated data for display only
      data = retailDetailedData
        .slice(startIndex, endIndex)
        .map((item, index) => ({
          id: startIndex + index + 1,
          billNumber: item.SaleId,
          orderId: item.SaleOrderID,
          businessDate: new Date(item.BusinessDate).toLocaleDateString(),
          itemName: item.ItemName,
          division: item.Division,
          channelId: item.ChannelID,
          customerID: item.POSCustomerID,
          saleQty: item.SaleQty,
          uom: item.UOMID,
          totalAmount: item.TotalAmount,
          pricingScheme: item.PricingSchemeID,
          itemId: item.ItemID,
          branchId: item.BranchId
        }));

      // Update title based on filter
      const isFiltered = selectedMetric !== 'Retail - Details';
      const filterType = isFiltered ? selectedMetric.replace('Retail - ', '').replace(' Details', '') : '';
      title = isFiltered ? `Retail Sales - ${filterType} Channel (Page ${retailTablePage})` : `Retail Sales - All Channels (Page ${retailTablePage})`;
    } else if (selectedMetric === 'Delivery Status - All Orders') {
      // Show all delivery orders
      fullData = [
        { id: 1, orderId: 'ORD001', status: 'Accepted', customer: 'John Doe', amount: '₹1,200', date: '2024-01-15' },
        { id: 2, orderId: 'ORD002', status: 'Delivered', customer: 'Jane Smith', amount: '₹850', date: '2024-01-15' },
        { id: 3, orderId: 'ORD003', status: 'Out for Delivery', customer: 'Bob Johnson', amount: '₹2,100', date: '2024-01-14' },
        { id: 4, orderId: 'ORD004', status: 'Pending', customer: 'Alice Brown', amount: '₹650', date: '2024-01-14' },
        { id: 5, orderId: 'ORD005', status: 'Accepted', customer: 'Mike Wilson', amount: '₹1,800', date: '2024-01-13' }
      ];
      data = fullData;
      title = 'All Delivery Orders';
    } else if (selectedMetric === 'Delivery Status - Delayed Orders') {
      // Show only delayed orders
      fullData = [
        { id: 1, orderId: 'ORD006', status: 'Delayed', customer: 'Sarah Davis', amount: '₹950', date: '2024-01-12', delay: '2 days' },
        { id: 2, orderId: 'ORD007', status: 'Delayed', customer: 'Tom Anderson', amount: '₹1,350', date: '2024-01-11', delay: '3 days' },
        { id: 3, orderId: 'ORD008', status: 'Delayed', customer: 'Lisa White', amount: '₹750', date: '2024-01-10', delay: '4 days' }
      ];
      data = fullData;
      title = 'Delayed Delivery Orders';
    } else if (selectedMetric.startsWith('Delivery Status - ')) {
      // Show orders for specific status
      const status = selectedMetric.replace('Delivery Status - ', '');
      const statusOrders = {
        'Accepted': [
          { id: 1, orderId: 'ORD009', customer: 'David Lee', amount: '₹1,100', date: '2024-01-15' },
          { id: 2, orderId: 'ORD010', customer: 'Emma Wilson', amount: '₹900', date: '2024-01-15' }
        ],
        'Delivered': [
          { id: 1, orderId: 'ORD011', customer: 'Chris Taylor', amount: '₹1,500', date: '2024-01-14' },
          { id: 2, orderId: 'ORD012', customer: 'Anna Garcia', amount: '₹800', date: '2024-01-14' }
        ],
        'Out for Delivery': [
          { id: 1, orderId: 'ORD013', customer: 'Mark Brown', amount: '₹1,200', date: '2024-01-13' }
        ],
        'Pending': [
          { id: 1, orderId: 'ORD014', customer: 'Sophie Miller', amount: '₹700', date: '2024-01-13' },
          { id: 2, orderId: 'ORD015', customer: 'Ryan Jones', amount: '₹1,400', date: '2024-01-12' }
        ]
      };
      fullData = statusOrders[status] || [];
      data = fullData;
      title = `${status} Orders`;
    }

    // Ensure fullData is set if not already (for cases without pagination)
    if (fullData.length === 0 && data.length > 0) {
      fullData = data;
    }

    if (!data || data.length === 0) return null;

    // Use optimized columns from component-level memoization or fallback to quick analysis
    const columns = optimizedColumns.length > 0 ? optimizedColumns : (() => {
      // Fallback for unknown data types - analyze only first 5 rows for performance
      if (!data || data.length === 0) return [];

      const sampleData = data.slice(0, Math.min(5, data.length));
      const allColumns = new Set();
      sampleData.forEach(row => {
        Object.keys(row).forEach(key => {
          if (key !== 'id' && row[key] !== null && row[key] !== undefined && row[key] !== '') {
            allColumns.add(key);
          }
        });
      });

      return Array.from(allColumns);
    })();

    // Memoized table row component for performance
    const MemoizedTableRow = React.memo(({ row, columns, index, getStatusColor }) => (
      <TableRow key={row.id || index} hover>
        {columns.map((column) => (
          <TableCell
            key={column}
            sx={{
              padding: '12px 16px',
              fontSize: '0.85rem',
              color: '#555',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '200px'
            }}
          >
            {(() => {
              const value = row[column];

              // Handle null/undefined/empty values quickly
              if (value === null || value === undefined || value === '') {
                return (
                  <span style={{
                    color: '#bbb',
                    fontStyle: 'italic',
                    fontSize: '0.8rem'
                  }}>
                    —
                  </span>
                );
              }

              // Handle status columns with Chip component
              if (['status', 'stockStatus'].includes(column)) {
                return (
                  <Chip
                    label={value}
                    size="small"
                    color={getStatusColor(value)}
                    variant="filled"
                    sx={{
                      fontSize: '0.75rem',
                      height: '24px',
                      fontWeight: 'medium'
                    }}
                  />
                );
              }

              // Handle monetary values - expanded list
              if (['amount', 'totalAmount', 'totalSales', 'totalRevenue', 'revenue', 'currentBalance', 'balance', 'totalSpent', 'sales', 'creditLimit', 'totalCreditLimit', 'stockValue', 'avgOrderValue', 'avgPrice', 'monthlyRevenue'].includes(column)) {
                const numValue = Number(value);
                return (
                  <span style={{
                    fontWeight: '600',
                    color: '#2e7d32'
                  }}>
                    {isNaN(numValue) ? value : `₹${numValue.toLocaleString()}`}
                  </span>
                );
              }

              // Handle percentage columns
              if (['discount', 'discountPercent', 'growthRate', 'growth', 'utilization', 'deliveryRate'].includes(column) && String(value).includes('%')) {
                return (
                  <span style={{
                    color: column === 'deliveryRate' || column === 'utilization' ? '#1976d2' :
                      String(value).includes('+') ? '#2e7d32' : '#d32f2f',
                    fontWeight: '600'
                  }}>
                    {value}
                  </span>
                );
              }

              // Handle order ID / transaction IDs
              if (column === 'orderId' || column.toLowerCase().includes('id')) {
                return (
                  <span style={{
                    fontFamily: 'monospace',
                    backgroundColor: '#f5f5f5',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '0.8rem'
                  }}>
                    {value}
                  </span>
                );
              }

              // Default value display
              return String(value).trim() || '—';
            })()}
          </TableCell>
        ))}
      </TableRow>
    ));

    // Pagination logic - using regular function instead of useMemo to fix ESLint
    const getPaginationInfo = () => {
      let paginatedData = data;
      let showPagination = false;
      let currentPage = 0;
      let rowsPerPage = 10;
      let totalPages = 0;

      if (selectedMetric.startsWith('Retail - ') && retailDetailedData.length > retailTableRowsPerPage) {
        showPagination = true;
        currentPage = retailTablePage - 1;
        rowsPerPage = retailTableRowsPerPage;
        totalPages = Math.ceil(retailDetailedData.length / retailTableRowsPerPage);
        paginatedData = data;
      } else if (selectedMetric === 'Horeca - Details' && horecaTableData.length > horecaTableRowsPerPage) {
        showPagination = true;
        currentPage = horecaTablePage - 1;
        rowsPerPage = horecaTableRowsPerPage;
        totalPages = Math.ceil(horecaTableData.length / horecaTableRowsPerPage);
        const startIndex = (horecaTablePage - 1) * horecaTableRowsPerPage;
        const endIndex = startIndex + horecaTableRowsPerPage;
        paginatedData = horecaTableData.slice(startIndex, endIndex);
      } else if ((selectedMetric === 'Division Sales' || selectedMetric === 'Division Sales - Total Quantity') && data.length > productTableRowsPerPage) {
        showPagination = true;
        currentPage = productTablePage;
        rowsPerPage = productTableRowsPerPage;
        totalPages = Math.ceil(data.length / productTableRowsPerPage);
        paginatedData = data.slice(
          productTablePage * productTableRowsPerPage,
          (productTablePage + 1) * productTableRowsPerPage
        );
      }

      return { paginatedData, showPagination, currentPage, rowsPerPage, totalPages };
    };

    const { paginatedData, showPagination, currentPage, rowsPerPage, totalPages } = getPaginationInfo();

    // Check if current table is loading - use component-level memoized value
    const isTableCurrentlyLoading = isCurrentTableLoading;

    // Skeleton loader for table rows
    const TableSkeleton = React.memo(() => (
      <>
        {Array.from({ length: Math.min(10, rowsPerPage) }).map((_, index) => (
          <TableRow key={`skeleton-${index}`}>
            {columns.map((column, colIndex) => (
              <TableCell key={`skeleton-${index}-${colIndex}`} sx={{ padding: '12px 16px' }}>
                <Box
                  sx={{
                    height: '20px',
                    bgcolor: '#f0f0f0',
                    borderRadius: '4px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                      '100%': { opacity: 1 }
                    }
                  }}
                />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </>
    ));


    return (
      <Slide direction="up" in={showTable} timeout={800}>
        <Card ref={tableRef} sx={{ mt: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {title} - Detailed View
                </Typography>
                {selectedPincode && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <LocationOn color="primary" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Showing data for {selectedPincode.city} - Pin Code: {selectedPincode.pincode}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedPincode(null);
                        setSelectedMetric(null);
                        setShowTable(false);
                      }}
                      variant="outlined"
                      sx={{ ml: 2 }}
                    >
                      Clear Filter
                    </Button>
                  </Box>
                )}
              </Box>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={() => exportToExcel(fullData.length > 0 ? fullData : data, `${title.replace(/\s+/g, '_')}_export`)}
                size="small"
                sx={{ ml: 2 }}
              >
                Export XLSX
              </Button>
            </Box>
            <TableContainer sx={{
              maxHeight: 420,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              '& .MuiTableCell-root': {
                borderBottom: '1px solid #f5f5f5',
              },
              '& .MuiTableRow-root:hover': {
                backgroundColor: '#f9f9f9',
              }
            }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column}
                        sx={{
                          fontWeight: 'bold',
                          textTransform: 'capitalize',
                          backgroundColor: '#fafafa',
                          color: '#333',
                          fontSize: '0.875rem',
                          padding: '14px 16px',
                          borderBottom: '2px solid #e0e0e0',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {column.replace(/([A-Z])/g, ' $1').trim()}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isTableCurrentlyLoading ? (
                    <TableSkeleton />
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((row, index) => (
                      <MemoizedTableRow
                        key={row.id || `${selectedMetric}-${index}`}
                        row={row}
                        columns={columns}
                        index={index}
                        getStatusColor={getStatusColor}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Pagination Controls */}
            {showPagination && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} px={2}>
                <Typography variant="body2" color="text.secondary">
                  {selectedMetric.startsWith('Retail - ')
                    ? `Showing ${Math.min((retailTablePage - 1) * retailTableRowsPerPage + 1, retailDetailedData.length)}-${Math.min(retailTablePage * retailTableRowsPerPage, retailDetailedData.length)} of ${retailDetailedData.length} records`
                    : selectedMetric === 'Horeca - Details'
                      ? `Showing ${Math.min((horecaTablePage - 1) * horecaTableRowsPerPage + 1, horecaTableData.length)}-${Math.min(horecaTablePage * horecaTableRowsPerPage, horecaTableData.length)} of ${horecaTableData.length} records`
                      : `Showing ${Math.min(currentPage * rowsPerPage + 1, data.length)}-${Math.min((currentPage + 1) * rowsPerPage, data.length)} of ${data.length} records`
                  }
                </Typography>

                <Box display="flex" alignItems="center" gap={1}>
                  <Button
                    size="small"
                    disabled={selectedMetric.startsWith('Retail - ') ? retailTablePage === 1 : selectedMetric === 'Horeca - Details' ? horecaTablePage === 1 : currentPage === 0}
                    onClick={() => {
                      if (selectedMetric.startsWith('Retail - ')) {
                        setRetailTablePage(retailTablePage - 1);
                      } else if (selectedMetric === 'Horeca - Details') {
                        setHorecaTablePage(horecaTablePage - 1);
                      } else {
                        setProductTablePage(currentPage - 1);
                      }
                    }}
                    sx={{ mr: 1 }}
                  >
                    Previous
                  </Button>

                  <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
                    {selectedMetric.startsWith('Retail - ')
                      ? `Page ${retailTablePage} of ${Math.ceil(retailDetailedData.length / retailTableRowsPerPage)}`
                      : selectedMetric === 'Horeca - Details'
                        ? `Page ${horecaTablePage} of ${Math.ceil(horecaTableData.length / horecaTableRowsPerPage)}`
                        : `Page ${currentPage + 1} of ${totalPages}`
                    }
                  </Typography>

                  <Button
                    size="small"
                    disabled={selectedMetric.startsWith('Retail - ')
                      ? retailTablePage >= Math.ceil(retailDetailedData.length / retailTableRowsPerPage)
                      : selectedMetric === 'Horeca - Details'
                        ? horecaTablePage >= Math.ceil(horecaTableData.length / horecaTableRowsPerPage)
                        : (currentPage + 1) * rowsPerPage >= data.length
                    }
                    onClick={() => {
                      if (selectedMetric.startsWith('Retail - ')) {
                        setRetailTablePage(retailTablePage + 1);
                      } else if (selectedMetric === 'Horeca - Details') {
                        setHorecaTablePage(horecaTablePage + 1);
                      } else {
                        setProductTablePage(currentPage + 1);
                      }
                    }}
                    sx={{ ml: 1 }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Slide>
    );
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary', index }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const currentStats = getRoleBasedStats();
    const numericValue = currentStats.find(stat => stat.title === title)?.numericValue || 0;

    // Breakdown data for tooltips
    const getBreakdownData = () => {
      switch (title) {
        case 'Placed Orders':
          const placedOrdersBreakdown = currentStats.find(stat => stat.title === 'Placed Orders')?.breakdown || [];
          return placedOrdersBreakdown;
        case 'Scheduled Deliveries':
          // Create breakdown from OrderStatus data
          if (!retailOrderSummary.rawData || retailOrderSummary.rawData.length === 0) {
            return [];
          }

          const statusColors = {
            'CN': '#f44336',  // Red for Cancelled
            'CR': '#ff5722',  // Deep Orange for Cancellation Request
            'HR': '#9c27b0',  // Purple for Hold Request
            'PL': '#2196f3',  // Blue for Planned
            'PN': '#ff9800',  // Orange for Pending
            'DR': '#795548',  // Brown for Diversion Request
            'DL': '#4caf50',  // Green for Delivered
            'OD': '#00bcd4'   // Cyan for Out for Delivery
          };

          const statusLabels = {
            'CN': 'Cancelled',
            'CR': 'Cancellation Request',
            'HR': 'Hold Request',
            'PL': 'Planned',
            'PN': 'Pending',
            'DR': 'Diversion Request',
            'DL': 'Delivered',
            'OD': 'Out for Delivery'
          };

          return retailOrderSummary.rawData.map(item => ({
            label: statusLabels[item.OrderStatus] || item.OrderStatus,
            value: `${item.OrderCount} Orders / ₹${item.TotalAmount.toLocaleString()}`,
            color: statusColors[item.OrderStatus] || '#757575',
            icon: <ShoppingCart sx={{ color: statusColors[item.OrderStatus] || '#757575', fontSize: 18 }} />,
            orderStatus: item.OrderStatus
          }));
        case 'Wallet':
          // Create breakdown from order type data
          const orderTypeBreakdown = walletTableData.reduce((acc, item) => {
            const orderType = item.OrderType;
            if (!acc[orderType]) {
              acc[orderType] = {
                orderType,
                count: 0,
                totalAmount: 0
              };
            }
            acc[orderType].count += 1;
            acc[orderType].totalAmount += item.TotalAmount || 0;
            return acc;
          }, {});

          const orderTypeColors = {
            'PREPD': '#1976d2',
            'SBSCR': '#388e3c',
            'WALLET': '#ff9800'
          };

          const orderTypeLabels = {
            'PREPD': 'Prepaid',
            'SBSCR': 'Subscription',
            'WALLET': 'Wallet'
          };

          return Object.values(orderTypeBreakdown).map(orderType => ({
            label: orderTypeLabels[orderType.orderType] || orderType.orderType,
            value: `${orderType.count} / ₹${orderType.totalAmount.toLocaleString()}`,
            color: orderTypeColors[orderType.orderType] || '#757575',
            icon: <AttachMoney sx={{ color: orderTypeColors[orderType.orderType] || '#757575', fontSize: 18 }} />,
            orderType: orderType.orderType
          }));
        case 'Happy Hour':
          // Group Happy Hour data by ChannelID instead of Division
          const channelBreakdownHH = happyHourDetailedData.reduce((acc, item) => {
            const channelId = item.ChannelID || 'Unknown';
            if (!acc[channelId]) {
              acc[channelId] = { channelId, qty: 0, amount: 0 };
            }
            acc[channelId].qty += item.SaleQty || 0;
            acc[channelId].amount += item.TotalAmount || 0;
            return acc;
          }, {});

          return Object.values(channelBreakdownHH).map((channel, index) => ({
            label: channel.channelId,
            value: `${channel.qty?.toLocaleString() || '0'} KG / ₹${channel.amount?.toLocaleString() || '0'}`,
            color: index === 0 ? '#9c27b0' : index === 1 ? '#ff6b35' : index === 2 ? '#1976d2' : '#388e3c',
            icon: <Schedule sx={{ color: index === 0 ? '#9c27b0' : index === 1 ? '#ff6b35' : index === 2 ? '#1976d2' : '#388e3c', fontSize: 18 }} />
          }));
        case 'Retail':
          return retailSummary.channelBreakdown.map((channel, index) => {
            const colors = ['#1976d2', '#2e7d32', '#ff6b35', '#9c27b0'];
            const color = colors[index % colors.length];
            return {
              label: channel.channelId,
              value: `${channel.qty?.toLocaleString() || '0'} KG / ₹${channel.amount?.toLocaleString() || '0'}`,
              color: color,
              icon: <Store sx={{ color: color, fontSize: 18 }} />
            };
          });
        case 'Division Sales':
          return [
            {
              label: 'RTC',
              value: `${salesSummaryData.find(item => item.name === 'RTC')?.count || 0} KG / ₹${salesSummaryData.find(item => item.name === 'RTC')?.totalAmount?.toLocaleString() || '0'}`,
              color: '#1976d2',
              icon: <Analytics sx={{ color: '#1976d2', fontSize: 18 }} />
            },
            {
              label: 'Non-poultry',
              value: `${salesSummaryData.find(item => item.name === 'NON-POULTRY')?.count || 0} KG / ₹${salesSummaryData.find(item => item.name === 'NON-POULTRY')?.totalAmount?.toLocaleString() || '0'}`,
              color: '#ff6b35',
              icon: <Analytics sx={{ color: '#ff6b35', fontSize: 18 }} />
            },
            {
              label: 'Egg',
              value: `${salesSummaryData.find(item => item.name === 'EGG')?.count || 0} KG / ₹${salesSummaryData.find(item => item.name === 'EGG')?.totalAmount?.toLocaleString() || '0'}`,
              color: '#2e7d32',
              icon: <Analytics sx={{ color: '#2e7d32', fontSize: 18 }} />
            },
            {
              label: 'Poultry',
              value: `${salesSummaryData.find(item => item.name === 'POULTRY')?.count || 0} KG / ₹${salesSummaryData.find(item => item.name === 'POULTRY')?.totalAmount?.toLocaleString() || '0'}`,
              color: '#9c27b0',
              icon: <Analytics sx={{ color: '#9c27b0', fontSize: 18 }} />
            }
          ];
        case 'Horeca': {
          // Show Horeca Sale summary from API, including Division label
          const horecaRows = [
            {
              Division: horecaSummary.Division || 'POULTRY',
              SaleQty: horecaSummary.SaleQty,
              TotalAmount: horecaSummary.TotalAmount
            }
          ];

          return (
            <Slide direction="up" in={showTable} timeout={800}>
              <Card ref={tableRef} sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Horeca Sale - {horecaSummary.Division || 'POULTRY'}
                  </Typography>
                  <TableContainer sx={{
                    maxHeight: 220,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    mt: 2
                  }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Division</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Sale Qty (KG)</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Total Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {horecaRows.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{row.Division}</TableCell>
                            <TableCell>
                              <span style={{ color: '#388e3c', fontWeight: 600 }}>
                                {row.SaleQty?.toLocaleString()}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span style={{ color: '#d32f2f', fontWeight: 600 }}>
                                ₹{row.TotalAmount?.toLocaleString()}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Slide>
          );
        }

        case 'Stock':
          return [
            { label: 'In Stock', value: 1045, color: '#4CAF50' },
            { label: 'Low Stock', value: 200, color: '#FF5722' }
          ];
        case 'Sale Quantity':
          return [
            { label: 'Bulk Orders', value: 27000, color: '#2196F3' },
            { label: 'Retail Orders', value: 18670, color: '#9C27B0' }
          ];
        default:
          return [];
      }
    };

    const breakdownData = getBreakdownData();

    // Tooltip content component
    const TooltipContent = () => (
      <Box sx={{ p: 1, minWidth: 260 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          {title} Breakdown
        </Typography>
        {breakdownData.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
            onClick={() => handleBreakdownClick(item)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: item.color
                }}
              />
              <Typography variant="body2">{item.label}</Typography>
            </Box>
            <Typography variant="body2" fontWeight="bold">
              {typeof item.value === 'number' && item.value > 1000
                ? formatValue(item.value)
                : item.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );

    // Handle breakdown item click
    const handleBreakdownClick = (breakdownItem) => {
      let channelFilter = null;
      let typeFilter = null;
      let statusFilter = null;

      if (title === 'Placed Orders') {
        // Use orderStatus for filtering
        if (breakdownItem.orderStatus) {
          statusFilter = breakdownItem.orderStatus;
        }

        fetchPlacedOrdersTableData(statusFilter, fromDate, toDate);
        setSelectedMetric('Placed Orders - Details');
        setShowTable(true);
      } else if (title === 'Wallet') {
        if (breakdownItem.orderType) typeFilter = breakdownItem.orderType;
        fetchWalletData(typeFilter, fromDate, toDate);
        setSelectedMetric('Wallet - Details');
        setShowTable(true);
      } else if (title === 'Scheduled Deliveries') {
        // Use orderStatus for filtering
        if (breakdownItem.orderStatus) {
          statusFilter = breakdownItem.orderStatus;
        }

        fetchTotalOrdersTableData(statusFilter, fromDate, toDate);
        setSelectedMetric('Scheduled Deliveries - Details');
        setShowTable(true);
      } else if (title === 'Horeca') {
        setSelectedMetric('Horeca - Details');
        setHorecaTablePage(1);
        setShowTable(true);

        fetchHorecaTableData(fromDate, toDate).then(() => {
          setTimeout(() => {
            tableRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }, 100);
        });
      } else if (title === 'Happy Hour') {
        fetchHappyHourTableData(fromDate, toDate);
        setSelectedMetric('Happy Hour - Details');
        setShowTable(true);
      } else if (title === 'Retail') {
        const channelFilter = breakdownItem.label;
        setSelectedMetric(`Retail - ${channelFilter} Details`);
        setRetailTablePage(1); // Reset to first page
        setShowTable(true);

        // Fetch filtered data and show table immediately
        fetchRetailDetailedData(channelFilter).then(() => {
          setTimeout(() => {
            tableRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }, 100);
        });
      } else {
        // Existing logic for other metrics
        const filterTitle = `${title} - ${breakdownItem.label}`;
        setSelectedMetric(filterTitle);
      }
      setSelectedPincode(null);
      setTooltipOpen(false);

      // Show table immediately for instant feedback
      setTimeout(() => {
        tableRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    };


    // Handle main card click
    const handleCardClick = (e) => {
      if (!tooltipOpen && !isTableLoading && !showBottomLoader) {
        if (title === 'Placed Orders') {
          setIsTableLoading(true);
          setSelectedMetric('Placed Orders - Details');
          setShowBottomLoader(true);
          setLoadingProgress(0);
          setShowTable(false);

          fetchPlacedOrdersTableData(null, fromDate, toDate);

          const progressInterval = setInterval(() => {
            setLoadingProgress((prev) => {
              if (prev >= 100) {
                clearInterval(progressInterval);
                setTimeout(() => {
                  setShowBottomLoader(false);
                  setShowTable(true);
                  setIsTableLoading(false);
                  setTimeout(() => {
                    tableRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'end'
                    });
                  }, 200);
                }, 300);
                return 100;
              }
              return prev + 3;
            });
          }, 35);
        } else if (title === 'Wallet') {
          setIsTableLoading(true);
          setSelectedMetric('Wallet - Details');
          setShowBottomLoader(true);
          setLoadingProgress(0);
          setShowTable(false);

          fetchWalletData(null, fromDate, toDate);

          const progressInterval = setInterval(() => {
            setLoadingProgress((prev) => {
              if (prev >= 100) {
                clearInterval(progressInterval);
                setTimeout(() => {
                  setShowBottomLoader(false);
                  setShowTable(true);
                  setIsTableLoading(false);
                  setTimeout(() => {
                    tableRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'end'
                    });
                  }, 200);
                }, 300);
                return 100;
              }
              return prev + 3;
            });
          }, 35);
        } else if (title === 'Scheduled Deliveries') {
          setIsTableLoading(true);
          setSelectedMetric('Scheduled Deliveries - Details');
          setShowBottomLoader(true);
          setLoadingProgress(0);
          setShowTable(false);

          fetchTotalOrdersTableData(null, fromDate, toDate);

          const progressInterval = setInterval(() => {
            setLoadingProgress((prev) => {
              if (prev >= 100) {
                clearInterval(progressInterval);
                setTimeout(() => {
                  setShowBottomLoader(false);
                  setShowTable(true);
                  setIsTableLoading(false);
                  setTimeout(() => {
                    tableRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'end'
                    });
                  }, 200);
                }, 300);
                return 100;
              }
              return prev + 3;
            });
          }, 35);
        } else if (title === 'Retail') {
          setSelectedMetric('Retail - Details');
          setRetailTablePage(1); // Reset to first page
          setShowTable(true);

          // Fetch data and show table immediately
          fetchRetailDetailedData().then(() => {
            setTimeout(() => {
              tableRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }, 100);
          });
        } else if (title === 'Happy Hour') {
          setIsTableLoading(true);
          setSelectedMetric('Happy Hour - Details');
          setShowBottomLoader(true);
          setLoadingProgress(0);
          setShowTable(false);

          fetchHappyHourTableData(fromDate, toDate);

          const progressInterval = setInterval(() => {
            setLoadingProgress((prev) => {
              if (prev >= 100) {
                clearInterval(progressInterval);
                setTimeout(() => {
                  setShowBottomLoader(false);
                  setShowTable(true);
                  setIsTableLoading(false);
                  setTimeout(() => {
                    tableRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'end'
                    });
                  }, 200);
                }, 300);
                return 100;
              }
              return prev + 3;
            });
          }, 35);
        } else if (title === 'Horeca') {
          setSelectedMetric('Horeca - Details');
          setHorecaTablePage(1); // Reset to first page
          setShowTable(true);

          // Fetch data and show table immediately
          fetchHorecaTableData(fromDate, toDate).then(() => {
            setTimeout(() => {
              tableRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }, 100);
          });
        } else {
          handleStatCardClick(title);
        }
      }
    };

    // Handle tooltip toggle
    const handleTooltipToggle = (e) => {
      e.stopPropagation();
      setTooltipOpen(!tooltipOpen);
    };

    // Format value for display
    const formatValue = (val) => {
      if (typeof val === 'number') {
        if (val >= 1000000) {
          return `₹${(val / 1000000).toFixed(1)}M`;
        } else if (val >= 1000) {
          return `₹${(val / 1000).toFixed(1)}K`;
        }
        return `₹${val}`;
      }
      return val;
    };

    return (
      <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
        <Tooltip
          title={breakdownData.length > 0 ? <TooltipContent /> : ''}
          open={tooltipOpen && breakdownData.length > 0}
          placement="bottom"
          arrow
          PopperProps={{
            sx: {
              '& .MuiTooltip-tooltip': {
                backgroundColor: 'rgba(0,0,0,0.9)',
                color: 'white',
                fontSize: '12px',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                maxWidth: 'none'
              },
              '& .MuiTooltip-arrow': {
                color: 'rgba(0,0,0,0.9)'
              }
            }
          }}
        >
          <Card
            onClick={handleCardClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
              height: '100%',
              minHeight: 160,
              maxHeight: 180,
              cursor: (isTableLoading || showBottomLoader) ? 'not-allowed' : 'pointer',
              transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              transform: (isTableLoading || showBottomLoader)
                ? 'translateY(0) scale(1)'
                : isHovered ? 'translateY(-6px) scale(1.02)' : 'translateY(0) scale(1)',
              boxShadow: (isTableLoading || showBottomLoader)
                ? '0 2px 8px rgba(0,0,0,0.05)'
                : isHovered
                  ? `0 12px 24px rgba(0,0,0,0.15), 
                   0 6px 12px rgba(0,0,0,0.1),
                   0 0 0 1px ${color === 'primary' ? '#1976d2' :
                    color === 'success' ? '#388e3c' :
                      color === 'warning' ? '#ff6b35' :
                        color === 'secondary' ? '#9c27b0' :
                          color === 'info' ? '#00bcd4' :
                            '#d32f2f'}30`
                  : '0 4px 12px rgba(0,0,0,0.08)',
              background: (isTableLoading || showBottomLoader)
                ? 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)'
                : `linear-gradient(135deg, 
                   rgba(255,255,255,0.95) 0%, 
                   rgba(248,250,252,0.95) 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${(isTableLoading || showBottomLoader) ? 'rgba(224,224,224,0.3)' :
                isHovered ? `${color === 'primary' ? '#1976d2' :
                  color === 'success' ? '#388e3c' :
                    color === 'warning' ? '#ff6b35' :
                      color === 'secondary' ? '#9c27b0' :
                        color === 'info' ? '#00bcd4' :
                          '#d32f2f'}40` : 'rgba(0,0,0,0.08)'}`,
              opacity: (isTableLoading || showBottomLoader) ? 0.7 : 1,
              position: 'relative',
              overflow: 'visible',
              pointerEvents: (isTableLoading || showBottomLoader) ? 'none' : 'auto',
              borderRadius: 1,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg, 
                ${color === 'primary' ? '#1976d2' :
                    color === 'success' ? '#388e3c' :
                      color === 'warning' ? '#ff6b35' :
                        color === 'secondary' ? '#9c27b0' :
                          color === 'info' ? '#00bcd4' :
                            '#d32f2f'}08, 
                transparent 50%)`,
                borderRadius: 1,
                opacity: isHovered ? 1 : 0.5,
                transition: 'opacity 0.3s ease',
                zIndex: -1,
              },
            }}
          >
            <CardContent sx={{
              p: 2,
              pb: '12px !important',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              zIndex: 1,
              background: 'transparent',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: `linear-gradient(90deg, 
                ${color === 'primary' ? '#1976d2' :
                    color === 'success' ? '#388e3c' :
                      color === 'warning' ? '#ff6b35' :
                        color === 'secondary' ? '#9c27b0' :
                          color === 'info' ? '#00bcd4' :
                            '#d32f2f'}, 
                ${color === 'primary' ? '#42a5f5' :
                    color === 'success' ? '#66bb6a' :
                      color === 'warning' ? '#ffab40' :
                        color === 'secondary' ? '#ba68c8' :
                          color === 'info' ? '#4dd0e1' :
                            '#ef5350'})`,
              }
            }}>
              {/* Header Section */}
              <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 1,
                minHeight: 28,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  right: 0,
                  height: '1px',
                  background: `linear-gradient(90deg, 
                  ${color === 'primary' ? '#1976d2' :
                      color === 'success' ? '#388e3c' :
                        color === 'warning' ? '#ff6b35' :
                          color === 'secondary' ? '#9c27b0' :
                            color === 'info' ? '#00bcd4' :
                              '#d32f2f'}20, 
                  transparent 50%, 
                  ${color === 'primary' ? '#1976d2' :
                      color === 'success' ? '#388e3c' :
                        color === 'warning' ? '#ff6b35' :
                          color === 'secondary' ? '#9c27b0' :
                            color === 'info' ? '#00bcd4' :
                              '#d32f2f'}20)`,
                  opacity: isHovered ? 1 : 0.5,
                  transition: 'opacity 0.3s ease',
                }
              }}>
                <Box sx={{ flex: 1, pr: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      mb: 1.5,
                      fontWeight: 800,
                      fontSize: '0.65rem',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      lineHeight: 1.2,
                      display: 'block',
                      background: `linear-gradient(135deg, 
                      ${color === 'primary' ? '#1976d2' :
                          color === 'success' ? '#388e3c' :
                            color === 'warning' ? '#ff6b35' :
                              color === 'secondary' ? '#9c27b0' :
                                color === 'info' ? '#00bcd4' :
                                  '#d32f2f'}, 
                      ${color === 'primary' ? '#1565c0' :
                          color === 'success' ? '#2e7d32' :
                            color === 'warning' ? '#f57c00' :
                              color === 'secondary' ? '#7b1fa2' :
                                color === 'info' ? '#0097a7' :
                                  '#c62828'})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      textShadow: isHovered ? `0 0 20px ${color === 'primary' ? '#1976d2' :
                        color === 'success' ? '#388e3c' :
                          color === 'warning' ? '#ff6b35' :
                            color === 'secondary' ? '#9c27b0' :
                              color === 'info' ? '#00bcd4' :
                                '#d32f2f'}30` : 'none',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {title}
                  </Typography>
                </Box>

                {/* Icon and Info Section */}
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 1
                }}>
                  <Box
                    sx={{
                      p: 1.2,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, 
                      ${color === 'primary' ? 'rgba(25,118,210,0.15)' :
                          color === 'success' ? 'rgba(56,142,60,0.15)' :
                            color === 'warning' ? 'rgba(255,107,53,0.15)' :
                              color === 'info' ? 'rgba(0,188,212,0.15)' :
                                color === 'secondary' ? 'rgba(156,39,176,0.15)' : 'rgba(211,47,47,0.15)'}, 
                      ${color === 'primary' ? 'rgba(25,118,210,0.05)' :
                          color === 'success' ? 'rgba(56,142,60,0.05)' :
                            color === 'warning' ? 'rgba(255,107,53,0.05)' :
                              color === 'info' ? 'rgba(0,188,212,0.05)' :
                                color === 'secondary' ? 'rgba(156,39,176,0.05)' : 'rgba(211,47,47,0.05)'})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      minWidth: 48,
                      minHeight: 48,
                      boxShadow: isHovered
                        ? `0 8px 25px ${color === 'primary' ? 'rgba(25,118,210,0.4)' :
                          color === 'success' ? 'rgba(56,142,60,0.4)' :
                            color === 'warning' ? 'rgba(255,107,53,0.4)' :
                              color === 'info' ? 'rgba(0,188,212,0.4)' :
                                color === 'secondary' ? 'rgba(156,39,176,0.4)' : 'rgba(211,47,47,0.4)'}`
                        : `0 4px 15px ${color === 'primary' ? 'rgba(25,118,210,0.2)' :
                          color === 'success' ? 'rgba(56,142,60,0.2)' :
                            color === 'warning' ? 'rgba(255,107,53,0.2)' :
                              color === 'info' ? 'rgba(0,188,212,0.2)' :
                                color === 'secondary' ? 'rgba(156,39,176,0.2)' : 'rgba(211,47,47,0.2)'}`,
                      transform: isHovered ? 'scale(1.08) rotate(2deg)' : 'scale(1) rotate(0deg)',
                      border: `2px solid ${color === 'primary' ? 'rgba(25,118,210,0.2)' :
                        color === 'success' ? 'rgba(56,142,60,0.2)' :
                          color === 'warning' ? 'rgba(255,107,53,0.2)' :
                            color === 'info' ? 'rgba(0,188,212,0.2)' :
                              color === 'secondary' ? 'rgba(156,39,176,0.2)' : 'rgba(211,47,47,0.2)'}`,
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 24,
                        background: `linear-gradient(135deg, 
                        ${color === 'primary' ? '#1976d2' :
                            color === 'success' ? '#388e3c' :
                              color === 'warning' ? '#ff6b35' :
                                color === 'info' ? '#00bcd4' :
                                  color === 'secondary' ? '#9c27b0' : '#d32f2f'}, 
                        ${color === 'primary' ? '#1565c0' :
                            color === 'success' ? '#2e7d32' :
                              color === 'warning' ? '#f57c00' :
                                color === 'info' ? '#0097a7' :
                                  color === 'secondary' ? '#7b1fa2' : '#c62828'})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        filter: isHovered
                          ? `drop-shadow(0 0 8px ${color === 'primary' ? '#1976d2' :
                            color === 'success' ? '#388e3c' :
                              color === 'warning' ? '#ff6b35' :
                                color === 'info' ? '#00bcd4' :
                                  color === 'secondary' ? '#9c27b0' : '#d32f2f'}40)`
                          : 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))',
                        transition: 'all 0.3s ease',
                        transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                      }}
                    />
                  </Box>

                  {/* {breakdownData.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={handleTooltipToggle}
                    sx={{
                      backgroundColor: tooltipOpen ? 'action.selected' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        transform: 'scale(1.1)'
                      },
                      transition: 'all 0.2s ease',
                      width: 28,
                      height: 28,
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )} */}
                </Box>
              </Box>

              {/* Value Section */}
              <Box sx={{
                mb: 2,
                minHeight: 80,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center'
              }}>
                {/* Enhanced value rendering for different card types */}
                {title === 'Placed Orders' ? (
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ width: '100%' }}>

                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          opacity: 0.5
                        }}
                      >
                        Order Date
                      </Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      fontWeight="800"
                      sx={{
                        color: '#1565c0',
                        fontSize: { xs: '1.5rem', sm: '1.8rem' },
                        textAlign: 'center',
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em',
                        textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      {placedOrdersTotal.count.toLocaleString()}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{
                        color: '#666',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Placed Orders
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <Typography
                        variant="body1"
                        fontWeight="700"
                        sx={{
                          color: '#2e7d32',
                          fontSize: '1rem'
                        }}
                      >
                        ₹{placedOrdersTotal.amount.toLocaleString()}
                      </Typography>
                      {breakdownData.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={handleTooltipToggle}
                          sx={{
                            backgroundColor: tooltipOpen ? 'action.selected' : 'transparent',
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            },
                            width: 24,
                            height: 24,
                            border: '1px solid rgba(0,0,0,0.1)'
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ) : title === 'Scheduled Deliveries' ? (
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ width: '100%' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          opacity: 0.5
                        }}
                      >
                        Schedule Date
                      </Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      fontWeight="800"
                      sx={{
                        color: '#1565c0',
                        fontSize: { xs: '1.5rem', sm: '1.8rem' },
                        textAlign: 'center',
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em',
                        textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      {retailOrderSummary.totalCount.toLocaleString()}
                    </Typography>

                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{
                        color: '#666',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Scheduled Deliveries
                    </Typography>

                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <Typography
                        variant="body1"
                        fontWeight="700"
                        sx={{
                          color: '#2e7d32',
                          fontSize: '1rem'
                        }}
                      >
                        ₹{retailOrderSummary.totalAmount.toLocaleString()}
                      </Typography>
                      {breakdownData.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={handleTooltipToggle}
                          sx={{
                            backgroundColor: tooltipOpen ? 'action.selected' : 'transparent',
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            },
                            width: 24,
                            height: 24,
                            border: '1px solid rgba(0,0,0,0.1)'
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ) : title === 'Wallet' ? (
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ width: '100%' }}>

                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          opacity: 0.5
                        }}
                      >
                        Bill Date
                      </Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      fontWeight="800"
                      sx={{
                        color: '#1565c0',
                        fontSize: { xs: '1.5rem', sm: '1.8rem' },
                        textAlign: 'center',
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em'
                      }}
                    >
                      {(walletSummary.prepaidCount + walletSummary.subscriptionCount).toLocaleString()}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{
                        color: '#666',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Total Accounts Billed
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <Typography
                        variant="body1"
                        fontWeight="700"
                        sx={{
                          color: '#2e7d32',
                          fontSize: '1rem'
                        }}
                      >
                        ₹{(walletSummary.prepaidAmount + walletSummary.subscriptionAmount).toLocaleString()}
                      </Typography>
                      {breakdownData.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={handleTooltipToggle}
                          sx={{
                            backgroundColor: tooltipOpen ? 'action.selected' : 'transparent',
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            },
                            width: 24,
                            height: 24,
                            border: '1px solid rgba(0,0,0,0.1)'
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ) : title === 'Horeca' ? (
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ width: '100%' }}>

                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          opacity: 0.5
                        }}
                      >
                        Bill Date
                      </Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      fontWeight="800"
                      sx={{
                        color: '#ef6c00',
                        fontSize: { xs: '1.5rem', sm: '1.8rem' },
                        textAlign: 'center',
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em',
                        textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      {horecaSummary.SaleQty?.toLocaleString() || '0'}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight="600"
                      sx={{
                        color: '#666',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      KG Billed
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <Typography
                        variant="body1"
                        fontWeight="700"
                        sx={{
                          color: '#2e7d32',
                          fontSize: '1rem',
                        }}
                      >
                        ₹{horecaSummary.TotalAmount?.toLocaleString() || '0'}
                      </Typography>
                      {breakdownData.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={handleTooltipToggle}
                          sx={{
                            backgroundColor: tooltipOpen ? 'action.selected' : 'transparent',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease',
                            width: 28,
                            height: 28,
                            border: '1px solid rgba(0,0,0,0.1)'
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ) : title === 'Happy Hour' ? (
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ width: '100%' }}>

                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          opacity: 0.5
                        }}
                      >
                        Bill Date
                      </Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      fontWeight="800"
                      sx={{
                        color: '#7b1fa2',
                        fontSize: { xs: '1.5rem', sm: '1.8rem' },
                        textAlign: 'center',
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em',
                        textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      {happyHourSummary.totalQty?.toLocaleString() || '0'}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight="600"
                      sx={{
                        color: '#666',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      KG Billed
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <Typography
                        variant="body1"
                        fontWeight="700"
                        sx={{
                          color: '#2e7d32',
                          fontSize: '1rem',
                        }}
                      >
                        ₹{happyHourSummary.totalAmount?.toLocaleString() || '0'}
                      </Typography>
                      {breakdownData.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={handleTooltipToggle}
                          sx={{
                            backgroundColor: tooltipOpen ? 'action.selected' : 'transparent',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease',
                            width: 28,
                            height: 28,
                            border: '1px solid rgba(0,0,0,0.1)'
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ) : title === 'Retail' ? (
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <Box display="flex" flexDirection="column" alignItems="flex-start" sx={{ width: '100%' }}>

                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666',
                          fontSize: '0.7rem',
                          fontWeight: 200,
                          opacity: 0.5
                        }}
                      >
                        Bill Date
                      </Typography>
                    </Box>
                    <Typography
                      variant="h4"
                      fontWeight="800"
                      sx={{
                        color: '#2e7d32',
                        fontSize: { xs: '1.5rem', sm: '1.8rem' },
                        textAlign: 'center',
                        lineHeight: 1.2,
                        letterSpacing: '-0.02em',
                        textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      {retailSummary.totalQty?.toLocaleString() || '0'}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight="600"
                      sx={{
                        color: '#666',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Total Qty Billed
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <Typography
                        variant="body1"
                        fontWeight="700"
                        sx={{
                          color: '#388e3c',
                          fontSize: '1.15rem',
                          letterSpacing: '0.5px',
                        }}
                      >
                        ₹{retailSummary.totalAmount?.toLocaleString() || '0'}
                      </Typography>
                      {breakdownData.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTooltipOpen(!tooltipOpen);
                          }}
                          sx={{
                            color: '#2e7d32',
                            '&:hover': {
                              backgroundColor: 'rgba(46, 125, 50, 0.1)',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease',
                            width: 28,
                            height: 28,
                            border: '1px solid rgba(0,0,0,0.1)'
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ) : title === 'Division Sales' ? (
                  <Box display="flex" flexDirection="column" gap={0.8} sx={{ width: '100%', height: '100%' }}>
                    {salesSummaryData && salesSummaryData.length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={0.8} justifyContent="center">
                        {salesSummaryData.slice(0, 4).map((division, index) => {
                          const colors = ['#1976d2', '#388e3c', '#ff6b35', '#9c27b0'];
                          const color = colors[index % colors.length];
                          return (
                            <Box
                              key={division.name}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (isTableLoading || showBottomLoader) return;

                                setIsTableLoading(true);
                                setSelectedMetric('Division Sales');
                                setSelectedPincode(null);
                                setShowBottomLoader(true);
                                setLoadingProgress(0);
                                setShowTable(false);

                                try {
                                  await fetchDetailedSalesData(division.name);

                                  // Simulate loading progress
                                  const progressInterval = setInterval(() => {
                                    setLoadingProgress((prev) => {
                                      if (prev >= 100) {
                                        clearInterval(progressInterval);
                                        setTimeout(() => {
                                          setShowBottomLoader(false);
                                          setShowTable(true);
                                          setIsTableLoading(false);

                                          // Scroll to table
                                          setTimeout(() => {
                                            tableRef.current?.scrollIntoView({
                                              behavior: 'smooth',
                                              block: 'start'
                                            });
                                          }, 100);
                                        }, 200);
                                        return 100;
                                      }
                                      return prev + 3;
                                    });
                                  }, 35);
                                } catch (error) {
                                  setShowBottomLoader(false);
                                  setIsTableLoading(false);
                                }
                              }}
                              sx={{
                                flex: '1 1 calc(50% - 3px)',
                                minWidth: '75px',
                                maxWidth: '95px',
                                p: 0.8,
                                borderRadius: 1.5,
                                background: `linear-gradient(135deg, ${color}08, ${color}15)`,
                                border: `1px solid ${color}40`,
                                textAlign: 'center',
                                transition: 'all 0.3s ease',
                                boxShadow: `0 1px 4px ${color}20`,
                                cursor: 'pointer',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 2px 8px ${color}30`,
                                  border: `1px solid ${color}60`,
                                }
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight="700"
                                sx={{
                                  color: color,
                                  fontSize: '0.6rem',
                                  display: 'block',
                                  mb: 0.3,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                {division.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight="800"
                                sx={{
                                  color: color,
                                  fontSize: '0.65rem',
                                  lineHeight: 1.2,
                                  textShadow: `0 1px 2px ${color}20`
                                }}
                              >
                                {division.count.toLocaleString()}
                              </Typography>
                              <Typography
                                variant="caption"
                                fontWeight="600"
                                sx={{
                                  color: '#666',
                                  fontSize: '0.6rem',
                                  display: 'block',
                                  mt: 0.2
                                }}
                              >
                                ₹{(division.totalAmount / 1000).toFixed(1)}K
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                          Loading divisions...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  value
                )}
              </Box>

              {/* Subtitle Section */}
              {/* {subtitle && (
              <Box sx={{ mt: 'auto' }}>
                {title === 'Scheduled Deliveries' ? (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: '#2e7d32',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }} />
                      <Typography variant="caption" fontWeight="600" color="#2e7d32" sx={{ fontSize: '0.7rem' }}>
                        {retailOrderSummary.completedCount} Completed
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: '#c62828',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }} />
                      <Typography variant="caption" fontWeight="600" color="#c62828" sx={{ fontSize: '0.7rem' }}>
                        {retailOrderSummary.canceledCount} Canceled
                      </Typography>
                    </Box>
                  </Box>
                ) : title === 'Wallet' ? (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: '#1565c0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }} />
                      <Typography variant="caption" fontWeight="600" color="#1565c0" sx={{ fontSize: '0.7rem' }}>
                        {walletSummary.prepaidCount} Prepaid
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: '#2e7d32',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }} />
                      <Typography variant="caption" fontWeight="600" color="#2e7d32" sx={{ fontSize: '0.7rem' }}>
                        {walletSummary.subscriptionCount} Subscription
                      </Typography>
                    </Box>
                  </Box>
                ) : title === 'Horeca' ? (
                  <Box display="flex" justifyContent="center" alignItems="center">
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: '#ef6c00',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }} />
                      <Typography variant="caption" fontWeight="600" color="#ef6c00" sx={{ fontSize: '0.7rem' }}>
                        {horecaSummary.Division || 'POULTRY'}
                      </Typography>
                    </Box>
                  </Box>
                ) : title === 'Happy Hour' ? (
                  <Box display="flex" justifyContent="center" alignItems="center">
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <AccessTime sx={{ fontSize: 12, color: '#7b1fa2' }} />
                      <Typography variant="caption" fontWeight="600" color="#7b1fa2" sx={{ fontSize: '0.7rem' }}>
                        {happyHourSummary.divisions?.length || 0} Divisions
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: typeof subtitle === 'string' && subtitle.includes('+') ? '#2e7d32' : '#c62828',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textAlign: 'center',
                      display: 'block'
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            )} */}
            </CardContent>

            {/* Enhanced Hover Indicator */}
            {isHovered && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 20,
                  right: 10,
                  height: 4,
                  background: `linear-gradient(90deg, ${color === 'primary' ? '#1565c0' :
                    color === 'success' ? '#2e7d32' :
                      color === 'warning' ? '#ef6c00' :
                        color === 'secondary' ? '#7b1fa2' :
                          '#c62828'}, transparent)`,
                  animation: 'pulse 2s infinite'
                }}
              />
            )}
          </Card>
        </Tooltip>
      </ClickAwayListener>
    );
  };


  const PinCodeCard = ({ pincode, city, orders, value, growth }) => (
    <Card
      onClick={() => handlePincodeClick(pincode, city)}
      sx={{
        height: '100%',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <LocationOn color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight="bold">
              {city}
            </Typography>
          </Box>
          <Chip
            label={growth}
            size="small"
            color={growth.includes('+') ? 'success' : 'error'}
            variant="outlined"
          />
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Pin Code: {pincode}
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Box textAlign="center" p={1} bgcolor="primary.light" borderRadius={1}>
              <Typography variant="h6" fontWeight="bold" color="white">
                {orders}
              </Typography>
              <Typography variant="caption" color="white">
                Orders
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box textAlign="center" p={1} bgcolor="success.light" borderRadius={1}>
              <Typography variant="h6" fontWeight="bold" color="white">
                ₹{(value / 1000).toFixed(0)}K
              </Typography>
              <Typography variant="caption" color="white">
                Value
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const ProductCard = ({
    title,
    products,
    isWorst = false,
    onProductClick,
    loading = false,
    totalQty = 0,
    totalSale = 0,
  }) => {

    // Handle Total Quantity click
    const handleTotalQtyClick = () => {
      if (products && products.length > 0) {
        setIsTableLoading(true);
        setSelectedMetric('Division Sales - Total Quantity');
        setShowBottomLoader(true);
        setLoadingProgress(0);
        setShowTable(false);

        // Simulate loading and show table
        const progressInterval = setInterval(() => {
          setLoadingProgress((prev) => {
            if (prev >= 100) {
              clearInterval(progressInterval);
              setTimeout(() => {
                setShowBottomLoader(false);
                setShowTable(true);
                setIsTableLoading(false);
              }, 500);
              return 100;
            }
            return prev + 10;
          });
        }, 200);
      }
    };

    return (
      <Card
        sx={{
          height: '100%',
          transition: 'all 0.3s ease',
          background: 'white',
          boxShadow: '0 4px 12px rgba(102,126,234,0.08)',
          border: '1px solid #e0e0e0',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(156,39,176,0.08)',
          },
        }}
      >
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontWeight="600" color="#1976d2">
              {title}
            </Typography>
            {loading && (
              <CircularProgress size={20} sx={{ color: '#1976d2', ml: 1 }} />
            )}
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box
              sx={{
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#e8f5e8',
                  transform: 'scale(1.02)'
                }
              }}
              onClick={handleTotalQtyClick}
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="caption" fontWeight="bold" color="#388e3c">
                  Total Qty:
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="#388e3c">
                  {totalQty.toLocaleString()}
                </Typography>
                <InfoOutlinedIcon sx={{ fontSize: 14, color: '#388e3c', ml: 0.5 }} />
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" fontWeight="bold" color="#d32f2f">
                Total Sale:
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="#d32f2f" ml={0.5} display="inline">
                ₹{totalSale.toLocaleString()}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 1 }}>
            {products.length === 0 && !loading && (
              <Typography variant="caption" color="text.secondary">
                No data available.
              </Typography>
            )}
            {products.map((product, index) => (
              <Box
                key={index}
                sx={{
                  mb: 1.5,
                  cursor: onProductClick ? 'pointer' : 'default',
                  background: index % 2 === 0 ? '#f9f9f9' : '#fff',
                  borderRadius: 1,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                  '&:hover': onProductClick
                    ? {
                      backgroundColor: '#e3f2fd',
                    }
                    : {},
                }}
                onClick={onProductClick ? () => onProductClick(product) : undefined}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#1976d2', fontWeight: 600 }}>
                    {product.name}
                  </Typography>
                  <Typography variant="caption" color="#d32f2f" fontWeight="bold">
                    ₹{product.totalAmount ? product.totalAmount.toLocaleString() : '0'}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#388e3c', fontWeight: 500 }}>
                    Qty: {product.count.toLocaleString()}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#9c27b0', fontWeight: 600 }}>
                      {product.performance}% of Total
                    </Typography>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: product.performance >= 30 ? '#4caf50' : product.performance >= 15 ? '#ff9800' : '#f44336'
                      }}
                    />
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={product.performance}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#f0f0f0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: product.performance >= 30 ? '#4caf50' :
                        product.performance >= 15 ? '#ff9800' :
                          product.performance >= 5 ? '#1976d2' : '#f44336',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const DeliveryStatusCard = ({ title, items }) => (
    <Card sx={{
      height: '100%',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }
    }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom fontWeight="600">
          {title}
        </Typography>
        <Box sx={{ mt: 1 }}>
          {items.map((item, index) => (
            <Box key={index} sx={{ mb: 1.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: item.isBold ? 'bold' : 'normal',
                    color: item.isBold ? '#333' : 'text.primary',
                    cursor: !item.isBold ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      textDecoration: !item.isBold ? 'underline' : 'none',
                      color: !item.isBold ? item.color : '#333'
                    }
                  }}
                  onClick={() => !item.isBold && handleDeliveryStatusClick(item.name)}
                >
                  {item.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: item.isBold ? 'bold' : '500',
                    color: item.color
                  }}
                >
                  {item.count}
                </Typography>
              </Box>
              {!item.isBold && (
                <Box sx={{ height: 4, backgroundColor: '#f0f0f0', borderRadius: 2, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      backgroundColor: item.color,
                      width: `${(item.count / 658) * 100}%`,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              )}
              {item.isBold && index < items.length - 1 && (
                <Divider sx={{ mt: 1 }} />
              )}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const DeliveryStatusPieChart = React.memo(({ title, items, isLoading }) => {
    // Store the last stable data to prevent fluctuations during loading
    const [stableData, setStableData] = React.useState(items);
    const [stableLoading, setStableLoading] = React.useState(false);

    // Update stable data only when not loading
    React.useEffect(() => {
      if (!isLoading && items && items.length > 0) {
        setStableData(items);
      }
    }, [isLoading, items]);

    // Track loading state changes
    React.useEffect(() => {
      if (isLoading) {
        setStableLoading(true);
      } else {
        // Delay clearing loading state to prevent flicker
        const timer = setTimeout(() => setStableLoading(false), 100);
        return () => clearTimeout(timer);
      }
    }, [isLoading]);

    // Use stable data for rendering
    const displayData = stableLoading ? stableData : items;

    // Extract pie chart data (non-bold items for the chart)
    const pieData = React.useMemo(() => displayData.filter(item => !item.isBold), [displayData]);

    // Extract totals (bold items)
    const totalOrders = React.useMemo(() => displayData.find(item => item.name === 'Online Orders')?.count || 0, [displayData]);
    const delayedOrders = React.useMemo(() => displayData.find(item => item.name === 'Delayed Orders')?.count || 0, [displayData]);

    const COLORS = ['#4caf50', '#2196f3', '#ff9800', '#f44336'];

    // Custom tooltip component for pie chart - disabled during loading
    const CustomTooltip = ({ active, payload }) => {
      // Don't show tooltip when loading
      if (isLoading) return null;

      if (active && payload && payload.length) {
        const data = payload[0];
        const totalCount = pieData.reduce((sum, d) => sum + d.count, 0);
        const percentage = totalCount > 0 ? ((data.value / totalCount) * 100).toFixed(1) : 0;

        return (
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ccc',
              borderRadius: 1,
              padding: '8px 12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: data.payload.color }}>
              {data.name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Count: {data.value}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Percentage: {percentage}%
            </Typography>
          </Box>
        );
      }
      return null;
    };

    return (
      <Card sx={{
        height: '100%',
        transition: 'box-shadow 0.3s ease, border-color 0.3s ease, opacity 0.3s ease',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: 4,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        opacity: isLoading ? 0.5 : 1,
        pointerEvents: isLoading ? 'none' : 'auto',
        position: 'relative',
        '&:hover': {
          boxShadow: isLoading ? '0 4px 20px rgba(0,0,0,0.08)' : '0 8px 28px rgba(0,0,0,0.12)',
          borderColor: isLoading ? 'rgba(255,255,255,0.2)' : 'rgba(76,175,80,0.3)',
        },
        '&::after': isLoading ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.3)',
          backdropFilter: 'blur(2px)',
          borderRadius: 4,
          pointerEvents: 'none',
          zIndex: 1
        } : {}
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            fontWeight="700"
            textAlign="center"
            sx={{
              background: 'linear-gradient(135deg, #4caf50, #388e3c)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 2
            }}
          >
            {title}
          </Typography>

          {/* Pie Chart */}
          <Box sx={{
            height: 220,
            mt: 2,
            position: 'relative',
            borderRadius: 2,
            background: 'rgba(255,255,255,0.8)',
            border: '1px solid rgba(255,255,255,0.3)',
            cursor: isLoading ? 'default' : 'pointer',
            pointerEvents: isLoading ? 'none' : 'auto',
            '& .recharts-wrapper': {
              position: 'absolute !important',
              inset: 0,
              cursor: isLoading ? 'default !important' : 'pointer !important',
              pointerEvents: isLoading ? 'none !important' : 'auto !important',
            },
            '& .recharts-surface': {
              overflow: 'visible',
              pointerEvents: isLoading ? 'none !important' : 'auto !important',
            },
            '& .recharts-pie-sector': {
              cursor: isLoading ? 'default' : 'pointer',
              transition: 'none !important',
              pointerEvents: isLoading ? 'none !important' : 'auto !important',
            },
            '& .recharts-layer': {
              transition: 'none !important',
              pointerEvents: isLoading ? 'none !important' : 'auto !important',
            },
            '& .recharts-tooltip-wrapper': {
              display: isLoading ? 'none !important' : 'block',
              pointerEvents: isLoading ? 'none !important' : 'auto !important',
            }
          }}>
            <ResponsiveContainer width="100%" height={220} debounce={1}>
              <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }} key={stableLoading ? 'loading' : 'loaded'}>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  innerRadius={25}
                  fill="#8884d8"
                  dataKey="count"
                  isAnimationActive={false}
                  animationBegin={0}
                  animationDuration={0}
                  label={false}
                  onClick={(data, index) => {
                    if (data && data.name && !isLoading) {
                      handleDeliveryStatusClick(data.name);
                    }
                  }}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color || COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                {!isLoading && <RechartsTooltip content={<CustomTooltip />} />}
              </PieChart>
            </ResponsiveContainer>
          </Box>

          {/* Legend */}
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={1}>
              {pieData.map((item, index) => {
                return (
                  <Grid item xs={6} key={index}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{
                        cursor: isLoading ? 'default' : 'pointer',
                        '&:hover': {
                          backgroundColor: isLoading ? 'transparent' : 'rgba(0,0,0,0.05)',
                          borderRadius: 1
                        }
                      }}
                      onClick={() => !isLoading && handleDeliveryStatusClick(item.name)}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: item.color || COLORS[index % COLORS.length],
                          borderRadius: '50%'
                        }}
                      />
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {item.name} ({item.count})
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Summary */}
          <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #e0e0e0' }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Box
                  textAlign="center"
                  sx={{
                    cursor: isLoading ? 'default' : 'pointer',
                    p: 1,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: isLoading ? 'transparent' : 'rgba(0,0,0,0.05)'
                    }
                  }}
                  onClick={() => {
                    if (!isLoading) {
                      handleDeliveryStatusClick('Online Orders');
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color="#333">
                    {totalOrders}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Online Orders
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  textAlign="center"
                  sx={{
                    cursor: isLoading ? 'default' : 'pointer',
                    p: 1,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: isLoading ? 'transparent' : 'rgba(244, 67, 54, 0.1)'
                    }
                  }}
                  onClick={() => {
                    if (!isLoading) {
                      handleDeliveryStatusClick('Delayed Orders');
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color="#f44336">
                    {delayedOrders}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Delay Orders
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    );
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper elevation={3} sx={{ p: 1, bgcolor: 'white' }}>
          <Typography variant="caption" fontWeight="medium">
            {label}
          </Typography>
          <Typography variant="caption" color="primary">
            Sales: ₹{payload[0].value.toLocaleString()}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer />
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default' }}>
        {/* Header */}
        <Header />
        <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary', height: 48 }}>
          <Toolbar sx={{ minHeight: '48px !important', px: 2 }}>
            {/* <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 2 }}
              size="small"
            >
              <MenuIcon />
            </IconButton> */}

            {/* Role Selector - Only show for RGMGR */}
            {(localStorage.getItem('userRole') === 'RGMGR' ||
              localStorage.getItem('userRole') === 'RMGR1') && (
                <Button
                  onClick={(e) => setRoleMenuAnchor(e.currentTarget)}
                  startIcon={roles[currentRole].icon}
                  sx={{
                    mr: 2,
                    bgcolor: roles[currentRole].color + '20',
                    color: roles[currentRole].color,
                    border: `1px solid ${roles[currentRole].color}40`,
                    '&:hover': {
                      bgcolor: roles[currentRole].color + '30',
                    }
                  }}
                  size="small"
                >
                  {roles[currentRole].name}
                </Button>
              )}

            {/* Dashboard title with area and branch info */}
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontSize: '1rem' }}>
              {getDashboardTitle()}
            </Typography>

            {/* <IconButton
              color="inherit"
              onClick={handleSortClick}
              sx={{ mr: 1 }}
              size="small"
            >
              <Sort />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<Download />}
              size="small"
              sx={{ fontSize: '0.75rem', px: 1 }}
              onClick={() => {
                if (selectedMetric === 'Division Sales' && detailedSalesData.length > 0) {
                  exportToExcel(detailedSalesData, `${selectedMetric}_export`);
                } else if (selectedMetric === 'Division Sales - Total Quantity' && salesSummaryData.length > 0) {
                  exportToExcel(salesSummaryData, `${selectedMetric}_export`);
                } else if (selectedMetric === 'Scheduled Deliveries - Details' && totalOrdersTableData.length > 0) {
                  exportToExcel(totalOrdersTableData, `${selectedMetric}_export`);
                } else if (selectedMetric === 'Wallet - Details' && walletTableData.length > 0) {
                  exportToExcel(walletTableData, `${selectedMetric}_export`);
                } else if (selectedMetric === 'Horeca - Details' && horecaTableData.length > 0) {
                  exportToExcel(horecaTableData, `${selectedMetric}_export`);
                } else if (selectedMetric === 'Retail - Details' && retailDetailedData.length > 0) {
                  exportToExcel(retailDetailedData, `${selectedMetric}_export`);
                } else if (selectedMetric.startsWith('Delivery Status - ')) {
                  // Handle delivery status exports
                  let deliveryData = [];
                  if (selectedMetric === 'Delivery Status - All Orders') {
                    deliveryData = [
                      { orderId: 'ORD001', status: 'Accepted', customer: 'John Doe', amount: '₹1,200', date: '2024-01-15' },
                      { orderId: 'ORD002', status: 'Delivered', customer: 'Jane Smith', amount: '₹850', date: '2024-01-15' },
                      { orderId: 'ORD003', status: 'Out for Delivery', customer: 'Bob Johnson', amount: '₹2,100', date: '2024-01-14' },
                      { orderId: 'ORD004', status: 'Pending', customer: 'Alice Brown', amount: '₹650', date: '2024-01-14' },
                      { orderId: 'ORD005', status: 'Accepted', customer: 'Mike Wilson', amount: '₹1,800', date: '2024-01-13' }
                    ];
                  } else if (selectedMetric === 'Delivery Status - Delayed Orders') {
                    deliveryData = [
                      { orderId: 'ORD006', status: 'Delayed', customer: 'Sarah Davis', amount: '₹950', date: '2024-01-12', delay: '2 days' },
                      { orderId: 'ORD007', status: 'Delayed', customer: 'Tom Anderson', amount: '₹1,350', date: '2024-01-11', delay: '3 days' },
                      { orderId: 'ORD008', status: 'Delayed', customer: 'Lisa White', amount: '₹750', date: '2024-01-10', delay: '4 days' }
                    ];
                  } else {
                    const status = selectedMetric.replace('Delivery Status - ', '');
                    const statusOrders = {
                      'Accepted': [
                        { orderId: 'ORD009', customer: 'David Lee', amount: '₹1,100', date: '2024-01-15' },
                        { orderId: 'ORD010', customer: 'Emma Wilson', amount: '₹900', date: '2024-01-15' }
                      ],
                      'Delivered': [
                        { orderId: 'ORD011', customer: 'Chris Taylor', amount: '₹1,500', date: '2024-01-14' },
                        { orderId: 'ORD012', customer: 'Anna Garcia', amount: '₹800', date: '2024-01-14' }
                      ],
                      'Out for Delivery': [
                        { orderId: 'ORD013', customer: 'Mark Brown', amount: '₹1,200', date: '2024-01-13' }
                      ],
                      'Pending': [
                        { orderId: 'ORD014', customer: 'Sophie Miller', amount: '₹700', date: '2024-01-13' },
                        { orderId: 'ORD015', customer: 'Ryan Jones', amount: '₹1,400', date: '2024-01-12' }
                      ]
                    };
                    deliveryData = statusOrders[status] || [];
                  }
                  exportToExcel(deliveryData, `${selectedMetric}_export`);
                } else {
                  const dashboardData = getRoleBasedStats().map(stat => ({
                    metric: stat.title,
                    value: stat.numericValue,
                    growth: stat.subtitle,
                    role: roles[currentRole].name
                  }));
                  exportToExcel(dashboardData, `dashboard_${currentRole}_export`);
                }
              }}
            >
              Export
            </Button> */}
          </Toolbar>
        </AppBar>

        {/* Role Selection Menu - Only for RGMGR */}
        {(localStorage.getItem('userRole') === 'RGMGR' ||
          localStorage.getItem('userRole') === 'RMGR1') && (
            <Menu
              anchorEl={roleMenuAnchor}
              open={Boolean(roleMenuAnchor)}
              onClose={() => setRoleMenuAnchor(null)}
            >
              {/* Only show Central Manager option for RGMGR */}
              <MenuItem
                onClick={() => handleRoleChange('central-manager')}
                selected={currentRole === 'central-manager'}
              >
                <ListItemIcon sx={{ color: roles['central-manager'].color }}>
                  {roles['central-manager'].icon}
                </ListItemIcon>
                <ListItemText
                  primary={roles['central-manager'].name}
                  secondary={roles['central-manager'].description}
                />
              </MenuItem>
            </Menu>
          )}

        {/* Sort Menu */}
        <Menu
          anchorEl={sortMenuAnchor}
          open={Boolean(sortMenuAnchor)}
          onClose={handleSortClose}
        >
          <MenuItem onClick={() => handleSortSelect('default')}>
            <ListItemIcon>
              <SwapVert />
            </ListItemIcon>
            Default Order
          </MenuItem>
          <MenuItem onClick={() => handleSortSelect('highest')}>
            <ListItemIcon>
              <TrendingUp />
            </ListItemIcon>
            Highest Values
          </MenuItem>
          <MenuItem onClick={() => handleSortSelect('lowest')}>
            <ListItemIcon>
              <TrendingDown />
            </ListItemIcon>
            Lowest Values
          </MenuItem>
          <MenuItem onClick={() => handleSortSelect('alphabetical')}>
            <ListItemIcon>
              <Sort />
            </ListItemIcon>
            Alphabetical
          </MenuItem>
        </Menu>

        {/* Sidebar */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 240,
              bgcolor: 'background.paper',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Navigation
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
          <Divider />

          {/* Role indicator in sidebar */}
          <Box sx={{ p: 2, bgcolor: roles[currentRole].color + '10' }}>
            <Box display="flex" alignItems="center" gap={1}>
              {roles[currentRole].icon}
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {roles[currentRole].name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {roles[currentRole].description}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider />

          <List dense>
            {menuItems.map((item, index) => (
              <ListItem
                key={index}
                button
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  bgcolor: item.active ? 'primary.main' : 'transparent',
                  color: item.active ? 'white' : 'text.primary',
                  '&:hover': {
                    bgcolor: item.active ? 'primary.dark' : 'action.hover',
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
        </Drawer>


        {/* Main Content */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1, position: 'relative' }}>
          <Container maxWidth="xl" sx={{ py: 1 }}>
            {/* Filters */}
            <Paper elevation={1} sx={{ p: 1.5, mb: 2 }}>
              <Grid container spacing={1} alignItems="center">
                {/* Area Dropdown - Show for Central Manager */}
                {currentRole === 'central-manager' && (
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth size="small" sx={{
                      minWidth: 120,
                      zIndex: 1,
                      pointerEvents: 'auto'
                    }}>
                      <InputLabel id="area-select-label">Area</InputLabel>
                      <Select
                        key={`area-select-${areas.length}`}
                        labelId="area-select-label"
                        id="area-select"
                        value={selectedAreaID || ""}
                        displayEmpty
                        renderValue={(value) => {
                          if (isAreasLoading) return "Loading...";
                          if (value === "ALL") return "ALL Areas";
                          const area = areas.find(a => a.sfAreaId === value);
                          return area ? area.sfAreaName : value;
                        }}
                        startAdornment={
                          isAreasLoading && (
                            <CircularProgress size={16} sx={{ ml: 1, mr: 1 }} />
                          )
                        }
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setSelectedAreaID(newValue);
                          // Any change to filters requires the user to submit again
                          setFiltersSubmitted(false);
                          // Reset branch to ALL when area changes to ensure clean state
                          setSelectedBranchID('ALL');
                          // Only clear API caches, keep branch cache
                          setApiCache(prev => {
                            const newCache = { ...prev };
                            // Remove API caches but keep branch caches
                            Object.keys(newCache).forEach(key => {
                              if (!key.startsWith('branches_')) {
                                delete newCache[key];
                              }
                            });
                            return newCache;
                          });
                          setLastApiParams({});
                          // Fetch branches for the new area
                          fetchBranchList(e.target.value);
                          // Note: Removed manual handleRefresh() call as useEffect will handle it
                        }}
                        label="Area"
                        disabled={isAreasLoading || isRefreshing}
                        sx={{
                          fontSize: '0.75rem',
                          '& .MuiSelect-select': {
                            cursor: 'pointer'
                          }
                        }}
                        MenuProps={{
                          disablePortal: false,
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              zIndex: 9999,
                            },
                            sx: {
                              '& .MuiMenuItem-root': {
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                              },
                            },
                          },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                      >
                        {isAreasLoading ? (
                          <MenuItem disabled>Loading areas...</MenuItem>
                        ) : areas.length === 0 ? (
                          <MenuItem disabled>No areas available</MenuItem>
                        ) : (
                          <>
                            {/* ALL option for Central Manager */}
                            <MenuItem
                              value="ALL"
                              onClick={() => {
                                setSelectedAreaID('ALL');
                                setSelectedBranchID('ALL');
                                setApiCache(prev => {
                                  const newCache = { ...prev };
                                  Object.keys(newCache).forEach(key => {
                                    if (!key.startsWith('branches_')) {
                                      delete newCache[key];
                                    }
                                  });
                                  return newCache;
                                });
                                setLastApiParams({});
                                fetchBranchList('ALL');
                              }}
                            >
                              ALL Areas
                            </MenuItem>
                            {areas.map((area) => (
                              <MenuItem
                                key={area.sfAreaId}
                                value={area.sfAreaId}
                                onClick={() => {
                                  setSelectedAreaID(area.sfAreaId);
                                  setSelectedBranchID('ALL');
                                  setApiCache(prev => {
                                    const newCache = { ...prev };
                                    Object.keys(newCache).forEach(key => {
                                      if (!key.startsWith('branches_')) {
                                        delete newCache[key];
                                      }
                                    });
                                    return newCache;
                                  });
                                  setLastApiParams({});
                                  fetchBranchList(area.sfAreaId);
                                }}
                              >
                                {area.sfAreaName}
                              </MenuItem>
                            ))}
                          </>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* Branch Dropdown - Show for Central Manager and Area Manager */}
                {(() => {
                  return (currentRole === 'area-manager' || currentRole === 'central-manager') && (
                    <Grid item xs={12} sm={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Branch</InputLabel>
                        <Select
                          value={selectedBranchID}
                          renderValue={(value) => {
                            if (isBranchesLoading) return "Loading...";
                            if (value === "ALL") return "All Branches";
                            const branch = branches.find(b => b.branchID === value);
                            return branch ? branch.branchName : value;
                          }}
                          startAdornment={
                            isBranchesLoading && (
                              <CircularProgress size={16} sx={{ ml: 1, mr: 1 }} />
                            )
                          }
                          onChange={(e) => {
                            const newBranchID = e.target.value;
                            setSelectedBranchID(newBranchID);
                            // Any change to filters requires the user to submit again
                            setFiltersSubmitted(false);

                            // For Area Manager, extract AreaID from branch mapping only if not ALL
                            // For Central Manager, keep the already selected AreaID
                            if (currentRole === 'area-manager' && newBranchID !== 'ALL') {
                              const areaMapping = {
                                'L108': '00001',
                                'L109': '00001',
                                // Add more mappings as needed based on your branch-area structure
                              };
                              const newAreaID = areaMapping[newBranchID] || '00001';
                              setSelectedAreaID(newAreaID);
                            }
                            // For Central Manager, selectedAreaID is already set from area dropdown

                            // Only clear API caches, keep branch cache
                            setApiCache(prev => {
                              const newCache = { ...prev };
                              // Remove API caches but keep branch caches
                              Object.keys(newCache).forEach(key => {
                                if (!key.startsWith('branches_')) {
                                  delete newCache[key];
                                }
                              });
                              return newCache;
                            });
                            setLastApiParams({});

                            // NOTE: Removed automatic refresh on branch change. User must click Submit to fetch data.
                          }}
                          label="Branch"
                          disabled={isBranchesLoading || isRefreshing}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {isBranchesLoading ? (
                            <MenuItem disabled>Loading branches...</MenuItem>
                          ) : branches.length === 0 ? (
                            <MenuItem disabled>No branches available</MenuItem>
                          ) : (
                            [
                              <MenuItem key="ALL" value="ALL">
                                All Branches
                              </MenuItem>,
                              ...branches.map((branch) => (
                                <MenuItem key={branch.branchID} value={branch.branchID}>
                                  {branch.branchName}
                                </MenuItem>
                              ))
                            ]
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                })()}

                {/* Date Range Dropdown - Second position */}
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Date Range</InputLabel>
                    <Select
                      value={dateRangeOption}
                      onChange={(e) => handleDateRangeChange(e.target.value)}
                      label="Date Range"
                      disabled={isRefreshing}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      <MenuItem value="Today">Today</MenuItem>
                      <MenuItem value="Yesterday">Yesterday</MenuItem>
                      <MenuItem value="This Week">This Week</MenuItem>
                      <MenuItem value="This Month">This Month</MenuItem>
                      <MenuItem value="Custom">Custom</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {dateRangeOption === 'Custom' && (
                  <>
                    <Grid item xs={12} sm={2}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Tooltip
                          title={dateRangeError || ""}
                          open={!!dateRangeError}
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: '#d32f2f',
                                color: 'white',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                '& .MuiTooltip-arrow': {
                                  color: '#d32f2f',
                                },
                              },
                            },
                          }}
                        >
                          <DatePicker
                            label="From Date"
                            value={fromDate}
                            onChange={(newValue) => {
                              setFromDate(newValue);
                              // Any change to date requires re-submit
                              setFiltersSubmitted(false);
                              if (dateRangeOption !== 'Custom') {
                                setDateRangeOption('Custom');
                              }

                              // Validate date range
                              const validation = validateDateRange(newValue, toDate);
                              setDateRangeError(validation.error);

                              // Clear error if dates become valid
                              if (validation.isValid && dateRangeError) {
                                setDateRangeError('');
                              }
                            }}
                            format="DD-MM-YYYY"
                            slotProps={{
                              textField: {
                                size: "small",
                                variant: "outlined",
                                sx: {
                                  fontSize: '0.75rem',
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                      borderColor: dateRangeError ? '#d32f2f' : undefined,
                                      borderWidth: dateRangeError ? '2px' : '1px',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: dateRangeError ? '#d32f2f' : undefined,
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: dateRangeError ? '#d32f2f' : undefined,
                                    },
                                  },
                                },
                                disabled: isRefreshing
                              }
                            }}
                          />
                        </Tooltip>
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Tooltip
                          title={dateRangeError || ""}
                          open={!!dateRangeError}
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: '#d32f2f',
                                color: 'white',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                '& .MuiTooltip-arrow': {
                                  color: '#d32f2f',
                                },
                              },
                            },
                          }}
                        >
                          <DatePicker
                            label="To Date"
                            value={toDate}
                            onChange={(newValue) => {
                              setToDate(newValue);
                              // Any change to date requires re-submit
                              setFiltersSubmitted(false);
                              if (dateRangeOption !== 'Custom') {
                                setDateRangeOption('Custom');
                              }

                              // Validate date range
                              const validation = validateDateRange(fromDate, newValue);
                              setDateRangeError(validation.error);

                              // Clear error if dates become valid
                              if (validation.isValid && dateRangeError) {
                                setDateRangeError('');
                              }
                            }}
                            format="DD-MM-YYYY"
                            slotProps={{
                              textField: {
                                size: "small",
                                variant: "outlined",
                                sx: {
                                  fontSize: '0.75rem',
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                      borderColor: dateRangeError ? '#d32f2f' : undefined,
                                      borderWidth: dateRangeError ? '2px' : '1px',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: dateRangeError ? '#d32f2f' : undefined,
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: dateRangeError ? '#d32f2f' : undefined,
                                    },
                                  },
                                },
                                disabled: isRefreshing
                              }
                            }}
                          />
                        </Tooltip>
                      </LocalizationProvider>
                    </Grid>
                  </>
                )}
                {dateRangeOption !== 'Custom' && (
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', pl: 1 }}>
                      {dateRangeOption === 'Today' && `Selected: ${dayjs().format('DD-MM-YYYY')}`}
                      {dateRangeOption === 'Yesterday' && `Selected: ${dayjs().subtract(1, 'day').format('DD-MM-YYYY')}`}
                      {dateRangeOption === 'This Week' && `Selected: ${dayjs().subtract(7, 'day').format('DD-MM-YYYY')} to ${dayjs().format('DD-MM-YYYY')}`}
                      {dateRangeOption === 'This Month' && `Selected: ${dayjs().subtract(30, 'day').format('DD-MM-YYYY')} to ${dayjs().format('DD-MM-YYYY')}`}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {filtersSubmitted ? (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          const validation = validateDateRange(fromDate, toDate);
                          if (validation.isValid) {
                            setFiltersSubmitted(true);
                            handleRefresh({ force: true, all: true });
                          } else {
                            setDateRangeError(validation.error);
                          }
                        }}
                        disabled={isRefreshing || !validateDateRange(fromDate, toDate).isValid}
                        sx={{
                          fontSize: '0.75rem',
                          minWidth: '96px',
                          bgcolor: '#ff3d00',
                          color: 'white',
                          fontWeight: 'bold',
                          '&:hover': {
                            bgcolor: '#dd2c00',
                          },
                          '&:disabled': {
                            bgcolor: 'rgba(255, 61, 0, 0.5)',
                            color: 'rgba(255, 255, 255, 0.8)',
                          }
                        }}
                      >
                        {isRefreshing ? <CircularProgress size={16} sx={{ color: 'white' }} /> : '⚡ Submit'}
                      </Button>
                    ) : (
                      <Tooltip
                        title={
                          <Typography sx={{ p: 1, fontSize: '0.875rem' }}>
                            <strong>Action Required:</strong> Your filters have changed!<br />
                            Click the Submit button to update the dashboard with your new selections.
                          </Typography>
                        }
                        arrow
                        placement="top"
                        sx={{
                          '& .MuiTooltip-tooltip': {
                            bgcolor: 'rgba(0, 0, 0, 0.9)',
                            maxWidth: 300,
                          }
                        }}>
                        <Box
                          sx={{
                            position: 'relative',
                            '&:before': {
                              content: '""',
                              position: 'absolute',
                              top: -4,
                              right: -4,
                              bottom: -4,
                              left: -4,
                              border: '3px solid',
                              borderColor: '#ff0000',
                              borderRadius: '8px',
                              boxShadow: '0 0 10px rgba(255, 0, 0, 0.3)',
                              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                              '@keyframes pulse': {
                                '0%, 100%': {
                                  opacity: 0.8,
                                  transform: 'scale(1)',
                                  boxShadow: '0 0 10px rgba(255, 0, 0, 0.3)',
                                },
                                '50%': {
                                  opacity: 0.5,
                                  transform: 'scale(1.05)',
                                  boxShadow: '0 0 20px rgba(255, 0, 0, 0.5)',
                                },
                              },
                            },
                          }}
                        >
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              const validation = validateDateRange(fromDate, toDate);
                              if (validation.isValid) {
                                setFiltersSubmitted(true);
                                handleRefresh({ force: true, all: true });
                              } else {
                                setDateRangeError(validation.error);
                              }
                            }}
                            disabled={isRefreshing || !validateDateRange(fromDate, toDate).isValid}
                            sx={{
                              fontSize: '0.75rem',
                              minWidth: '96px',
                              bgcolor: 'primary.main',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                              },
                            }}
                          >
                            {isRefreshing ? <CircularProgress size={16} color="inherit" /> : 'Submit*'}
                          </Button>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  {lastUpdated && (
                    <Typography variant="caption" color="text.secondary">
                      Last updated: {lastUpdated.format('DD-MM-YYYY HH:mm:ss')}
                    </Typography>
                  )}
                </Grid>
                {/* <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Category</InputLabel>
                    <Select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      label="Category"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      <MenuItem value="all">All Categories</MenuItem>
                      <MenuItem value="electronics">Electronics</MenuItem>
                      <MenuItem value="clothing">Clothing</MenuItem>
                      <MenuItem value="books">Books</MenuItem>
                      <MenuItem value="home">Home & Garden</MenuItem>
                    </Select>
                  </FormControl>
                </Grid> */}
                {/* <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Region</InputLabel>
                    <Select
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      label="Region"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      <MenuItem value="all">All Regions</MenuItem>
                      <MenuItem value="north-america">North America</MenuItem>
                      <MenuItem value="europe">Europe</MenuItem>
                      <MenuItem value="asia">Asia Pacific</MenuItem>
                      <MenuItem value="latin-america">Latin America</MenuItem>
                    </Select>
                  </FormControl>
                </Grid> */}
              </Grid>
            </Paper>

            {/* Pin Code Cards - Only for Central Manager */}
            {/* {currentRole === 'central-manager' && (
              <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                  Top Pin Codes Performance (Click to view details)
                </Typography>
                <Grid container spacing={2}>
                  {topPinCodes.map((pincode, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <PinCodeCard {...pincode} />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )} */}

            {/* New Layout Structure */}
            <Grid container spacing={3}>
              {/* Top Section - KPI Cards Layout */}
              <Grid item xs={12}>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {/* Left Column (70% width) */}
                  <Grid item xs={12} md={8.4}>
                    {/* Row 1: Placed Orders, Online Orders, Retail */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {/* Placed Orders */}
                      {cardLoadingChecks['Placed Orders']() ? (
                        <Grid item xs={12} sm={4}>
                          <CardSkeleton />
                        </Grid>
                      ) : (
                        getSortedStats().filter(stat => stat.title === 'Placed Orders').map((stat, index) => (
                          <Grid item xs={12} sm={4} key={stat.title}>
                            <StatCard
                              title={stat.title}
                              value={stat.value}
                              subtitle={stat.subtitle}
                              icon={stat.icon}
                              color={stat.color}
                              index={index}
                            />
                          </Grid>
                        ))
                      )}

                      {/* Online Orders */}
                      {cardLoadingChecks['Scheduled Deliveries']() ? (
                        <Grid item xs={12} sm={4}>
                          <CardSkeleton />
                        </Grid>
                      ) : (
                        getSortedStats().filter(stat => stat.title === 'Scheduled Deliveries').map((stat, index) => (
                          <Grid item xs={12} sm={4} key={stat.title}>
                            <StatCard
                              title={stat.title}
                              value={stat.value}
                              subtitle={stat.subtitle}
                              icon={stat.icon}
                              color={stat.color}
                              index={index}
                            />
                          </Grid>
                        ))
                      )}

                      {/* Retail */}
                      {cardLoadingChecks['Retail']() ? (
                        <Grid item xs={12} sm={4}>
                          <CardSkeleton />
                        </Grid>
                      ) : (
                        getSortedStats().filter(stat => stat.title === 'Retail').map((stat, index) => (
                          <Grid item xs={12} sm={4} key={stat.title}>
                            <StatCard
                              title={stat.title}
                              value={stat.value}
                              subtitle={stat.subtitle}
                              icon={stat.icon}
                              color={stat.color}
                              index={index}
                            />
                          </Grid>
                        ))
                      )}
                    </Grid>

                    {/* Row 2: Wallet, Happy Hour, Horeca */}
                    <Grid container spacing={2}>
                      {/* Wallet */}
                      {cardLoadingChecks['Wallet']() ? (
                        <Grid item xs={12} sm={4}>
                          <CardSkeleton />
                        </Grid>
                      ) : (
                        getSortedStats().filter(stat => stat.title === 'Wallet').map((stat, index) => (
                          <Grid item xs={12} sm={4} key={stat.title}>
                            <StatCard
                              title={stat.title}
                              value={
                                <Box display="flex" flexDirection="column" gap={0.5}>
                                  <Typography
                                    variant="body1"
                                    fontWeight="bold"
                                    sx={{
                                      color: '#1976d2',
                                      fontSize: '0.8rem',
                                      letterSpacing: '0.5px',
                                      mb: 0.5,
                                    }}
                                  >
                                    {walletSummary.prepaidCount} / ₹{walletSummary.prepaidAmount.toLocaleString()}
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    fontWeight="bold"
                                    sx={{
                                      color: '#388e3c',
                                      fontSize: '1.15rem',
                                      letterSpacing: '0.5px',
                                    }}
                                  >
                                    Subscription: {walletSummary.subscriptionCount} / ₹{walletSummary.subscriptionAmount.toLocaleString()}
                                  </Typography>
                                </Box>
                              }
                              subtitle={
                                <Box display="flex" alignItems="center" gap={2}>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <AttachMoney sx={{ fontSize: 16, color: '#1976d2' }} />
                                    <Typography variant="caption" fontWeight="bold" color="#1976d2">
                                      Prepaid: {walletSummary.prepaidCount}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <AttachMoney sx={{ fontSize: 16, color: '#388e3c' }} />
                                    <Typography variant="caption" fontWeight="bold" color="#388e3c">
                                      Subscription: {walletSummary.subscriptionCount}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                              icon={stat.icon}
                              color={stat.color}
                              index={index}
                            />
                          </Grid>
                        ))
                      )}

                      {/* Happy Hour */}
                      {cardLoadingChecks['Happy Hour']() ? (
                        <Grid item xs={12} sm={4}>
                          <CardSkeleton />
                        </Grid>
                      ) : (
                        getSortedStats().filter(stat => stat.title === 'Happy Hour').map((stat, index) => (
                          <Grid item xs={12} sm={4} key={stat.title}>
                            <StatCard
                              title={stat.title}
                              value={stat.value}
                              subtitle={stat.subtitle}
                              icon={stat.icon}
                              color={stat.color}
                              index={index}
                            />
                          </Grid>
                        ))
                      )}

                      {/* HORECA */}
                      {cardLoadingChecks['Horeca']() ? (
                        <Grid item xs={12} sm={4}>
                          <CardSkeleton />
                        </Grid>
                      ) : (
                        getSortedStats().filter(stat => stat.title === 'Horeca').map((stat, index) => (
                          <Grid item xs={12} sm={4} key={stat.title}>
                            <StatCard
                              title={stat.title}
                              value={stat.value}
                              subtitle={stat.subtitle}
                              icon={stat.icon}
                              color={stat.color}
                              index={index}
                            />
                          </Grid>
                        ))
                      )}
                    </Grid>
                  </Grid>

                  {/* Right Column (30% width) - Division Sales Card */}
                  <Grid item xs={12} md={3.6}>
                    {cardLoadingChecks['Division Sales']() ? (
                      <CardSkeleton />
                    ) : (
                      <Card
                        onClick={async () => {
                          if (isTableLoading || showBottomLoader) return;

                          setIsTableLoading(true);
                          setSelectedMetric('Division Sales');
                          setSelectedPincode(null);
                          setShowBottomLoader(true);
                          setLoadingProgress(0);
                          setShowTable(false);

                          try {
                            await fetchDetailedSalesData(); // Fetch all division sales data

                            // Simulate loading progress
                            const progressInterval = setInterval(() => {
                              setLoadingProgress((prev) => {
                                if (prev >= 100) {
                                  clearInterval(progressInterval);
                                  setTimeout(() => {
                                    setShowBottomLoader(false);
                                    setShowTable(true);
                                    setIsTableLoading(false);

                                    // Scroll to table
                                    setTimeout(() => {
                                      tableRef.current?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start'
                                      });
                                    }, 100);
                                  }, 500);
                                  return 100;
                                }
                                return prev + 3;
                              });
                            }, 35);
                          } catch (error) {
                            console.error('Error loading Division Sales data:', error);
                            setShowBottomLoader(false);
                            setIsTableLoading(false);
                          }
                        }}
                        sx={{
                          height: '100%',
                          maxHeight: '380px',
                          cursor: 'pointer',
                          borderRadius: 6,
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                          }
                        }}>
                        <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography
                              variant="subtitle2"
                              fontWeight="800"
                              sx={{
                                color: '#1976d2',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                fontSize: '0.75rem'
                              }}
                            >
                              DIVISION SALES
                            </Typography>
                            <Store sx={{ color: '#1976d2', fontSize: 20 }} />
                          </Box>

                          {/* Overall Totals */}
                          <Box mb={1}>
                            <Typography variant="h6" fontWeight="bold" sx={{ color: '#1976d2', mb: 0.5, lineHeight: 1.2 }}>
                              {(() => {
                                // Check if salesSummaryData has the transformed structure (name, count) or raw structure (Division, SaleQty)
                                if (!salesSummaryData || salesSummaryData.length === 0) return "0 Units";

                                let totalQty = 0;
                                let uomLabel = "Units";

                                // Handle both transformed and raw data structures
                                if (salesSummaryData[0]?.name !== undefined) {
                                  // Transformed data structure
                                  totalQty = salesSummaryData.reduce((sum, item) => sum + (item.count || 0), 0);
                                } else {
                                  // Raw API data structure - group by division first
                                  const groupedData = salesSummaryData.reduce((acc, item) => {
                                    const division = item.Division;
                                    if (!acc[division]) {
                                      acc[division] = { SaleQty: 0, UOMID: item.UOMID };
                                    }
                                    acc[division].SaleQty += item.SaleQty || 0;
                                    return acc;
                                  }, {});
                                  totalQty = Object.values(groupedData).reduce((sum, item) => sum + item.SaleQty, 0);
                                  const kgItem = Object.values(groupedData).find(item => item.UOMID === 'KG');
                                  if (kgItem) uomLabel = 'KG';
                                }

                                return `${totalQty.toFixed(1)} ${uomLabel}`;
                              })()}
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#388e3c', fontWeight: 600 }}>
                              ₹{(() => {
                                if (!salesSummaryData || salesSummaryData.length === 0) return "0";

                                let totalAmount = 0;
                                if (salesSummaryData[0]?.name !== undefined) {
                                  // Transformed data structure
                                  totalAmount = salesSummaryData.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
                                } else {
                                  // Raw API data structure
                                  totalAmount = salesSummaryData.reduce((sum, item) => sum + (item.TotalAmount || 0), 0);
                                }

                                return totalAmount.toLocaleString();
                              })()}
                            </Typography>
                          </Box>

                          {/* Division Breakdowns - Compact */}
                          <Box sx={{
                            flex: 1,
                            maxHeight: '320px',
                            overflow: 'auto',
                            pr: 1,
                            '&::-webkit-scrollbar': {
                              width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                              background: '#f1f1f1',
                              borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: '#c1c1c1',
                              borderRadius: '3px',
                              '&:hover': {
                                background: '#a8a8a8',
                              },
                            },
                          }}>
                            {(() => {
                              if (!salesSummaryData || salesSummaryData.length === 0) {
                                return (
                                  <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                                    <Typography variant="caption" color="textSecondary">
                                      Loading division data...
                                    </Typography>
                                  </Box>
                                );
                              }

                              let groupedArray = [];
                              let totalAmount = 0;

                              // Handle both transformed and raw data structures
                              if (salesSummaryData[0]?.name !== undefined) {
                                // Transformed data structure
                                groupedArray = salesSummaryData;
                                totalAmount = salesSummaryData.reduce((sum, item) => sum + (item.totalAmount || 0), 0) || 1;
                              } else {
                                // Raw API data structure - group by division and sum quantities and amounts
                                const groupedData = salesSummaryData.reduce((acc, item) => {
                                  const division = item.Division;
                                  if (!acc[division]) {
                                    acc[division] = {
                                      Division: division,
                                      SaleQty: 0,
                                      TotalAmount: 0,
                                      UOMID: item.UOMID
                                    };
                                  }
                                  acc[division].SaleQty += item.SaleQty || 0;
                                  acc[division].TotalAmount += item.TotalAmount || 0;
                                  return acc;
                                }, {});

                                groupedArray = Object.values(groupedData);
                                totalAmount = groupedArray.reduce((sum, item) => sum + (item.TotalAmount || 0), 0) || 1;
                              }

                              return groupedArray.map((division, index) => {
                                const colors = [
                                  { bg: 'rgba(25, 118, 210, 0.05)', border: 'rgba(25, 118, 210, 0.1)', text: '#1976d2', accent: '#1976d2' },
                                  { bg: 'rgba(56, 142, 60, 0.05)', border: 'rgba(56, 142, 60, 0.1)', text: '#388e3c', accent: '#388e3c' },
                                  { bg: 'rgba(255, 152, 0, 0.05)', border: 'rgba(255, 152, 0, 0.1)', text: '#ff9800', accent: '#ff9800' },
                                  { bg: 'rgba(156, 39, 176, 0.05)', border: 'rgba(156, 39, 176, 0.1)', text: '#9c27b0', accent: '#9c27b0' }
                                ];
                                const colorScheme = colors[index % colors.length];

                                // Handle both data structures for calculations
                                const divisionAmount = division.TotalAmount || division.totalAmount || 0;
                                const divisionQty = division.SaleQty || division.count || 0;
                                const divisionName = division.Division || division.name || '';
                                const divisionUOM = division.UOMID || 'Units';

                                const percentage = ((divisionAmount / totalAmount) * 100).toFixed(1);

                                return (
                                  <Box
                                    key={divisionName}
                                    mb={index < groupedArray.length - 1 ? 1 : 0}
                                    p={1}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (isTableLoading || showBottomLoader) return;

                                      setIsTableLoading(true);
                                      setSelectedMetric('Division Sales');
                                      setSelectedPincode(null);
                                      setShowBottomLoader(true);
                                      setLoadingProgress(0);
                                      setShowTable(false);

                                      try {
                                        await fetchDetailedSalesData(divisionName);

                                        // Simulate loading progress
                                        const progressInterval = setInterval(() => {
                                          setLoadingProgress((prev) => {
                                            if (prev >= 100) {
                                              clearInterval(progressInterval);
                                              setTimeout(() => {
                                                setShowBottomLoader(false);
                                                setShowTable(true);
                                                setIsTableLoading(false);

                                                // Scroll to table
                                                setTimeout(() => {
                                                  tableRef.current?.scrollIntoView({
                                                    behavior: 'smooth',
                                                    block: 'start'
                                                  });
                                                }, 100);
                                              }, 200);
                                              return 100;
                                            }
                                            return prev + 3;
                                          });
                                        }, 35);
                                      } catch (error) {
                                        console.error('Error loading filtered division data:', error);
                                        setShowBottomLoader(false);
                                        setIsTableLoading(false);
                                      }
                                    }}
                                    sx={{
                                      backgroundColor: colorScheme.bg,
                                      borderRadius: 0.5,
                                      border: `1px solid ${colorScheme.border}`,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        backgroundColor: colorScheme.accent + '10',
                                        border: `1px solid ${colorScheme.accent}40`,
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 2px 8px ${colorScheme.accent}20`
                                      }
                                    }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                      <Typography variant="caption" fontWeight="bold" sx={{ color: colorScheme.text, fontSize: '0.75rem' }}>
                                        {divisionName}
                                      </Typography>
                                      <Typography variant="caption" sx={{
                                        backgroundColor: colorScheme.accent,
                                        color: 'white',
                                        px: 0.5,
                                        py: 0.2,
                                        borderRadius: 0.3,
                                        fontSize: '0.65rem'
                                      }}>
                                        {percentage}%
                                      </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                      <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                                        {divisionQty.toFixed(1)} {divisionUOM}
                                      </Typography>
                                      <Typography variant="caption" fontWeight="600" sx={{ color: '#388e3c', fontSize: '0.75rem' }}>
                                        ₹{divisionAmount.toLocaleString()}
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              });
                            })()}
                          </Box>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>
                </Grid>
              </Grid>

              {/* Bottom Section - Charts */}
              <Grid item xs={12}>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {/* Product Sales Comparison (70% left) */}
                  <Grid item xs={12} md={8.4}>
                    <Card sx={{
                      position: 'relative',
                      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: 1,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 0 30px rgba(25,118,210,0.1)',
                        borderColor: 'rgba(25,118,210,0.3)',
                      }
                    }}>
                      <CardContent sx={{ p: 4 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                          <Box>
                            <Typography
                              variant="h6"
                              fontWeight="700"
                              sx={{
                                background: 'linear-gradient(135deg, #1976d2, #1565c0)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                mb: 1
                              }}
                            >
                              Product Sales Comparison
                            </Typography>
                            <GraphFilterButtons value={graphFilter} onChange={setGraphFilter} />
                          </Box>

                        </Box>
                        <Box sx={{
                          height: 400,
                          mt: 2,
                          maxWidth: '100%',
                          p: 2,
                          borderRadius: 2,
                          background: 'rgba(255,255,255,0.8)',
                          border: '1px solid rgba(255,255,255,0.3)',
                        }}>
                          {graphLoading ? (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                              <CircularProgress size={60} thickness={4} sx={{ color: '#1976d2' }} />
                            </Box>
                          ) : (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={graphData} margin={{ top: 30, right: 40, left: 30, bottom: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                                <XAxis
                                  dataKey="ItemName"
                                  fontSize={12}
                                  interval={0}
                                  angle={-45}
                                  textAnchor="end"
                                  height={90}
                                  tick={{ fontSize: 12, fontWeight: 500 }}
                                  stroke="#666"
                                />
                                <YAxis
                                  fontSize={12}
                                  tick={{ fontSize: 12, fontWeight: 500 }}
                                  stroke="#666"
                                />
                                <Legend
                                  align="left"
                                  wrapperStyle={{ fontSize: '14px', paddingTop: '20px', fontWeight: 600 }}
                                  iconType="line"
                                />
                                <RechartsTooltip
                                  content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                      const data = payload[0].payload;
                                      return (
                                        <div className="recharts-default-tooltip" style={{
                                          backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                          border: '1px solid rgba(25,118,210,0.3)',
                                          borderRadius: '8px',
                                          padding: '16px',
                                          fontSize: '13px',
                                          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                                          backdropFilter: 'blur(10px)',
                                        }}>
                                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: '#1976d2' }}>
                                            {label}
                                          </Typography>

                                          <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5, color: '#333' }}>
                                            Current Period:
                                          </Typography>
                                          <Typography variant="caption" display="block" color="#388e3c" sx={{ mb: 0.25 }}>
                                            Sale Qty: {data.SaleQty?.toLocaleString()} units
                                          </Typography>
                                          <Typography variant="caption" display="block" color="#1976d2" sx={{ mb: 1 }}>
                                            Total Amount: ₹{data.TotalAmount?.toLocaleString()}
                                          </Typography>

                                          {/* <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5, color: '#333' }}>
                                            Previous Period:
                                          </Typography>
                                          <Typography variant="caption" display="block" color="#d32f2f" sx={{ mb: 0.25 }}>
                                            Sale Qty: {data.PrevSaleQty?.toLocaleString()} units
                                          </Typography>
                                          <Typography variant="caption" display="block" color="#d32f2f" sx={{ mb: 1 }}>
                                            Total Amount: ₹{data.PrevTotalAmount?.toLocaleString()}
                                          </Typography>
                                          
                                          <Typography variant="caption" display="block" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                                            Change: {data.TotalAmount && data.PrevTotalAmount ? 
                                              `${((data.TotalAmount - data.PrevTotalAmount) / data.PrevTotalAmount * 100).toFixed(1)}%` : 'N/A'}
                                          </Typography> */}
                                        </div>
                                      );
                                    }
                                    return null;
                                  }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="TotalAmount"
                                  stroke="#1976d2"
                                  strokeWidth={3}
                                  dot={{ r: 4, fill: '#1976d2', strokeWidth: 2 }}
                                  activeDot={{ r: 7, fill: '#1976d2', strokeWidth: 3 }}
                                  name="Current Amount"
                                />
                                {/* <Line
                                  type="monotone"
                                  dataKey="PrevTotalAmount"
                                  stroke="#d32f2f"
                                  strokeWidth={3}
                                  strokeDasharray="8 4"
                                  dot={{ r: 4, fill: '#d32f2f', strokeWidth: 2 }}
                                  activeDot={{ r: 7, fill: '#d32f2f', strokeWidth: 3 }}
                                  name="Previous Amount"
                                /> */}
                              </LineChart>
                            </ResponsiveContainer>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Delivery Status (30% right) */}
                  <Grid item xs={12} md={3.6}>
                    {cardLoadingChecks['Delivery Status']() ? (
                      <CardSkeleton />
                    ) : (
                      <DeliveryStatusPieChart
                        key="delivery-status-chart"
                        title="Delivery Status"
                        items={deliveryStatus}
                        isLoading={isRefreshing || isApiLoading('deliveryStatus') || isTableLoading || showBottomLoader}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>

            </Grid>

            {/* Dynamic Table */}
            {renderTable()}
          </Container>

          {/* Bottom Loading Bar */}
          <Slide direction="up" in={showBottomLoader} timeout={500}>
            <Box
              sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                p: 2,
                zIndex: 1000,
                boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading detailed data...
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={loadingProgress}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 2,
                    background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
                  }
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
              >
                {loadingProgress}% complete
              </Typography>
            </Box>
          </Slide>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DashBoard;

